import { AnimatedSlideTemplate } from './components/AnimatedSlider/AnimatedSlide';
import { ProductTypeId, TLifestyleHeader } from '@types';
import MeetTheUltraLightCollection from './components/MeetTheUltraLightCollection';

type ExtraDataReturnValue = {
  lifestyleHeader?: TLifestyleHeader;
} & any;

const VIDEOS_DOMAIN = 'https://static.velkybrands.com/v';

const getExtraData = (type: ProductTypeId): ExtraDataReturnValue => {
  switch (type) {
    case ProductTypeId.SOFT_PACK_COOLERS_TYPE:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              A durable fully-waterproof Cooler perfect for adventures from the road to the water
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>Leakproof design</li>
              <li className='tw-text-base tw-font-normal'>Waterproof zipper</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 2 days when following{' '}
                <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        lifestyleHeader: {
          title: 'BUILT TO FLOAT',
          image: {
            desktop: require('./images/soft-pack-cooler/hero-fallback-d.jpg'),
            mobile: require('./images/soft-pack-cooler/hero-fallback-m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/soft-pack-cooler-hero_d.mp4`,
        },
        sizesNote: (
          <span className='tw-italic'>
            The Ultra-Tough Soft Cooler got a new name to reflect just how tough it is. Some packaging may
            still say "Soft Pack Cooler" to help us reduce waste during this transition.
          </span>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/meet-soft-pack-cooler/Premium-Ice-Retention-D.jpg'),
                mobile: require('./images/soft-pack-cooler/meet-soft-pack-cooler/Premium-Ice-Retention-m.jpg'),
                alt: 'Soft Pack Cooler with ice and drinks inside',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <>
                    Up to 1.5’’ of closed cell foam insulation and a durable, waterproof zipper lock in the
                    cold for up to 2 days when following <RticCoolingTipsLink />. The Ultra-Tough Soft Cooler
                    can be used with loose ice or gel packs.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/meet-soft-pack-cooler/Fun-That-Floats-D.jpg'),
                mobile: require('./images/soft-pack-cooler/meet-soft-pack-cooler/Fun-That-Floats-m.jpg'),
                alt: 'RTIC Soft Pack Cooler floating in water',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER',
                title: <span>Fun That Floats</span>,
                description: (
                  <>
                    This cooler combines welded construction, lightweight design, and a waterproof zipper to
                    lock the air in and keep water out. So your drinks and snacks can go wherever you go—from
                    the road to the water.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/meet-soft-pack-cooler/Durable-Portable-D.jpg'),
                mobile: require('./images/soft-pack-cooler/meet-soft-pack-cooler/Durable-Portable-m.jpg'),
                alt: 'RTIC Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER',
                title: <span>Durable & Portable</span>,
                description: (
                  <>
                    Made with heavy-duty coated fabric and a puncture-resistant, waterproof interior, these
                    coolers are lightweight yet extremely durable.
                  </>
                ),
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '12 Can',
              sections: [
                {
                  title: {
                    text: '12 Can',
                  },
                  images: {
                    desktop: require('./images/soft-pack-cooler/dims/12-can-dims-m.png'),
                    mobile: require('./images/soft-pack-cooler/dims/12-can-dims-m.png'),
                    alt: 'RTIC Soft Pack Cooler 12 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Dimensions: 11” × 11” × 9½”</p>
                      <p className='tw-bold tw-mb-1'>Can Capacity: 12 Cans</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 2.4 lbs</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '20 Can',
              sections: [
                {
                  title: {
                    text: '20 Can',
                  },
                  images: {
                    desktop: require('./images/soft-pack-cooler/dims/20-can-dims-m.png'),
                    mobile: require('./images/soft-pack-cooler/dims/20-can-dims-m.png'),
                    alt: 'RTIC Soft Pack Cooler 20 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Dimensions: 13 &frac12;” × 13 &frac34;” × 9 &frac12;”</p>
                      <p className='tw-bold tw-mb-1'>Can Capacity: 20 Cans</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 3 lbs</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '30 Can',
              sections: [
                {
                  title: {
                    text: '30 Can',
                  },
                  images: {
                    desktop: require('./images/soft-pack-cooler/dims/30-can-dims-m.png'),
                    mobile: require('./images/soft-pack-cooler/dims/30-can-dims-m.png'),
                    alt: 'RTIC Soft Pack Cooler 30 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>
                        Dimensions: 15 &frac12;” × 12 &frac34;” × 11 &frac12;”
                      </p>
                      <p className='tw-bold tw-mb-1'>Can Capacity: 30 Cans</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 4 lbs</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '40 Can',
              sections: [
                {
                  title: {
                    text: '40 Can',
                  },
                  images: {
                    desktop: require('./images/soft-pack-cooler/dims/40-can-dims-m.png'),
                    mobile: require('./images/soft-pack-cooler/dims/40-can-dims-m.png'),
                    alt: 'RTIC Soft Pack Cooler 40 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Dimensions: 18 &frac12;” × 17” × 12”</p>
                      <p className='tw-bold tw-mb-1'>Can Capacity: 40 Cans</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 5.5 lbs</p>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/features/Leakproof-Design-d.jpg'),
                mobile: require('./images/soft-pack-cooler/features/Leakproof-Design-m.jpg'),
                alt: 'RTIC Soft Pack Cooler leakproof design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Leakproof Design</span>,
                description: (
                  <>
                    Welded seams and a waterproof zipper keep this cooler air tight and prevent water from
                    getting in or out.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/features/Heavy-Duty-Exterior-d.jpg'),
                mobile: require('./images/soft-pack-cooler/features/Heavy-Duty-Exterior-m.jpg'),
                alt: 'RTIC Soft Pack Cooler heavy-duty exterior',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Heavy-Duty Exterior</span>,
                description: (
                  <>Heavy-duty, coated fabric is tear and puncture resistant for extreme durability.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/features/Waterproof-Interior-d.jpg'),
                mobile: require('./images/soft-pack-cooler/features/Waterproof-Interior-m.jpg'),
                alt: 'RTIC Soft Pack Cooler waterproof interior',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Waterproof Interior</span>,
                description: (
                  <>
                    Puncture-resistant, waterproof interior can be used with gel packs or loose ice and is
                    easy to clean.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/features/Shoulder-Strap-d.jpg'),
                mobile: require('./images/soft-pack-cooler/features/Shoulder-Strap-m.jpg'),
                alt: 'RTIC Soft Pack Cooler with shoulder strap',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Shoulder Strap</span>,
                description: (
                  <>
                    A removable, adjustable shoulder strap and built-in carrying handles make this lightweight
                    cooler easy to carry.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/features/Exterior-Pocket-d.jpg'),
                mobile: require('./images/soft-pack-cooler/features/Exterior-Pocket-m.jpg'),
                alt: 'RTIC Soft Pack Cooler with exterior pocket',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Exterior Pocket</span>,
                description: (
                  <>
                    A convenient, exterior pocket* allows for easy storing of essentials.
                    <br />
                    <br />
                    *This pocket is not waterproof.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-cooler/features/Wide-Mouth-Opening-d.jpg'),
                mobile: require('./images/soft-pack-cooler/features/Wide-Mouth-Opening-m.jpg'),
                alt: 'RTIC Soft Pack Cooler leakproof design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Wide Mouth Opening</span>,
                description: (
                  <>The wide mouth opening makes it easy to pack and access your food and drinks.</>
                ),
              },
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/soft-pack-cooler/icon-ice.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 1.5’’ of closed cell foam insulation keeps contents cold for up to 2 days when following{' '}
                <a className='tw-underline' target='_blank' href='/ice-retention-tips'>
                  RTIC's Cooling Tips
                </a>
                .
              </>
            ),
          },
          {
            icon: require('./images/soft-pack-cooler/icon-leakproof.svg'),
            title: <>Leakproof Design</>,
            description: (
              <>
                Welded seams and a waterproof zipper make it air and water tight, so the spills stay in and
                the water stays out.
              </>
            ),
          },
          {
            icon: require('./images/soft-pack-cooler/icon-durable.svg'),
            title: <>Durable & Portable</>,
            description: (
              <>
                A heavy-duty, coated exterior and waterproof, puncture-resistant interior make it durable and
                lightweight.
              </>
            ),
          },
          {
            icon: require('./images/soft-pack-cooler/icon-built-to-float.svg'),
            title: <>Built to Float</>,
            description: (
              <>
                The leakproof design, welded seams, and a waterproof zipper allow this cooler to float in
                water.
              </>
            ),
          },
        ],
      };

    case ProductTypeId.CHILLOUT_BAG_2_IN_1:
      return {
        lifestyleHeader: {
          title: 'HALF BAG, HALF COOLER',
          image: {
            desktop: require('./images/2-in-1-chillout-bag/hero-fallback_d.jpg'),
            mobile: require('./images/2-in-1-chillout-bag/hero-fallback_m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/2-in-1-chillout-hero_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              A hybrid carryall to keep your essentials dry and your drinks cool.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>24 can insulated cooler base compartment</li>
              <li className='tw-text-base tw-font-normal'>25 liter non-insulated top compartment</li>
              <li className='tw-text-base tw-font-normal'>Lightweight and easy to clean</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-2-in-1-bag-func.svg'),
            title: <>2-in-1 Functionality</>,
            description: (
              <>
                This hybrid carryall features a water-resistant top compartment and an attached,
                fully-insulated cooler base.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>Fully Insulated Base</>,
            description: (
              <>
                Fully insulated with closed-cell foam, the 24 can cooler compartment locks in the cold all day
                long.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-optimal-organization.svg'),
            title: <>Optimal Organization</>,
            description: (
              <>
                Designed to carry all your essentials on one arm, this bag features multiple internal and
                external pockets and straps.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-easy-to-wipe.svg'),
            title: <>Easy to Clean</>,
            description: (
              <>
                The back-coated exterior, anti-microbial interior, and waterproof cooler base are easy to wipe
                clean.
              </>
            ),
          },
        ],
        iconFeaturesSmallprint: (
          <>
            <b>Note:</b> This bag is constructed with a fabric zipper, not a waterproof zipper. It is not
            recommended for use with loose ice as it may leak liquid contents.
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/meet-the-collection/the-ultimate-carryall_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/meet-the-collection/the-ultimate-carryall_m.jpg'),
                alt: 'Chillout Bag 2-in-1 functionality',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/2-in-1-chillout-ultimate-carryall_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/2-in-1-chillout-ultimate-carryall_m.mp4`,
              copy: {
                tagline: 'MEET THE 2-IN-1 CHILLOUT BAG',
                title: <span>The Ultimate Carryall</span>,
                description: (
                  <span>
                    This 2-in-1 bag combines a non-insulated tote top and a fully insulated cooler base to
                    give you the best of both worlds. Pack your clothes, shoes, and essentials in the dry top,
                    and chill your food and drinks in the bottom compartment. Plus, roll up your towel and
                    secure it on the outside.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/meet-the-collection/keep-it-cool_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/meet-the-collection/keep-it-cool_m.jpg'),
                alt: 'A family at the beach with the 2-in-1 Chillout Bag',
              },
              copy: {
                tagline: 'MEET THE 2-IN-1 CHILLOUT BAG',
                title: <span>Keep It Cool</span>,
                description: (
                  <span>
                    The base compartment fits up to 24 cans and is fully insulated with closed-cell foam to
                    lock in the cold all day long. Featuring a seam-sealed, antimicrobial liner that is easy
                    to clean at the end of a beach day.
                    <br />
                    <br />
                    Pro Tip: This cooler base is best used with gel ice packs. Get the{' '}
                    <a href='/Ice-Packs?size=Medium' className='tw-underline' target='_blank'>
                      Medium RTIC Ice Pack
                    </a>{' '}
                    for a perfect fit.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/meet-the-collection/bag-it-all_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/meet-the-collection/bag-it-all_m.jpg'),
                alt: '2-in-1 Chillout Bag water-repellent large zippered pocket',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/2-in-1-chillout-bag-it-all_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/2-in-1-chillout-bag-it-all_m.mp4`,
              copy: {
                tagline: 'MEET THE 2-IN-1 CHILLOUT BAG',
                title: <span>Bag It All</span>,
                description: (
                  <span>
                    The interior features multiple mesh pockets and a water-repellent zippered pocket to keep
                    essentials organized. The exterior features a large zippered pocket with a tethered bottle
                    opener and a mesh pocket that sheds sand and water. Plus, external clip straps can carry a
                    towel, yoga mat, or the{' '}
                    <a href='/Anywhere-Blanket' target='_blank' className='tw-underline'>
                      RTIC Anywhere Blanket
                    </a>
                    .
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          title: 'Dimensions',
          textOnly: true,

          sections: [
            {
              title: {
                text: '',
              },
              images: {
                desktop: require('./images/2-in-1-chillout-bag/dims_d.png'),
                mobile: require('./images/2-in-1-chillout-bag/dims_m.png'),
                alt: '2-in-1 Chillout Bag dimensions',
              },
              description: (
                <>
                  <div className='tw-flex tw-justify-center'>
                    <ul className='tw-text-left [&>li]:tw-mb-2'>
                      <li>Can Capcity: 24 Cans</li>
                      <li>Bottle Capcity: 3 wine bottles</li>
                    </ul>
                  </div>
                </>
              ),
            },
          ],
        },

        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/built-in-cooler-compartment_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/built-in-cooler-compartment_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag built-in cooler compartment',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Cooler Compartment</span>,
                description: (
                  <span>
                    The separate cooler base features a seam-sealed antimicrobial liner and is fully insulated
                    with closed-cell foam to lock in the cold all day long.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/built-in-bag-compartment_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/built-in-bag-compartment_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag built-in bag compartment',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Bag Compartment</span>,
                description: (
                  <span>
                    The 25 liter zippered top compartment features a nylon interior that’s easy to wipe clean
                    with a damp cloth.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/durable-design_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/durable-design_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag durable design',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Durable Design</span>,
                description: (
                  <span>
                    Back-coated, ripstop fabric exterior repels water and makes it easy to wipe clean.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/free-standing_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/free-standing_m.jpg'),
                alt: '2-in-1 Chillout Bag free standing feature',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Free Standing</span>,
                description: (
                  <span>
                    Designed with internal structure and a waterproof base keep this tote upright, accessible,
                    and dry.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/interior-pockets_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/interior-pockets_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag interior pockets',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Interior Pockets</span>,
                description: (
                  <span>
                    The interior features multiple mesh pockets and a water-repellent zippered pocket with a
                    built-in key leash to keep essentials organized.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/exterior-org_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/exterior-org_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag exterior organization',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Exterior Organization</span>,
                description: (
                  <span>
                    The exterior features one large zippered pocket with a tethered bottle opener and a large
                    mesh pocket for wet or sandy items.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/built-in-clip-straps_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/built-in-clip-straps_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag built-in clip straps',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Clip Straps</span>,
                description: (
                  <span>
                    Convenient external clip straps are made to carry towels, a yoga mat, or a blanket.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/2-in-1-chillout-bag/tech-features/easy-to-carry_d.jpg'),
                mobile: require('./images/2-in-1-chillout-bag/tech-features/easy-to-carry_m.jpg'),
                alt: 'Close up on 2-in-1 Chillout Bag carrying straps',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Easy to Carry</span>,
                description: (
                  <span>
                    Built-in handles and a removable padded strap can be used as a tote handle, shoulder bag,
                    or cross body.
                  </span>
                ),
              },
            },

            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Built-In Cooler Compartment:</b> The separate cooler base features a seam-sealed
                    antimicrobial liner and is fully insulated with closed-cell foam to lock in the cold all
                    day long.
                  </span>,
                  <span>
                    <b>Durable Design:</b> Back-coated, ripstop fabric exterior repels water and makes it easy
                    to wipe clean.
                  </span>,
                  <span>
                    <b>Free Standing:</b> Designed with internal structure and a waterproof base keep this
                    tote upright, accessible, and dry.
                  </span>,
                  <span>
                    <b>Interior Pockets:</b> The interior features multiple mesh pockets and a water-repellent
                    zippered pocket with a built-in key leash to keep essentials organized.
                  </span>,
                ],
                [
                  <span>
                    <b>Exterior Organization:</b> The exterior features one large zippered pocket with a
                    tethered bottle opener and a large mesh pocket for wet or sandy items.
                  </span>,
                  <span>
                    <b>Built-In Clip Straps:</b> Convenient external clip straps are made to carry towels, a
                    yoga mat, or a blanket.
                  </span>,
                  <span>
                    <b>Easy to Carry:</b> Built-in handles and a removable padded strap can be used as a tote
                    handle, shoulder bag, or cross body.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.ULTRA_TOUGH_45QT_WHEELED:
      return {
        lifestyleHeader: {
          title: 'RUGGED & READY TO ROLL',
          image: {
            desktop: require('./images/45qt-ultra-tough-wheeled/Hero-Backup-D.jpg'),
            mobile: require('./images/45qt-ultra-tough-wheeled/Hero-Backup-M.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/45qt-ultra-tough_hero_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              Built tough, with all-terrain wheels to do the heavy lifting for you.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>60 can capacity</li>
              <li className='tw-text-base tw-font-normal'>Rotomolded construction for extreme durability</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 5 days when following{' '}
                <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/45qt-ultra-tough-wheeled/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2.9’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for up
                to 5 days when following{' '}
                <a className='tw-underline' target='_blank' href='/ice-retention-tips'>
                  RTIC's Cooling Tips
                </a>
                .
              </>
            ),
          },
          {
            icon: require('./images/45qt-ultra-tough-wheeled/icons/icon-rtic-tough.svg'),
            title: <>RTIC Tough</>,
            description: (
              <>
                Rotomolded Construction for extreme durability to outlast the most rugged adventures, year
                after year.
              </>
            ),
          },
          {
            icon: require('./images/45qt-ultra-tough-wheeled/icons/icon-strong-wheels.svg'),
            title: <>Ultra-Strong Wheels</>,
            description: (
              <>
                Puncture-resistant, all-terrain wheels tow through tough areas, like mud, sand, stairs and
                more, with ease.
              </>
            ),
          },
          {
            icon: require('./images/45qt-ultra-tough-wheeled/icons/icon-upright-design.svg'),
            title: <>Upright Design & Rugged Handle</>,
            description: (
              <>Retractable handle and upright design for easy maneuvering and stacking of gear.</>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/Tough-Enough-to-Go-the-Extra-Mile-Backup_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/Tough-Enough-to-Go-the-Extra-Mile-Backup_m.jpg'),
                alt: '45QT Ultra-Tough Wheeled cooler with all-terrain wheels',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/45qt-ultra-tough_extra-mile_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/45qt-ultra-tough_extra-mile_m.mp4`,
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH WHEELED COOLER',
                title: <span>Tough Enough to Go the Extra Mile</span>,
                description: (
                  <span>
                    With ultra-strong tires, and a reinforced suspension, our all-terrain, puncture-proof
                    wheels will help you take this cooler anywhere with ease, even with a full load. It can
                    handle mud, dry sand, stairs, rugged trails, and more.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/2-in-1-Bottle-Opener-Backup_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/2-in-1-Bottle-Opener-Backup_m.jpg'),
                alt: '45QT Ultra-Tough Wheeled cooler with 2-in-1 Bottle Opener',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/45qt-ultra-tough_bottle_opener_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/45qt-ultra-tough_bottle_opener_m.mp4`,
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH WHEELED COOLER',
                title: <span>2-in-1 Bottle Opener</span>,
                description: (
                  <span>
                    The bottle opener features a unique, multi-purpose design for your convenience. Grab it to
                    open drinks anytime, anywhere. Then, store it behind the silicone cargo net or place it
                    through the cooler lid to create a secure padlock shackle.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/Long-Live-the-Ice_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/Long-Live-the-Ice_m.jpg'),
                alt: 'RTIC 45QT Ultra-Tough Wheeled cooler with ice, drinks and food including Dry Goods Basket',
              },
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH WHEELED COOLER',
                title: <span>Long Live the Ice</span>,
                description: (
                  <span>
                    Built with up to 2.9’’ of closed-cell foam insulation, a freezer-style gasket, and secure
                    t-latches–this cooler locks in the cold for up to 5 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                    <br />
                    <br />
                    <span className='tw-text-sm tw-italic'>
                      Ultra-Tough Dry Goods Basket sold separately.
                    </span>
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/Durable-&-Portable_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/why-we-love-it/Durable-&-Portable_m.jpg'),
                alt: 'Going on a trip with the RTIC 45QT Ultra-Tough Wheeled cooler',
              },
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH WHEELED COOLER',
                title: <span>Durable & Portable</span>,
                description: (
                  <span>
                    This cooler is rotomolded for extreme durability, and also intentionally designed to fit
                    in more places. The narrow, upright design is the perfect shape to fit in smaller areas
                    and take up less space in the back of a car or boat deck. Plus, built-in lid lash points
                    make it easy to tow additional gear.
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: '',
              },
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>
                    Exterior Dimensions: 23” &times; 20 &frac34;” &times; 20 &frac12;”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>
                    Interior Dimensions: 17” &times; 16” &times; 11 &frac34;”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>Can Capacity: 60 Cans</p>
                  <p className='tw-mb-1 tw-font-medium'>Ice Capacity: 41 lbs.</p>
                  <p className='tw-mb-1 tw-font-medium'>Empty Weight: 33 lbs.</p>
                </>
              ),
              images: {
                desktop: require('./images/45qt-ultra-tough-wheeled/Dimensions_d.png'),
                mobile: require('./images/45qt-ultra-tough-wheeled/Dimensions_m.png'),
                alt: '45 QT Ultra-Tough Wheeled Cooler dimensions',
              },
            },
          ],
        },

        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Rotomolded-Construction_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Rotomolded-Construction_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler rotomolded construction',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Rotomolded Construction</span>,
                description: (
                  <span>
                    Rotomolded Construction for extreme durability to outlast the most rugged adventures, year
                    after year.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Premium-Ice-Retention_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Premium-Ice-Retention_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler closed-cell foam insulation and freezer-style gasket',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <span>
                    Up to 2.9’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for
                    up to 5 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Ultra-Strong-Wheels_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Ultra-Strong-Wheels_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler ultra-strong wheels',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Ultra-Strong Wheels</span>,
                description: (
                  <span>
                    Custom-designed, puncture- and impact-resistant tires easily navigate through tough
                    terrain.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Rugged-Retractable-Handle_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Rugged-Retractable-Handle_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler rugged retractable handle',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Rugged Retractable Handle</span>,
                description: (
                  <span>
                    The ergonomic, retractable handle makes it easy to tow and stores away when not in use.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/2-in-1-Bottle-Opener_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/2-in-1-Bottle-Opener_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler 2-in-1 Bottle Opener',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>2-in-1 Bottle Opener</span>,
                description: (
                  <span>
                    The multi-purpose bottle opener can be removed for ease of use, then stored behind the
                    cargo net or placed through the lid to create a padlock shackle to secure your cooler.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Upright-Design_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Upright-Design_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler upright design',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Upright Design</span>,
                description: (
                  <span>
                    The narrow, upright design is intentionally designed to fit in smaller areas and take up
                    less space.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Fully-Protected-Wheel-Axle_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Fully-Protected-Wheel-Axle_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler fully protected wheel axle',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Fully Protected Wheel Axle</span>,
                description: (
                  <span>
                    Premium wheel bearings and rugged stainless steel axle deliver an ultra-smooth ride and
                    long-lasting durability.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Secure-T-Latches_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Secure-T-Latches_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler secure T-latches',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Secure T-Latches</span>,
                description: (
                  <span>
                    Silicone T-latches clamp down to secure your goods and a freezer-style gasket keeps your
                    cooler sealed tight.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Built-In-Cargo-Net_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Built-In-Cargo-Net_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler built-in cargo net',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Cargo Net</span>,
                description: (
                  <span>
                    A durable, silicone cargo net is built into the lid of your cooler for easy storing of
                    smaller items.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Lid-Lash-Points_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Lid-Lash-Points_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler lid lash points',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Lid Lash Points</span>,
                description: (
                  <span>
                    Lid lash points are built in to tie down and tow additional gear on the top of the cooler.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Single-Plug-Rapid-V-Drain_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Single-Plug-Rapid-V-Drain_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler single plug rapid V-drain',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Single-Plug Rapid V-Drain</span>,
                description: (
                  <span>
                    Single plug rapid V-drain is tucked away in the back of the cooler and allows for easy,
                    hands-free draining.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-wheeled/features/Non-Skid-Rubber-Feet_d.jpg'),
                mobile: require('./images/45qt-ultra-tough-wheeled/features/Non-Skid-Rubber-Feet_m.jpg'),
                alt: 'Close up on 45QT Ultra-Tough Wheeled cooler non-skid rubber feet',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Non-Skid Rubber Feet</span>,
                description: (
                  <span>
                    Non-Skid Rubber Feet help keep the cooler in place even on the smoothest surfaces.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Rotomolded Construction</b> for extreme durability to outlast the most rugged
                    adventures, year after year.
                  </span>,
                  <span>
                    <b>Up to 2.9’’ of closed-cell foam insulation</b> and a freezer-style gasket lock in the
                    cold for up to 5 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                  </span>,
                  <span>
                    <b>Ultra-Strong Wheels</b> are custom-designed, puncture- and impact-resistant tires
                    easily navigate through tough terrain.
                  </span>,
                  <span>
                    <b>Rugged Retractable Handle</b> makes it easy to tow and stores away when not in use.
                  </span>,
                  <span>
                    <b>2-in-1 Bottle Opener</b> can be removed for ease of use, then stored behind the cargo
                    net or placed through the lid to create a padlock shackle to secure your cooler.
                  </span>,
                  <span>
                    <b>Upright Design</b> is intentionally designed to fit in smaller areas and take up less
                    space.
                  </span>,
                ],
                [
                  <span>
                    <b>Fully-Protected Wheel Axle</b> and premium wheel bearings for a smooth ride and
                    long-lasting durability.
                  </span>,
                  <span>
                    <b>Silicone T-Latches</b> clamp down to secure your goods and a freezer-style gasket keeps
                    your cooler sealed tight. .
                  </span>,
                  <span>
                    <b>Built-in Cargo Net</b> is made of durable silicone and makes it easy to store smaller,
                    dry items.
                  </span>,
                  <span>
                    <b>Lid Lash Points</b> are built in to tie down and tow additional gear on the top of the
                    cooler.
                  </span>,
                  <span>
                    <b>Single Plug Rapid V-Drain</b> is tucked away in the back of the cooler and allows for
                    easy, hands-free draining. .
                  </span>,
                  <span>
                    <b>Non-Skid Rubber Feet</b> help keep the cooler in place even on the smoothest surfaces.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.ULTRA_LIGHT_22_QT:
      return {
        lifestyleHeader: {
          title: 'YOUR GOOD TIME GO-TO COOLER',
          image: {
            desktop: require('./images/22qt-ultralight/hero-fallback-d.jpg'),
            mobile: require('./images/22qt-ultralight/hero-fallback-m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/22qt-ultralight-hero_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              A compact cooler you can pack and carry your way.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>Fits up to 30 cans or 8 wine bottles</li>
              <li className='tw-text-base tw-font-normal'>30% lighter than rotomolded coolers</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 6 days when following{' '}
                <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2.4’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for up
                to 6 days when following{' '}
                <a className='tw-underline' target='_blank' href='/ice-retention-tips'>
                  RTIC's Cooling Tips
                </a>
                .
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-construction-leaf.svg'),
            title: <>Ultra-Light Construction</>,
            description: (
              <>Engineered to be up to 30% lighter than competing rotomolded coolers of similar capacities.</>
            ),
          },
          {
            icon: require('./images/icons/icon-carrying-strap.svg'),
            title: <>2-in-1 Carrying Strap</>,
            description: (
              <>
                Durable, padded carrying strap can be fully adjusted to be used as a handle or shoulder strap.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-upright-design.svg'),
            title: <>Upright Design</>,
            description: (
              <>
                Designed to fit up to 8 standard wine bottles. Plus, compatible wine bottle holders and
                divider offer more stability.
              </>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/meet-the-collection/Pack-It-Your-Way-fallback_d.jpg'),
                mobile: require('./images/22qt-ultralight/meet-the-collection/Pack-It-Your-Way-fallback_m.jpg'),
                alt: 'Packing the RTIC 22QT Ultra-Light cooler',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/22qt-ultralight-pack_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/22qt-ultralight-pack_m.mp4`,
              copy: {
                tagline: 'MEET THE 22 QT ULTRA-LIGHT COOLER',
                title: `<span>Pack It Your Way</span>`,
                description: `<span>The 22 QT Ultra-Light Cooler offers even more ways to customize your experience. Choose from a variety of accessories, such as wine bottle holders, baskets, and freezable divider, to accessorize this cooler just the way you want it.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/meet-the-collection/Carry-It-Your-Way-fallback_d.jpg'),
                mobile: require('./images/22qt-ultralight/meet-the-collection/Carry-It-Your-Way-fallback_m.jpg'),
                alt: 'Carrying the RTIC 22QT Ultra-Light cooler',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/22qt-ultralight-carry_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/22qt-ultralight-carry_m.mp4`,
              copy: {
                tagline: 'MEET THE 22 QT ULTRA-LIGHT COOLER',
                title: `<span>Carry It Your Way</span>`,
                description: `<span>The durable, padded carrying strap can be fully adjusted based on your preference with two convenient sliders. Carry it on your shoulder, shorten it to a handle, or loosen it all the way as a crossbody.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/meet-the-collection/Take-It-Your-Way_d.jpg'),
                mobile: require('./images/22qt-ultralight/meet-the-collection/Take-It-Your-Way_m.jpg'),
                alt: 'A man carrying the RTIC 22QT Ultra-Light cooler',
              },
              copy: {
                tagline: 'MEET THE 22 QT ULTRA-LIGHT COOLER',
                title: `<span>Take It Your Way</span>`,
                description: `<span>This cooler is built 30% lighter than competing rotomolded coolers. That means it’s built to go anywhere and everywhere with you, while locking in the cold for multiple days. So whether you’re heading to the beach, taking lunch on the go, or packing for a quick overnight–this cooler is up for it.</span>`,
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>
                    Exterior Dimensions: 19 &frac12;” &times; 13” &times; 15 &frac34;”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>Can Capacity: 30 Cans</p>
                  <p className='tw-mb-1 tw-font-medium'>Ice Capacity: 22.5 lbs.</p>
                  <p className='tw-mb-1 tw-font-medium'>Empty Weight: 11.5 lbs.</p>
                </>
              ),
              images: {
                desktop: require('./images/22qt-ultralight/dims_d.png'),
                mobile: require('./images/22qt-ultralight/dims_m.png'),
                alt: '22 QT Ultra-Light dimensions',
              },
            },
          ],
        },

        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Premium-Ice-Retention-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Premium-Ice-Retention-m.jpg'),
                alt: 'Close up of the gasket on the lid of the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <span>
                    Up to 2.4’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for
                    up to 6 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Upright-Design-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Upright-Design-m.jpg'),
                alt: 'RTIC 22QT Ultra-Light Cooler showing upright design',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Upright Design</span>,
                description: (
                  <span>Upright design is wine bottle compatible and fits up to 8 wine bottles.</span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/2-in-1-Carrying-Strap-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/2-in-1-Carrying-Strap-m.jpg'),
                alt: 'RTIC 22QT Ultra-Light Cooler with 2-in-1 carrying strap',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>2-in-1 Carrying Strap</span>,
                description: (
                  <span>
                    2-in-1 Carrying Strap is made of durable webbing with a neoprene handle. It can be used as
                    a shoulder strap or shortened to a handle.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Secure-T-Latches-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Secure-T-Latches-m.jpg'),
                alt: 'Close up of the T-latches on the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Secure T-Latches</span>,
                description: (
                  <span>
                    Silicone T-Latches clamp down to secure your goods and a freezer-style gasket keeps your
                    cooler sealed tight.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Rapid-V-Drain-System-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Rapid-V-Drain-System-m.jpg'),
                alt: 'Close up of the drain plug on the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Rapid V-Drain System</span>,
                description: (
                  <span>
                    Single Plug Rapid V-Drain is tucked away in the back of the cooler and allows for easy,
                    hands-free draining.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Built-In-Cargo-Net-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Built-In-Cargo-Net-m.jpg'),
                alt: 'Close up of the cargo net on the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Cargo Net</span>,
                description: (
                  <span>
                    A small, durable silicone cargo net is built into the lid of your cooler for easy storing
                    of smaller items.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Built-In-Bottle-Opener-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Built-In-Bottle-Opener-m.jpg'),
                alt: 'Close up of the bottle opener on the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Bottle Opener</span>,
                description: (
                  <span>
                    A built-in bottle opener made of premium stainless steel makes it easy to open drinks
                    anytime, anywhere.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight/tech-features/Non-Skid-Rubber-Feet-d.jpg'),
                mobile: require('./images/22qt-ultralight/tech-features/Non-Skid-Rubber-Feet-m.jpg'),
                alt: 'Close up of the non-skid rubber feet on the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Non-Skid Rubber Feet</span>,
                description: (
                  <span>
                    Non-skid rubber feet help keep the cooler in place, even on the smoothest and most
                    slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially
                    when using in high heat or in direct sun.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Up to 2.4’’ of closed-cell foam insulation</b> and a freezer-style gasket lock in the
                    cold for up to 6 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC's Cooling Tips
                    </a>
                    .
                  </span>,
                  <span>
                    <b>Upright design</b> is wine bottle compatible and fits up to 8 wine bottles.
                  </span>,
                  <span>
                    <b>2-in-1 Carrying Strap</b> is made of durable webbing with a neoprene handle. It can be
                    used as a shoulder strap or shortened to a handle.
                  </span>,
                  <span>
                    <b>Silicone T-Latches</b> clamp down to secure your goods and a freezer-style gasket keeps
                    your cooler sealed tight.
                  </span>,
                ],
                [
                  <span>
                    <b>Single Plug Rapid V-Drain</b> is tucked away in the back of the cooler and allows for
                    easy, hands-free draining.
                  </span>,
                  <span>
                    <b>A small, durable silicone cargo net</b> is built into the lid of your cooler for easy
                    storing of smaller items.
                  </span>,
                  <span>
                    <b>A built-in bottle opener</b> made of premium stainless steel makes it easy to open
                    drinks anytime, anywhere.
                  </span>,
                  <span>
                    <b>Non-skid rubber feet</b> help keep the cooler in place, even on the smoothest and most
                    slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially
                    when using in high heat or in direct sun.
                  </span>,
                ],
              ],
            },
          ],
        },
        collectionComponent: <MeetTheUltraLightCollection />,
      };

    case ProductTypeId.ULTRA_LIGHT_22_QT_WHEELED:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              GOOD TIMES <br />
              ON THE GO
            </>
          ),
          image: {
            desktop: require('./images/22qt-ultralight-wheeled/Hero-Fallback-D.jpg'),
            mobile: require('./images/22qt-ultralight-wheeled/Hero-Fallback-M.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/22qt-ultralight-wheeled-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/22qt-ultralight-wheeled-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              The ultimate go-anywhere, do-everything, easy wheeled cooler.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>Fits up to 28 cans or 6 wine bottles</li>
              <li className='tw-text-base tw-font-normal'>All-terrain, puncture-proof wheels</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 4 days when following <RticCoolingTipsLink />
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2.3’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for up
                to 4 days when following <RticCoolingTipsLink />.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-ultra-strong-wheels.svg'),
            title: <>Ultra-Strong Wheels</>,
            description: (
              <>Puncture-proof, all-terrain wheels help you tow through rugged terrains with ease.</>
            ),
          },
          {
            icon: require('./images/icons/icon-rugged-retractable-handle.svg'),
            title: <>Rugged Retractable Handle</>,
            description: (
              <>The ergonomic retractable handle makes it comfortable to tow a full cooler for long hauls.</>
            ),
          },
          {
            icon: require('./images/icons/icon-upright-design.svg'),
            title: <>Upright Design</>,
            description: (
              <>
                Designed to fit up to 6 standard wine bottles. Plus, compatible wine bottle holders and
                divider offer more stability.
              </>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/meet-the-collection/Pack-It-Your-Way-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/meet-the-collection/Pack-It-Your-Way-M.jpg'),
                alt: 'Packing the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'MEET THE 22 QT ULTRA-LIGHT WHEELED COOLER',
                title: <span>Pack It Your Way</span>,
                description: (
                  <span>
                    The 22 QT Ultra-Light Wheeled Cooler locks in the cold for up to 4 days and offers even
                    more ways to customize your experience. Choose from a variety of accessories, such as wine
                    bottle holders, a basket, and freezable divider, to accessorize this cooler just the way
                    you want it.
                    <br />
                    <br />
                    Accessories sold separately.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/meet-the-collection/Roll-It-Your-Way-D-Fallback.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/meet-the-collection/Roll-It-Your-Way-M-Fallback.jpg'),
                alt: 'Rolling the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/22qt-ultralight-wheeled-roll-it-your-way_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/22qt-ultralight-wheeled-roll-it-your-way_m.mp4`,
              copy: {
                tagline: 'MEET THE 22 QT ULTRA-LIGHT WHEELED COOLER',
                title: <span>Roll It Your Way</span>,
                description: (
                  <span>
                    With ultra-strong tires and a reinforced suspension, our all-terrain, puncture-resistant
                    wheels will help you take this cooler anywhere with ease. It can handle mud, dry sand,
                    stairs, rugged trails, and more.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/meet-the-collection/Take-It-Your-Way-D-Fallback.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/meet-the-collection/Take-It-Your-Way-M-Fallback.jpg'),
                alt: 'A man carrying the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/22qt-ultralight-wheeled-take-it-your-way_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/22qt-ultralight-wheeled-take-it-your-way_m.mp4`,
              copy: {
                tagline: 'MEET THE 22 QT ULTRA-LIGHT WHEELED COOLER',
                title: <span>Take It Your Way</span>,
                description: (
                  <span>
                    Not only is this cooler light and durable, it also features an upright design that gives
                    it a small footprint to take up less space. That means it’s built to go anywhere and
                    everywhere with you, so whether you’re heading to the beach, towing to the tailgate, or
                    packing for a quick overnight–this wheeled cooler is up for it.
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>
                    Exterior Dimensions: 21.16” &times; 19.29” &times; 15.67”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>
                    Interior Dimensions (Inside Bottom): 12.76” &times; 8.66”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>Can Capacity: 28 Cans</p>
                  <p className='tw-mb-1 tw-font-medium'>Empty Weight: 17.8 lbs.</p>
                </>
              ),
              images: {
                desktop: require('./images/22qt-ultralight-wheeled/Dims-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/Dims-M.jpg'),
                alt: '22 QT Ultra-Light Wheeled dimensions',
              },
            },
          ],
        },

        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Ultra-Strong-Wheels-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Ultra-Strong-Wheels-M.jpg'),
                alt: 'Close up of the Ultra-Strong Wheels on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Ultra-Strong Wheels</span>,
                description: (
                  <span>
                    Custom-designed, puncture- and impact-resistant tires easily navigate through most any
                    terrain.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Premium-Ice-Retention-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Premium-Ice-Retention-M.jpg'),
                alt: 'Close up of the Premium Ice Retention on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <span>
                    Up to 2.3’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for
                    up to 4 days when following <RticCoolingTipsLink />.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Ultra-Light-Construction-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Ultra-Light-Construction-M.jpg'),
                alt: 'Close up of the Ultra-Light Construction on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Ultra-Light Construction</span>,
                description: (
                  <span>
                    Engineered with RTIC’s Ultra-Light technology for a lightweight, durable design.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Upright-Design-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Upright-Design-M.jpg'),
                alt: 'Close up of the Upright Design on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Upright Design</span>,
                description: (
                  <span>
                    Narrow, upright design fits in smaller areas and is wine bottle compatible, fitting up to
                    6 wine bottles or 28 cans.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Retractable-Handle-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Retractable-Handle-M.jpg'),
                alt: 'Close up of the Retractable Handle on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Retractable Handle</span>,
                description: (
                  <span>
                    The ergonomic, retractable handle makes it easy to tow and stores away when not in use.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Fully-Protected-Wheel-Axle-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Fully-Protected-Wheel-Axle-M.jpg'),
                alt: 'Close up of the Fully-Protected Wheel Axle on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Fully-Protected Wheel Axle</span>,
                description: (
                  <span>
                    Premium wheel bearings and rugged stainless steel axle deliver an ultra-smooth ride and
                    long-lasting durability.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Secure-T-Latches-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Secure-T-Latches-M.jpg'),
                alt: 'Close up of the Secure T-Latches on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Secure T-Latches</span>,
                description: (
                  <span>
                    Silicone T-Latches clamp down to secure your goods and a freezer-style gasket keeps your
                    cooler sealed tight.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Silicone-Cargo-Net-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Silicone-Cargo-Net-M.jpg'),
                alt: 'Close up of the Silicone Cargo Net on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Silicone Cargo Net</span>,
                description: (
                  <span>
                    A small, durable silicone cargo net is built into the lid of your cooler for easy storing
                    of smaller items.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Built-In-Bottle-Opener-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Built-In-Bottle-Opener-M.jpg'),
                alt: 'Close up of the Built-In Bottle Opener on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Bottle Opener</span>,
                description: (
                  <span>
                    A built-in bottle opener made of premium stainless steel makes it easy to open drinks
                    anytime, anywhere.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Rapid-V-Drain-System-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Rapid-V-Drain-System-M.jpg'),
                alt: 'Close up of the Rapid V-Drain System on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Rapid V-Drain System</span>,
                description: (
                  <span>
                    Single plug rapid V-drain is tucked away in the back of the cooler and allows for easy,
                    hands-free draining.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/22qt-ultralight-wheeled/features/Non-Skid-Rubber-Feet-D.jpg'),
                mobile: require('./images/22qt-ultralight-wheeled/features/Non-Skid-Rubber-Feet-M.jpg'),
                alt: 'Close up of the Non-Skid Rubber Feet on the RTIC 22QT Ultra-Light Wheeled Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Non-Skid Rubber Feet</span>,
                description: (
                  <span>
                    Non-skid rubber feet help keep the cooler in place, even on smooth or slippery surfaces.
                    Avoid setting coolers on light-colored surfaces or fabrics, especially when using in high
                    heat or in direct sun.
                  </span>
                ),
              },
            },

            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Ultra-Strong Wheels</b> custom-designed, puncture- and impact-resistant tires easily
                    navigate through most any terrain.
                  </span>,
                  <span>
                    <b>Premium Ice Retention</b> up to 2.3’’ of closed-cell foam insulation and a
                    freezer-style gasket lock in the cold for up to 4 days when following{' '}
                    <RticCoolingTipsLink />.
                  </span>,
                  <span>
                    <b>Ultra-Light Construction</b> engineered with RTIC’s Ultra-Light technology for a
                    lightweight, durable design.
                  </span>,
                  <span>
                    <b>Upright Design</b> narrow, upright design fits in smaller areas and is wine bottle
                    compatible, fitting up to 6 wine bottles or 28 cans.
                  </span>,
                  <span>
                    <b>Retractable Handle</b> ergonomic, retractable handle makes it easy to tow and stores
                    away when not in use.
                  </span>,
                ],
                [
                  <span>
                    <b>Fully-Protected Wheel Axle</b> premium wheel bearings and rugged stainless steel axle
                    deliver an ultra-smooth ride and long-lasting durability.
                  </span>,
                  <span>
                    <b>Secure T-Latches</b> clamp down to secure your goods and a freezer-style gasket keeps
                    your cooler sealed tight.
                  </span>,
                  <span>
                    <b>Silicone Cargo Net</b> small, durable silicone cargo net is built into the lid of your
                    cooler for easy storing of smaller items.
                  </span>,
                  <span>
                    <b>Built-In Bottle Opener</b> made of premium stainless steel makes it easy to open drinks
                    anytime, anywhere.
                  </span>,
                  <span>
                    <b>Rapid V-Drain System</b> single plug rapid V-drain is tucked away in the back of the
                    cooler and allows for easy, hands-free draining.
                  </span>,
                  <span>
                    <b>Non-Skid Rubber Feet</b> help keep the cooler in place, even on smooth or slippery
                    surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially when
                    using in high heat or in direct sun.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.RTIC_ICE_LUNCH_BAG:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              Portable meets freezable: designed with built-in, freezable gel packs to lock in the cold.
            </p>
            <ul className='tw-mb-0 tw-list-disc tw-pl-5 tw-text-base tw-font-normal'>
              <li>RTIC Tough exterior fabric and waterproof interior</li>
              <li>Fold-flat design</li>
              <li>Freezable design locks in the cold for multiple hours</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.LIGHTWEIGHT_BACKPACK_COOLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              Designed like a backpack. Built like a cooler.
            </p>
            <ul className='tw-mb-0 tw-list-disc tw-pl-5 tw-text-base tw-font-normal'>
              <li>15 can capacity</li>
              <li>Lightweight, portable design</li>
              <li>
                Locks in the cold for up to 24 hours when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.ULTRA_LIGHT_32_QT:
      return {
        lifestyleHeader: {
          title: 'Travel Lighter',
          image: {
            desktop: require('/public/images/rtic/pdp/ultra-light/extras/travel-lighter_d.jpg'),
            mobile: require('/public/images/rtic/pdp/ultra-light/extras/travel-lighter_m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/ultralight-32-pdp.mp4`,
        },
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: 'Premium Ice Retention',
            description: (
              <>
                Up to 2.5’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for up
                to 5 days with <RticCoolingTipsLink />.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-ultra-light-construction.svg'),
            title: 'Ultra-Light Construction',
            description: 'Engineered to be up to 30% lighter than rotomolded coolers of similar capacities.',
          },
          {
            icon: require('./images/icons/icon-compact-design.svg'),
            title: 'Compact Design',
            description:
              'Compact and easier to maneuver, this lightweight cooler can be carried by one person.',
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/why-we-love-it-1_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/why-we-love-it-1_m.jpg'),
                alt: 'Woman carrying RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Ditch The Weight, <br />Not The Fun.</span>`,
                description: (
                  <span>
                    Compact, and easier to maneuver, the 32 QT Ultra-Light is an even more portable version of
                    our best-selling cooler you’ve come to love. Like the 52 QT, it’s engineered to be up to
                    30% lighter than rotomolded coolers.
                    <br />
                    <br />
                    Weight: 13.4 lbs.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/why-we-love-it-2_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/why-we-love-it-2_m.jpg'),
                alt: 'Dog standing on top of RTIC 32QT Ultra-Light Cooler sitting in a kayak',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Shed The Pounds, <br />Not The Cold.</span>`,
                description: `<span>Takes up less space than the original Ultra-Light, and is lighter and easier to carry for smaller adventures. The injection molded construction with O-ring gaskets seal in the cold for long ice duration.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/why-we-love-it-3_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/why-we-love-it-3_m.jpg'),
                alt: 'Bottle opener being used on the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Utility Comes Standard</span>`,
                description: `<span>An on-board bottle opener is conveniently built right in. Plus the non-skid feet and traction top make for a great casting platform. Go ahead, pack it with fun.</span>`,
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: 'Dimensions',
                icon: require('./images/icon-ruler.svg'),
              },
              images: {
                desktop: require('./images/32qt-ultralight/dims_d.png'),
                mobile: require('./images/32qt-ultralight/dims_m.png'),
                alt: 'RTIC 32QT Ultra-Light Cooler front view and side view showing dimensions of the cooler',
              },
              description: (
                <>
                  <p>Exterior Dimensions: 23” &times; 14 &frac12;” &times; 14 &frac12;”</p>
                  <p>Interior Dimensions: 17 &frac12;” &times; 11” &times; 9 &frac34;”</p>
                  <p>Empty Weight: 13 lbs.</p>
                </>
              ),
            },
            {
              title: {
                text: 'Cans Only',
                icon: require('./images/icon-can.svg'),
              },
              images: {
                desktop: require('./images/32qt-ultralight/cans_d.png'),
                mobile: require('./images/32qt-ultralight/cans_m.png'),
                alt: 'RTIC 32QT Ultra-Light Cooler filled with 48 cans',
              },
              description: (
                <>
                  <p>Holds Up To 48 Cans (No Ice)</p>
                </>
              ),
            },
            {
              title: {
                text: 'Ice Only',
                icon: require('./images/icon-ice.svg'),
              },
              images: {
                desktop: require('./images/32qt-ultralight/ice_d.png'),
                mobile: require('./images/32qt-ultralight/ice_m.png'),
                alt: 'RTIC 32QT Ultra-Light Cooler filled with 30lbs. of ice',
              },
              description: (
                <>
                  <p>Holds Up To 30 lbs of Ice</p>
                </>
              ),
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/features/foam-insulation_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/features/foam-insulation_m.jpg'),
                alt: 'Close up of the gasket on the lid of the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Foam Insulation & O-Ring Gasket Hold In The Cold</span>`,
                description: `<span>Up to 2.5 inches of foam insulation and freezer style gasket seals in the cold for long-lasting ice retention.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/features/premium-stainless_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/features/premium-stainless_m.jpg'),
                alt: 'Close up of the built-in bottle opener on the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Premium Stainless Steel Hardware Will Not Rust</span>`,
                description: `<span>On-board bottle opener conveniently built right in.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/features/nylon-rope-handles_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/features/nylon-rope-handles_m.jpg'),
                alt: 'Close up of the rope handles of the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Marine-Grade Nylon Rope Handles</span>`,
                description: `<span>Comfortable hand grips make it even easier to maneuver.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/features/non-marking-rubber-feet_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/features/non-marking-rubber-feet_m.jpg'),
                alt: 'Close up of the rubber feet on the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Non-Skid Rubber Feet</span>`,
                description: (
                  <span>
                    Non-skid rubber feet help keep the cooler in place, even on the smoothest and most
                    slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially
                    when using in high heat or in direct sun.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/features/durable-t-latches_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/features/durable-t-latches_m.jpg'),
                alt: 'Close up of the rubber T-Latches on the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Durable T-Latches Clamp Down Lid Securely</span>`,
                description: `<span>Durable t-latches hold in the cold to protect your provisions all weekend long.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('/public/images/rtic/pdp/ultra-light/extras/features/tie-down-slots_d.jpg'),
                mobile: require('/public/images/rtic/pdp/ultra-light/extras/features/tie-down-slots_m.jpg'),
                alt: 'Close up of the tie down slots on the RTIC 32QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Tie Down Slots To Attach To Boat, Truck Bed, & More</span>`,
                description: `<span>Tie down slots so you can secure the most portable version of our best-selling cooler on your smaller adventures.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  'Convenient, easy to handle size.',
                  'Up to 2.5 inches of closed cell foam insulation with an O-Ring freezer style gasket that seals in cold for longer ice retention.',
                  'Lightweight and easy to carry due to its injection molded construction.',
                  '2 drainage holes and Rapid V-Drain System for quick and easy draining in any position.',
                  'Durable t-latches clamp down lid securely.',
                  'Non-skid rubber feet help keep the cooler in place, even on the smoothest and most slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially when using in high heat or in direct sun.',
                ],
                [
                  'Marine-grade Nylon Rope Handles with comfortable hand grips.',
                  'Molded-In Side Handles.',
                  'Premium stainless steel hardware will not rust.',
                  'Tie down slots to attach to boat, trailer or truck bed.',
                  'Traction lid - can be used as a casting platform.',
                  'Integrated Locking System allows cooler to be secured.',
                  'Custom Shop ready - a bold showcase of your brand!',
                ],
              ],
            },
          ],
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              Carry lighter, adventure more with this light, strong cooler.
            </p>
            <ul className='tw-mb-0 tw-list-disc tw-pl-5 tw-text-base tw-font-normal'>
              <li>48 can capacity</li>
              <li>30% lighter than rotomolded coolers</li>
              <li>
                Locks in the cold for up to 5 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        collectionComponent: <MeetTheUltraLightCollection />,
      };

    case ProductTypeId.ANYWHERE_BLANKET:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              A comfortable, adventure-ready blanket that repels dirt, pet hair, spills, and more.{' '}
            </p>
            <ul className='tw-mb-0 tw-list-none tw-space-y-1 tw-pl-0 tw-text-base'>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/anywhere-blanket/heavy-duty.svg')}
                />
                <div>Heavy-Duty Waterproof Base Fabric</div>
              </li>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/anywhere-blanket/water-repellent.svg')}
                />
                <div>Water & Stain-Repellent Quilted Top</div>
              </li>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/anywhere-blanket/carrying-handle.svg')}
                />
                <div>Built-In Carrying Handle</div>
              </li>
            </ul>
          </>
        ),
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              title: {
                text: 'Laid Flat',
              },
              images: {
                desktop: require('./images/anywhere-blanket/dimensions-flat_d.png'),
                mobile: require('./images/anywhere-blanket/dimensions-flat_m.png'),
                alt: 'Anywhere Blanket flat dimensions',
              },
            },
            {
              title: {
                text: 'Rolled Up',
              },
              images: {
                desktop: require('./images/anywhere-blanket/rolled-up.png'),
                mobile: require('./images/anywhere-blanket/rolled-up.png'),
                alt: 'Anywhere Blanket rolled up dimensions',
              },
            },
          ],
        },
      };

    case ProductTypeId.HARD_COOLER_110_QT_TYPE:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              A large, durable cooler built for rugged trips and longer journeys
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>130 can capacity</li>
              <li>Rotomolded construction for extreme durability</li>
              <li>
                Locks in the cold for up to 9 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.HARD_COOLER_145_QT_TYPE:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              A large, durable cooler built for rugged trips and longer journeys
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>195 can capacity</li>
              <li>Rotomolded construction for extreme durability</li>
              <li>
                Locks in the cold for up to 7 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.HARD_COOLER_65_QT_TYPE:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              A mid-sized, durable cooler built to withstand the extremes.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>84 can capacity</li>
              <li>Rotomolded construction for extreme durability</li>
              <li>
                Locks in the cold for up to 7 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.HARD_COOLER_45_QT_TYPE:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              BUILT <br />
              TO LAST
            </>
          ),
          image: {
            desktop: require('./images/45qt-ultra-tough-new-mold/Hero-D-Fallback.jpg'),
            mobile: require('./images/45qt-ultra-tough-new-mold/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/45qt-ultra-tough_gen2_hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/45qt-ultra-tough_gen2_hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              A mid-sized, durable cooler built to handle years of rugged adventures.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>60 can capacity</li>
              <li className='tw-text-base tw-font-normal'>Rotomolded construction for extreme durability</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 5 days when following{' '}
                <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/45qt-ultra-tough-new-mold/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2.8’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for up
                to 5 days when following{' '}
                <a className='tw-underline' target='_blank' href='/ice-retention-tips'>
                  RTIC's Cooling Tips
                </a>
                .
              </>
            ),
          },
          {
            icon: require('./images/45qt-ultra-tough-new-mold/icons/icon-rtic-tough.svg'),
            title: <>RTIC Tough</>,
            description: (
              <>
                Rotomolded Construction for extreme durability to outlast the most rugged adventures, year
                after year.
              </>
            ),
          },
          {
            icon: require('./images/45qt-ultra-tough-new-mold/icons/icon-cooler.svg'),
            title: <>The Perfect Size</>,
            description: (
              <>
                This mid-sized cooler fits up to 60 cans, making it the ideal size for a multi-day adventure.
              </>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/meet-the-collection/Unmatched-Durability-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/meet-the-collection/Unmatched-Durability-M.jpg'),
                alt: 'RTIC 45QT Ultra-Tough Cooler rugged design',
              },
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH COOLER',
                title: <span>Unmatched Durability</span>,
                description: (
                  <span>
                    The 45 QT Ultra-Tough Cooler is built to handle rugged adventures, year after year.
                    Featuring rotomolded construction, marine-grade rope handles, and secure silicone
                    t-latches–this cooler offers RTIC toughness you can rely on.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/meet-the-collection/Long-Live-the-Ice-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/meet-the-collection/Long-Live-the-Ice-M.jpg'),
                alt: 'RTIC 45QT Ultra-Tough Cooler with ice, drinks and food',
              },
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH COOLER',
                title: <span>Long Live the Ice</span>,
                description: (
                  <span>
                    Built with up to 2.8’’ of closed-cell foam insulation, a freezer-style gasket, and secure
                    t-latches–this cooler locks in the cold for up to 5 days when following{' '}
                    <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/meet-the-collection/Keep-it-Organized-D-Fallback.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/meet-the-collection/Keep-it-Organized-M-Fallback.jpg'),
                alt: 'RTIC 45QT Ultra-Tough Cooler lid features a durable silicone cargo net',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/45qt-ultra-tough_gen2_organized_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/45qt-ultra-tough_gen2_organized_m.mp4`,
              copy: {
                tagline: 'MEET THE 45 QT ULTRA-TOUGH COOLER',
                title: <span>Keep it Organized</span>,
                description: (
                  <span>
                    This cooler lid features a durable silicone cargo net for easy storing of small or dry
                    good items. It also features a built-in, stainless steel bottle opener so you can open
                    drinks anytime, anywhere.
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: '',
              },
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>
                    Exterior Dimensions: 26.35” &times; 18.19” &times; 15.75”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>
                    Interior Dimensions: 18.90” &times; 11.42” &times; 10.98”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>Can Capacity: Up to 60 Cans</p>
                  <p className='tw-mb-1 tw-font-medium'>Ice Capacity: Up to 52 lbs. of Ice</p>
                  <p className='tw-mb-1 tw-font-medium'>Empty Weight: 29.5 lbs.</p>
                </>
              ),
              images: {
                desktop: require('./images/45qt-ultra-tough-new-mold/Dims-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/Dims-M.jpg'),
                alt: '45 QT Ultra-Tough Cooler dimensions',
              },
            },
          ],
        },

        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Rotomolded-Construction-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Rotomolded-Construction-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler rotomolded construction',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Rotomolded Construction</span>,
                description: (
                  <span>
                    Rotomolded Construction for extreme durability to outlast the most rugged adventures, year
                    after year.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Premium-Ice-Retention-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Premium-Ice-Retention-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler ice retention',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <span>
                    Up to 2.8’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for
                    up to 5 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips{' '}
                    </a>
                    .
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Built-In-Bottle-Opener-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Built-In-Bottle-Opener-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler built-in bottle opener',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Bottle Opener</span>,
                description: (
                  <span>
                    A built-in bottle opener made of premium stainless steel makes it easy to open drinks
                    anytime, anywhere.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Built-In-Cargo-Net-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Built-In-Cargo-Net-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler built-in cargo net',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Built-In Cargo Net</span>,
                description: (
                  <span>
                    A durable silicone cargo net is built into the lid of your cooler for easy storing of
                    smaller items.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Secure-T-Latches-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Secure-T-Latches-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler secure T-Latches',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Secure T-Latches</span>,
                description: (
                  <span>
                    Silicone T-Latches clamp down to secure your goods and a freezer-style gasket keeps your
                    cooler sealed tight.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Rapid-V-Drain-System-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Rapid-V-Drain-System-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler Rapid V-Drain System',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Rapid V-Drain System (Dual Plug)</span>,
                description: (
                  <span>Dual Plug Rapid V-Drain System allows for easy, hands-free draining.</span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Non-Skid-Rubber-Feet-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Non-Skid-Rubber-Feet-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler non-skid rubber feet',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Non-Skid Rubber Feet</span>,
                description: (
                  <span>
                    Non-Skid Rubber Feet help keep the cooler in place even on the smoothest surfaces.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Integrated-Lock-Holes-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Integrated-Lock-Holes-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler integrated lock holes',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Integrated Lock Holes</span>,
                description: (
                  <span>
                    Two, integrated lock holes allow the cooler to be secured shut with personal padlocks.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/45qt-ultra-tough-new-mold/features/Compatible-Accessories-D.jpg'),
                mobile: require('./images/45qt-ultra-tough-new-mold/features/Compatible-Accessories-M.jpg'),
                alt: 'Close up on 45QT Ultra-Tough cooler rotomolded construction',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Compatible Accessories</span>,
                description: (
                  <span>
                    Get the most out of your cooler and stay organized with a compatible RTIC freezable
                    divider and dry goods basket. Note: Accessories are sold separately.
                  </span>
                ),
              },
            },

            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Rotomolded Construction</b> for extreme durability to outlast the most rugged
                    adventures, year after year.
                  </span>,
                  <span>
                    <b>Premium Ice Retention</b> with up to 2.8’’ of closed-cell foam insulation and a
                    freezer-style gasket lock in the cold for up to 5 days when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                  </span>,
                  <span>
                    <b>Built-In Bottle Opener</b> made of premium stainless steel makes it easy to open drinks
                    anytime, anywhere.
                  </span>,
                  <span>
                    <b>Built-In Cargo Net</b> is a durable silicone cargo net built into the lid of your
                    cooler for easy storing of smaller items.
                  </span>,
                  <span>
                    <b>Secure T-Latches</b> clamp down to secure your goods and a freezer-style gasket keeps
                    your cooler sealed tight.
                  </span>,
                ],
                [
                  <span>
                    <b>Rapid V-Drain System (Dual Plug)</b> allows for easy, hands-free draining.
                  </span>,
                  <span>
                    <b>Non-Skid Rubber Feet</b> help keep the cooler in place even on the smoothest surfaces.
                  </span>,
                  <span>
                    <b>Two Integrated Lock Holes</b> allow the cooler to be secured shut with personal
                    padlocks.
                  </span>,
                  <span>
                    <b>Compatible Accessories</b> get the most out of your cooler and stay organized with a
                    compatible RTIC freezable divider and dry goods basket. <b>Note:</b> Accessories are sold
                    separately.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.EVERYDAY_COOLER:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              Pack the good times with this fully-collapsible, fully-insulated Cooler.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Collapsible design</li>
              <li>Insulated for all-day cold</li>
              <li>Built-in bottle opener</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.EVERYDAY_INSULATED_TOTE:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              A fully-collapsible, fully-insulated Tote that’s perfect for beach days, picnics, and everything
              on the go.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>Waterproof base</li>
              <li className='tw-text-base tw-font-normal'>Collapsible, fold-flat design</li>
              <li className='tw-text-base tw-font-normal'>Fully-insulated interior</li>
            </ul>
          </>
        ),
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              title: {
                text: '',
              },
              images: {
                desktop: require('./images/everyday-insulated-tote/dimensions_d.svg'),
                mobile: require('./images/everyday-insulated-tote/dimensions_d.svg'),
                alt: 'Everyday Insulated Tote dimensions',
              },
            },
          ],
        },
      };

    case ProductTypeId.EVERYDAY_INSULATED_SLIM_TOTE:
      return {
        hideSectionsNav: true,
        showDescription: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              Pack in the good times with this fully-insulated, collapsible tote.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>Insulated Interior + Waterproof Base</li>
              <li className='tw-text-base tw-font-normal'>Collapsible, Fold-Flat Design</li>
              <li className='tw-text-base tw-font-normal'>Fits 4 Wine Bottles or 24 Cans</li>
            </ul>
          </>
        ),
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              title: {
                text: '',
              },
              images: {
                desktop: require('./images/everyday-insulated-slim-tote/capacity_d.png'),
                mobile: require('./images/everyday-insulated-slim-tote/capacity_m.png'),
                alt: 'Everyday Insulated Slim Tote dimensions',
              },
            },
          ],
        },
        featureSlides: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/everyday-insulated-slim-tote/happy-hour_d.jpg'),
                mobile: require('./images/everyday-insulated-slim-tote/happy-hour_m.jpg'),
                alt: 'Happy Hour with RTIC Insulated Slim Tote',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY INSULATED SLIM TOTE',
                title: `<span>Happy Hour Anywhere</span>`,
                description: `<p>This tote is designed to fit up to 4 wine bottles. Not only does the insulation lock in the cold for your chilled whites, it also acts as extra padding to protect your bottles.</p>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/everyday-insulated-slim-tote/use-pack-repeat_d.jpg'),
                mobile: require('./images/everyday-insulated-slim-tote/use-pack-repeat_m.jpg'),
                alt: 'Man cleaning RTIC Insulated Slim Tote after using it',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY INSULATED SLIM TOTE',
                title: `<span>Use it. Pack it. Repeat.</span>`,
                description: `<p>Forget the mess thanks to the fully-waterproof base that keeps your tote upright while protecting from wet, grassy, and sandy surfaces. Once the fun is done–just fold it flat for easy storing.</p>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/everyday-insulated-slim-tote/essentials-secured_d.jpg'),
                mobile: require('./images/everyday-insulated-slim-tote/essentials-secured_m.jpg'),
                alt: 'RTIC Insulated Slim Tote with keys clipped to the exterior pocket',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY INSULATED SLIM TOTE',
                title: `<span>Essentials Secured</span>`,
                description: `<p>The tote features two zippered pockets to keep your essentials handy and secured. Plus, the exterior pocket has a built-in clip to make sure your keys stay put.</p>`,
              },
            },
          ],
        },
      };

    case ProductTypeId.ULTRA_LIGHT_52_QT_WHEELED:
      return {
        lifestyleHeader: {
          title: 'Roll Into Adventure',
          image: {
            desktop: require('./images/52qt-ultralight-wheeled/hero-video-image_d.jpg'),
            mobile: require('./images/52qt-ultralight-wheeled/hero-video-image_m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/wheeled_52_hero_d.mp4`,
        },
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/why-we-love-it/ultra-light-meets-ultra-strong_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/why-we-love-it/ultra-light-meets-ultra-strong_m.jpg'),
                alt: 'Man towing 52QT Ultra-light Wheeled Cooler',
              },
              video: `${VIDEOS_DOMAIN}/Ultra-Light-meets-Ultra-Strong_d.mp4`,
              copy: {
                tagline: 'Ultra-Light Meets Ultra-Strong',
                title: `<span>Durable enough for tough terrain</span>`,
                description: `<span>With ultra-strong tires and a patented reinforced suspension, our all-terrain, puncture-resistant wheels will help you take this cooler anywhere with ease. It can handle mud, dry sand, stairs, rugged trails, and more.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/why-we-love-it/premium-cooling_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/why-we-love-it/premium-cooling_m.jpg'),
                alt: 'Opened 52QT Ultra-light Wheeled Cooler',
              },
              copy: {
                tagline: 'Premium Cooling Capacity',
                title: `<span>Premium Ice Retention</span>`,
                description: `<span>This cooler has been designed to provide multiple days of ice retention. Pro tip: To maximize your ice retention, prechill your cooler with ice or ice packs before filling it with your food, drinks and more ice.
                <a href='/cooler-care' target='_blank' style='display: block;'>See our Cooler Use & Care page for more information.</a>
                </span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/why-we-love-it/rtic-tough_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/why-we-love-it/rtic-tough_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled',
              },
              copy: {
                tagline: 'RTIC Tough',
                title: <span>Expertly designed with durability in mind</span>,
                description: (
                  <span>
                    With a welded, aluminum handle, stainless steel hardware, and structural reinforcements
                    throughout, we've created an exceptionally strong wheeled cooler that has been tested to
                    withstand over 1 ton of downward force-while still remaining up to 30% lighter than
                    wheeled rotomolded coolers of similar capacities.
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: 'Dimensions',
                icon: require('./images/icon-ruler.svg'),
              },
              images: {
                desktop: require('./images/52qt-ultralight-wheeled/dims_d.png'),
                mobile: require('./images/52qt-ultralight-wheeled/dims_m.png'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled front view and side view showing dimensions of the cooler',
              },
              description: (
                <>
                  <p className='tw-mb-2'>Dimensions: 29” &times; 18 &frac14;” &times; 18 &frac34;”</p>
                  <p className='tw-mb-2'>
                    Interior Dimensions: 20 &frac34;” &times; 12” &times; 12 &frac12;”
                  </p>
                  <p className='tw-mb-0'>Empty Weight: 30 lbs.</p>
                </>
              ),
            },
            {
              title: {
                text: 'Cans Only',
                icon: require('./images/icon-can.svg'),
              },
              images: {
                desktop: require('./images/52qt-ultralight-wheeled/capacity/cans_d.png'),
                mobile: require('./images/52qt-ultralight-wheeled/capacity/cans_m.png'),
                alt: 'RTIC Wheeled 52QT Ultra-Light Cooler filled with 78 cans',
              },
              description: <p>Holds Up To 78 Cans (No Ice)</p>,
            },
            {
              title: {
                text: 'Ice Only',
                icon: require('./images/icon-ice.svg'),
              },
              images: {
                desktop: require('./images/52qt-ultralight-wheeled/capacity/ice_d.png'),
                mobile: require('./images/52qt-ultralight-wheeled/capacity/ice_m.png'),
                alt: 'RTIC Wheeled 52QT Ultra-Light Cooler filled with 53lbs. of ice',
              },
              description: <p>Holds Up To 53 lbs of Ice</p>,
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/ultra-strong-wheels_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/ultra-strong-wheels_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Wheels',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Ultra-Strong Wheels</span>`,
                description: `<span>Custom-designed, puncture- and impact-resistant tires easily navigate through most any terrain.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/insulation_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/insulation_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled 2.5’’ of Insulation',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>2.5’’ of Insulation</span>`,
                description: `<span>With up to 2.5’’ of closed cell foam insulation, this cooler is designed to offer up to multiple days of ice retention.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/cast-aluminum-handle_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/cast-aluminum-handle_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Aluminum Handle',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Powder-Coated Aluminum Handle</span>`,
                description: `<span>The aluminum handle features a pair of non-slip silicone covers for easy towing. When not in use, it falls gently down into position without slamming.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/secure-t-latches_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/secure-t-latches_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Secure T-Latches',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Secure T-Latches</span>`,
                description: `<span>Silicone t-latches clamp down to secure your goods and the freezer-style gasket keeps your cooler sealed tight.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/fully-protected-wheel-axle_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/fully-protected-wheel-axle_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Fully Protected Wheel Axle',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Fully-Protected Wheel Axle</span>`,
                description: `<span>A unique patented chassis design, premium wheel bearings and rugged stainless steel axle deliver an ultra-smooth ride and long-lasting durability.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/built-in-bottle-opener_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/built-in-bottle-opener_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Bottle Opener',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Built-In Bottle Opener</span>`,
                description: `<span>A built-in bottle opener made of premium stainless steel makes it easy to open drinks anytime, anywhere.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight-wheeled/tech-and-features/silicone-cargo-net_d.jpg'),
                mobile: require('./images/52qt-ultralight-wheeled/tech-and-features/silicone-cargo-net_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Silicone Cargo Net',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Silicone Cargo Net</span>`,
                description: `<span>A convenient, durable silicone cargo net is built into the lid of your cooler for easy storing of smaller items.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Ultra-Strong Wheels</b> Custom-designed, puncture- and impact-resistant tires easily
                    navigate through most any terrain.
                  </span>,
                  <span>
                    <b>2.5’’ of Insulation</b> With up to 2.5’’ of closed cell foam insulation, this cooler is
                    designed to offer multiple days of ice retention.
                  </span>,
                  <span>
                    <b>Powder-Coated Aluminum Handle</b> The aluminum handle features a pair of non-slip
                    silicone covers for easy towing. When not in use, the handle falls gently down into
                    position without slamming.
                  </span>,
                  <span>
                    <b>Secure T-Latches</b> Silicone t-latches clamp down the lid and a freezer-style gasket
                    keeps your cooler sealed tight.
                  </span>,
                  <span>
                    <b>30% lighter than competing rolling coolers</b> This Wheeled Cooler is durably designed,
                    without the extra weight, making it up to 30% lighter than most competing wheeled coolers.
                  </span>,
                  <span>
                    <b>Silicone Cargo Net</b> A convenient, durable silicone cargo net is built into the lid
                    of your cooler for easy storing of smaller items.
                  </span>,
                ],
                [
                  <span>
                    <b>Fully-Protected Wheel Axle</b> A unique patented chassis design, premium wheel bearings
                    and rugged stainless steel axle deliver an ultra-smooth ride and long-lasting durability
                  </span>,
                  <span>
                    <b>Built-in Bottle Opener</b> A built-in bottle opener made of premium stainless steel
                    makes it easy to open drinks anytime, anywhere.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> The exterior of this cooler remains dry, so you can store it in
                    the back of your car without worrying about any mess.
                  </span>,
                  <span>
                    <b>Non-skid rubber feet</b> help keep the cooler in place, even on the smoothest and most
                    slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially
                    when using in high heat or in direct sun.
                  </span>,
                  <span>
                    <b>Tie Down Slots</b> Tie Down Points are available to easily attach your cooler to a
                    boat, trailer or truck bed.
                  </span>,
                ],
              ],
            },
          ],
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Take good times on the go with our all-terrain wheeled cooler.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li>78 can capacity</li>
              <li>All-terrain, puncture-proof wheels</li>
              <li>
                Locks in the cold for up to 5 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-ice-retention.svg'),
            title: 'Premium Ice Retention',
            description: 'Up to 2.5’’ of closed cell foam insulation keeps contents cold for multiple days.',
          },
          {
            icon: require('./images/ultra-light-construction.svg'),
            title: 'Ultra-Light Construction',
            description:
              'Engineered to be up to 30% lighter than wheeled rotomolded coolers of similar capacities.',
          },
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-ultra-strong-wheels.svg'),
            title: 'Ultra-Strong Wheels',
            description:
              'Puncture-resistant, all-terrain wheels help you tow through rugged terrains with ease.',
          },
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-easy-to-tow.svg'),
            title: 'Ergonomic Handle',
            description: 'A silicone-gripped, no-slam aluminum handle makes it easy to tow.',
          },
        ],
        collectionComponent: <MeetTheUltraLightCollection />,
      };

    case ProductTypeId.ULTRA_LIGHT_COOLER_TYPE:
      return {
        lifestyleHeader: {
          title: 'Travel Lighter',
          image: {
            desktop: require('./images/52qt-ultralight/banner_d.jpg'),
            mobile: require('./images/52qt-ultralight/banner_m.jpg'),
          },
        },
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: 'Premium Ice Retention',
            description: (
              <>
                Up to 3’’ of closed-cell foam insulation and a freezer-style gasket lock in the cold for up to
                7 days with <RticCoolingTipsLink />.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-ultra-light-construction.svg'),
            title: 'Ultra-Light Construction',
            description: 'Engineered to be up to 30% lighter than rotomolded coolers of similar capacities.',
          },
          {
            icon: require('./images/icons/icon-large-interior.svg'),
            title: 'Large Interior Space',
            description:
              'Designed with a large interior space making it easy to keep food and drinks organized.',
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/travel-lighter_d.jpg'),
                mobile: require('./images/52qt-ultralight/travel-lighter_m.jpg'),
                alt: 'Man carrying RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Travel Lighter &<br />Longer.</span>`,
                description: `<span>Engineered to deliver ice retention performance, without the weight. Infused with that legendary RTIC toughness, the 52QT Ultra-Light makes it easier to take your premium hard-sided cooler as far as your spirit of adventure takes you.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/lose-the-weight_d.jpg'),
                mobile: require('./images/52qt-ultralight/lose-the-weight_m.jpg'),
                alt: 'Man carrying Blue RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Lose The Weight, <br />Not The Durability.</span>`,
                description: `<span>Utilizing the latest cutting-edge technology and innovative injected molded design, the RTIC 52 Ultra-Light Hard Cooler is more than 30% lighter than roto-molded coolers of the same capacity.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/long-live-the-ice_d.jpg'),
                mobile: require('./images/52qt-ultralight/long-live-the-ice_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler from inside (opened)',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Long Live The Ice.</span>`,
                description: `<span>With up to 3 inches closed-cell foam insulation, this cooler is all about extremes - from portability and durability to long lasting ice retention.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/versatility-included_d.jpg'),
                mobile: require('./images/52qt-ultralight/versatility-included_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler from inside (opened)',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Versatility<br />Included.</span>`,
                description: (
                  <span>
                    The 52QT Ultra-Light Cooler can multi-task as a bench, non-slip step stool, or tabletop
                    while keeping the ice, perishables and vital supplies cold.
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: 'Dimensions',
                icon: require('./images/icon-ruler.svg'),
              },
              images: {
                desktop: require('./images/52qt-ultralight/dims_d.png'),
                mobile: require('./images/52qt-ultralight/dims_m.png'),
                alt: 'RTIC 52QT Ultra-Light Cooler front view and side view showing dimensions of the cooler',
              },
              description: (
                <>
                  <p>Exterior Dimensions: 27 &frac14;” &times; 17 &frac14;” &times; 17”</p>
                  <p>Interior Dimensions: 21 &frac14;” &times; 12” &times; 12”</p>
                  <p>Empty Weight: 21 lbs.</p>
                </>
              ),
            },
            {
              title: {
                text: 'Cans Only',
                icon: require('./images/icon-can.svg'),
              },
              images: {
                desktop: require('./images/52qt-ultralight/cans_d.png'),
                mobile: require('./images/52qt-ultralight/cans_m.png'),
                alt: 'RTIC 52QT Ultra-Light Cooler filled with 70 cans',
              },
              description: (
                <>
                  <p>Holds Up To 76 Cans</p>
                </>
              ),
            },
            {
              title: {
                text: 'Ice Only',
                icon: require('./images/icon-ice.svg'),
              },
              images: {
                desktop: require('./images/52qt-ultralight/ice_d.png'),
                mobile: require('./images/52qt-ultralight/ice_m.png'),
                alt: 'RTIC 52QT Ultra-Light Cooler filled with 60lbs. of ice',
              },
              description: (
                <>
                  <p>Holds Up To 51 lbs. Of Ice</p>
                </>
              ),
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/foam-insulation_d.jpg'),
                mobile: require('./images/52qt-ultralight/foam-insulation_m.jpg'),
                alt: 'Close up of the gasket on the lid of the RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Foam Insulation & O-Ring Gasket Hold In The Cold</span>`,
                description: `<span>Up to 3 inches of foam insulation and freezer style gasket seals in the cold for long-lasting ice retention.<br><br><strong>Basket &amp; Divider Sold Separately</strong></span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/nylon-rope_d.jpg'),
                mobile: require('./images/52qt-ultralight/nylon-rope_m.jpg'),
                alt: 'Close up of the rope handles of the RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Marine-Grade Nylon Rope Handles</span>`,
                description: `<span>Comfortable hand grips make it even easier to maneuver.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/rubber-feet_d.jpg'),
                mobile: require('./images/52qt-ultralight/rubber-feet_m.jpg'),
                alt: 'Close up of the rubber feet on the RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Non-Skid Rubber Feet</span>`,
                description: (
                  <span>
                    Non-skid rubber feet help keep the cooler in place, even on the smoothest and most
                    slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially
                    when using in high heat or in direct sun.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/durable-t-latches_d.jpg'),
                mobile: require('./images/52qt-ultralight/durable-t-latches_m.jpg'),
                alt: 'Close up of the rubber T-Latches on the RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Durable T-Latches Clamp Down Lid Securely</span>`,
                description: `<span>Durable T-latches hold in the cold to protect your provisions all weekend long.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/52qt-ultralight/tie-down-slots_d.jpg'),
                mobile: require('./images/52qt-ultralight/tie-down-slots_m.jpg'),
                alt: 'Close up of the tie down slots on the RTIC 52QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Tie Down Slots To Attach To Boat, Truck Bed, & More</span>`,
                description: `<span>Tie down slots so you can secure the most portable version of our best-selling cooler on your adventures.<br><br><strong>Basket &amp; Divider Sold Separately</strong></span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  'Convenient, easy to handle size.',
                  'Up to 3 inches of closed cell foam insulation with an O-Ring freezer style gasket that seals in cold for longer ice retention.',
                  'Lightweight and easy to carry due to its injection molded construction.',
                  '2 drainage holes and Rapid V-Drain System for quick and easy draining in any position.',
                  'Durable t-latches clamp down lid securely.',
                  'Non-skid rubber feet help keep the cooler in place, even on the smoothest and most slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially when using in high heat or in direct sun.',
                ],
                [
                  'Marine-grade Nylon Rope Handles with comfortable hand grips.',
                  'Molded-In Side Handles.',
                  'Tie down slots to attach to boat, trailer or truck bed.',
                  'Traction lid - can be used as a casting platform.',
                  'Integrated Locking System allows cooler to be secured.',
                  'Custom Shop ready - a bold showcase of your brand!',
                ],
              ],
            },
          ],
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Ditch the weight, keep the strength with this strong and lightweight cooler.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li>76 can capacity</li>
              <li>30% lighter than rotomolded coolers</li>
              <li>
                Locks in the cold for up to 7 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        collectionComponent: (
          <MeetTheUltraLightCollection
            smallPrint={
              <>
                *When using RTIC's{' '}
                <a className='!tw-underline' target='_blank' href='/cooler-care'>
                  Cooling & Care Tips
                </a>
              </>
            }
          />
        ),
      };

    case ProductTypeId.ROAD_TRIP_ROLLING_DUFFLE:
      return {
        lifestyleHeader: {
          title: 'Pack It. Roll It. Go.',
          image: {
            desktop: require('./images/road-trip-rolling-duffle/video-placeholder.jpg'),
            mobile: require('./images/road-trip-rolling-duffle/video-placeholder-mob.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/road-trip-rolling-duffle_hero.mp4`,
        },
      };

    case ProductTypeId.ULTRA_TOUGH_CHAIR:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              A chair as tough as our coolers, built for the outdoors.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li>Holds up to 300 lbs</li>
              <li>Quad-collapsible design</li>
              <li>Durable weather-resistant canvas</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.ULTRA_TOUGH_TOTE:
      return {
        subtitleComponent: ({ details }) => {
          const sizeId = details?.attributes?.sizeId;
          const capacityValue = sizeId === 10 ? '40L' : sizeId === 8 ? '25L' : null;

          return (
            <ul className='tw-mb-0 tw-mt-6 tw-list-outside tw-list-disc tw-space-y-1 tw-pl-5 tw-text-xs tw-font-normal tw-text-body'>
              {!!capacityValue && (
                <li>
                  <span>{capacityValue} Capacity</span>
                </li>
              )}
              <li>
                <span>Waterproof Design</span>
              </li>
              <li>
                <span className='tw-flex tw-items-center tw-space-x-2'>
                  <span>Personalize with</span>
                  <img className='tw-w-full tw-max-w-[56px]' src='/assets/images/rtic/myRTIC-Logo.png' />
                </span>
              </li>
            </ul>
          );
        },
      };

    case ProductTypeId.ULTRA_TOUGH_WAGON:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>Keep your gear in tow.</p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Built to carry up to 250 lbs.</li>
              <li>Compatible with coolers up to 65QT</li>
              <li>Ultra-Tough Rubber Wheels</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.PULLOVER_HOODIE_RELAXED:
      return {
        hideSectionsNav: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Your new favorite hoodie: versatile, water repellent, and comfortable.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Relaxed Fit</li>
              <li>Water-Resistant Material</li>
              <li>Soft Cotton Polyester Blend</li>
            </ul>
          </>
        ),
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/pullover-hoodie-relaxed/function-first_d.jpg'),
                mobile: require('./images/pullover-hoodie-relaxed/function-first_m.jpg'),
                alt: 'RTIC Pullover Hoodie Relaxed Fit Function First',
              },
              copy: {
                tagline: "Meet the Men's Water-Repellent Pullover Hoodie",
                title: `<span>Function First</span>`,
                description: `<span>The ergonomic seam design means nothing gets in the way of your movement, so you can play, work, and explore better.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/pullover-hoodie-relaxed/dry-comfort_d.jpg'),
                mobile: require('./images/pullover-hoodie-relaxed/dry-comfort_m.jpg'),
                alt: 'RTIC Pullover Hoodie Relaxed Fit Dry Comfort',
              },
              copy: {
                tagline: "Meet the Men's Water-Repellent Pullover Hoodie",
                title: `<span>Dry Comfort</span>`,
                description: `<span>The outer shell features water-resistant properties, to keep you dry on foggy mornings and chilly wet evenings.</span>`,
              },
            },
          ],
        },
      };

    case ProductTypeId.RTIC_SHORT_SLEEVE_TSHIRT:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Soft and comfortable, it’s your new go-to t-shirt.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Short-Sleeved Crew Neck</li>
              <li>Soft Fabric Blend</li>
              <li>True to Size Fit</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.HALFTIME_WATER_COOLER:
      return {
        /* tagline: 'Includes Divider And Basket', */
        lifestyleHeader: {
          title: 'Your New Hydration Station',
          image: {
            desktop: require('./images/halftime-water-cooler/hero-fallback-image_d.jpg'),
            mobile: require('./images/halftime-water-cooler/hero-fallback-image_m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/half-time-cooler_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Designed like a cooler. Built for shared hydration.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Keeps cold for up to 24 hours</li>
              <li>Dual fast-fill taps with 3 settings</li>
              <li>Lightweight, durable design</li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/why-we-love-it/hydration-station_d.jpg'),
                mobile: require('./images/halftime-water-cooler/why-we-love-it/Hydration-Station_m.jpg'),
                alt: 'RTIC Halftime Water Cooler',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Hydration Station</span>`,
                description: `<span>The Halftime Water Cooler has dual, fast-fill taps so you can fill up twice the drinks in half the time, whether it’s at a sports game, local restaurant, job site, or party! Fill it with ice and the refreshment of your choice to keep drinks cold for over 24 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: 'https://static.velkybrands.com/v/half-time-cooler-spout_d.jpg',
                mobile: 'https://static.velkybrands.com/v/half-time-cooler-spout_m.jpg',
                alt: 'RTIC Halftime Water Cooler 3-in-1 Taps',
              },
              video: `${VIDEOS_DOMAIN}/half-time-cooler-spout_d.mp4`,
              copy: {
                tagline: 'Why We Love It',
                title: `<span>3-in-1 Spout Lock</span>`,
                description: `<span>The uniquely designed spout lock provides three settings: Locked, Unlocked, and Autofill. With each setting, you can easily control how much flow you get, fill cups hands free, and transport your water cooler without spilling.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/why-we-love-it/Never go thirsty_d.jpg'),
                mobile: require('./images/halftime-water-cooler/why-we-love-it/Never-Go-Thirsty_m.jpg'),
                alt: 'RTIC Halftime Water Cooler Liquid-Level Viewport',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Never Go Thirsty</span>`,
                description: `<span>RTIC’s unique Liquid-Level Viewport makes it easy to keep tabs on your favorite iced beverage without having to open the cooler, so you never have to worry about an empty push on the tap.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '3 Gallon',
              sections: [
                {
                  title: {
                    text: '3 Gallon',
                  },
                  images: {
                    desktop: require('./images/halftime-water-cooler/capacity/3-gallon-dims_d.png'),
                    mobile: require('./images/halftime-water-cooler/capacity/3 gallon dims_m.png'),
                    alt: 'RTIC Halftime Water Cooler front view and side view showing dimensions of the cooler',
                  },
                  description: (
                    <>
                      <h4>3 Gallon</h4>
                      <p className='tw-mb-2'>Exterior Dims: 11 &frac34;” H | 11 &frac34;” W | 13” D</p>
                      <p className='tw-mb-0'>Empty Weight: 6.5 lbs.</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '6 Gallon',
              sections: [
                {
                  title: {
                    text: '6 Gallon',
                  },
                  images: {
                    desktop: require('./images/halftime-water-cooler/capacity/6-gallon-dims_d.png'),
                    mobile: require('./images/halftime-water-cooler/capacity/6 gallon dims_m.png'),
                    alt: 'RTIC Halftime Water Cooler front view and side view showing dimensions of the cooler',
                  },
                  description: (
                    <>
                      <h4>6 Gallon</h4>
                      <p className='tw-mb-2'>Exterior Dims: 20 &frac12;” H | 11 &frac34;” W | 13” D</p>
                      <p className='tw-mb-0'>Empty Weight: 9.5 lbs.</p>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/tech-and-features/Dual-Fast-Fill-Taps_d.jpg'),
                mobile: require('./images/halftime-water-cooler/tech-and-features/Dual-Fast-Fill-Taps_m.jpg'),
                alt: 'RTIC Halftime Water Cooler Dual Fast-Fill Taps',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Dual, Fast-Fill Taps</span>`,
                description: `<span>Two uniquely designed fast-fill taps make it easy to fill twice the amount of drinks at once.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/tech-and-features/3-in-1-Spout-Lock_d.jpg'),
                mobile: require('./images/halftime-water-cooler/tech-and-features/3-in-1-Spout-Lock_m.jpg'),
                alt: 'RTIC Halftime Water Cooler 3-in-1 Spout Lock',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>3-in-1 Spout Lock</span>`,
                description: `<span>RTIC’s patented design features a convenient 3-in-1 locking mechanism.<br/><br/><b>1. Unlocked:</b> Flip the wire lock fully upward and out of the way.<br/><br/><b>2. Autofill:</b> Press the tap to activate flow, and flip the wire lock down to keep the spout open.<br/><br/><b>3. Locked:</b> To prevent any spillage, flip wire lock down, lightly placing it over the top edge of the spout.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/tech-and-features/Liquid-Level-Viewport_d.jpg'),
                mobile: require('./images/halftime-water-cooler/tech-and-features/Liquid-Level-Viewport-_m.jpg'),
                alt: 'RTIC Halftime Water Cooler Liquid-Level Viewport',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Liquid-Level Viewport</span>`,
                description: `<span>This clear viewport makes it easy to keep an eye on the level of your beverage–and know when you need a refill before it runs out!</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/tech-and-features/Leak-Resistant-Mechanisms_d.jpg'),
                mobile: require('./images/halftime-water-cooler/tech-and-features/Leak-Resistant-Mechanisms_m.jpg'),
                alt: 'RTIC Halftime Water Cooler Leak-Resistant Mechanisms',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Leak-Resistant Mechanisms</span>`,
                description: `<span>The Lid Gasket, locking Lid Vent, and locking Spouts all prevent leaks when transporting your Water Cooler..</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/halftime-water-cooler/tech-and-features/Comfortable-Carry-Straps_d.jpg'),
                mobile: require('./images/halftime-water-cooler/tech-and-features/Comfortable-Carry-Straps_m.jpg'),
                alt: 'RTIC Halftime Water Cooler Comfortable Carry Straps',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Comfortable Carry Straps</span>`,
                description: `<span>Both sizes feature a comfortable carry strap. The 6 gallon size includes two separate straps so two people can easily share the load when carrying a full Water Cooler.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Dual, Fast-Fill Taps</b> Two uniquely designed fast-fill taps make it easy to fill
                    twice the amount of drinks at once.
                  </span>,
                  <span>
                    <b>3-in-1 Spout</b> The uniquely designed spout provides three settings: Locked, Unlocked,
                    and Autofill. With each setting, you can easily control how much flow you get, fill cups
                    hands free, and transport your water cooler without spilling.
                  </span>,
                  <span>
                    <b>Liquid Level Viewport</b> This clear viewport makes it easy to keep an eye on the level
                    of your beverage–and know when you need a refill before it runs out!
                  </span>,
                  <span>
                    <b>Premium Cold Retention for 24+ Hours</b> Closed Cell Foam Insulation offers premium
                    coolness retention, the Halftime Water Cooler keeps your contents icy cold for over 24
                    hours
                  </span>,
                  <span>
                    <b>Comfortable Carry Straps</b> The 6 gallon has two comfortable carry straps so two
                    people can easily share the load when carrying a full cooler
                  </span>,
                  <span>
                    <b>Leak-Resistant Mechanisms</b> The Lid Gasket, locking Lid Vent, and locking Spouts all
                    prevent leaks when transporting your Water Cooler.
                  </span>,
                ],
                [
                  <span>
                    <b>30% Lighter than Rotomolded Competitors</b> Built similarly to our Ultra-Light
                    collection to offer a durable, yet lightweight Water Cooler that’s 30% lighter than
                    rotomolded competitors.
                  </span>,
                  <span>
                    <b>Easy to Clean</b> BPA-Free, Antimicrobial properties makes it easy to keep your Water
                    Cooler clean–no matter what beverage you choose to put in it.
                  </span>,
                  <span>
                    <b>Stainless Steel Hardware</b> Quality, Stainless Steel Hardware means the Water Cooler
                    remains rust free, even when exposed to moisture over and over again.
                  </span>,
                  <span>
                    <b>Stackable Design</b> The unique square shape means you can easily stack as many Water
                    Coolers as you want on top of each other in multi-drink situations.
                  </span>,
                ],
              ],
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-ice-retention.svg'),
            title: 'Premium Ice Retention',
            description: 'Closed cell foam insulation helps keep drinks icy cold for 24+ hours.',
          },
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-lighter-weight.svg'),
            title: 'Ultra-Light Construction',
            description: 'Engineered with our Ultra-Light technology for a lightweight, durable design.',
          },
          {
            icon: require('./images/halftime-water-cooler/3-in-1 taps icon.svg'),
            title: '3-in-1 Taps',
            description: 'Two fast-fill taps offer 3 settings: Locked, Unlocked, and Autofill.',
          },
        ],
      };

    case ProductTypeId.ROAD_TRIP_TUMBLER:
      return {
        /* tagline: 'Includes Divider And Basket', */
        lifestyleHeader: {
          title: 'Sip it. Lock it. Go.',
          image: {
            desktop: require('./images/road-trip-tumbler/Hero-fallback-image_d.jpg'),
            mobile: require('./images/road-trip-tumbler/Hero-fallback-image_m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/road-trip-tumbler-hero_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Premium drinkware with a leak-resistant lid and ceramic lining for better tasting drinks.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>3-in-1 lid (two straws included)</li>
              <li>Keeps cold for up to 24 hours</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-tumbler/why-we-love-it/A-Better-Taste-Experience_d.jpg'),
                mobile: require('./images/road-trip-tumbler/why-we-love-it/A-Better-Taste-Experience_m.jpg'),
                alt: 'RTIC Road Trip Tumbler',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Road Trip Tumbler is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. So go ahead and fill it with your favorite green juice or iced coffee and enjoy great flavor with every sip.<br/><br/>Bonus: the ceramic lining makes it easy to clean and doesn’t absorb any flavors.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-tumbler/why-we-love-it/Drink-it-lock-it-go_fallback-image_d.jpg'),
                mobile: require('./images/road-trip-tumbler/why-we-love-it/Drink-it-lock-it-go_fallback-image_m.jpg'),
                alt: 'RTIC Road Trip Tumbler',
              },
              video: `${VIDEOS_DOMAIN}/road-trip-tumbler-features_d.mp4`,
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Drink It Your Way.</span>`,
                description: `<span>Our uniquely designed multi-function lid allows you to drink with two settings, open mouth or with a straw (2 straws included). Once you’re finished, just close the locking tab or remove the straw for leak-resistant transportation.<br/><br/>Note: The Lid and Straw are not recommended for use with hot beverages.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-tumbler/why-we-love-it/Hydration-within-reach_fallback-image_d.jpg'),
                mobile: require('./images/road-trip-tumbler/why-we-love-it/Hydration-within-reach_fallback-image_m.jpg'),
                alt: 'RTIC Road Trip Tumbler',
              },
              video: `${VIDEOS_DOMAIN}/road-trip-tumbler-car_d.mp4`,
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Hydration Within Reach</span>`,
                description: `<span>The Road Trip Tumbler features a comfortable handle, dual threading for left and right-handed use, and is intentionally designed to fit in most cup holders. That means you can take your tumbler anywhere with you, and always have quick and easy hydration within reach.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '30oz',
              sections: [
                {
                  title: {
                    text: '30oz',
                  },
                  images: {
                    desktop: require('./images/road-trip-tumbler/capacity/30oz.jpg'),
                    mobile: require('./images/road-trip-tumbler/capacity/30oz.jpg'),
                    alt: 'RTIC Road Trip Tumbler dimensions',
                  },
                  description: (
                    <>
                      <h4>30oz Road Trip Tumbler</h4>
                    </>
                  ),
                },
              ],
            },
            {
              value: '40oz',
              sections: [
                {
                  title: {
                    text: '40oz',
                  },
                  images: {
                    desktop: require('./images/road-trip-tumbler/capacity/40oz.jpg'),
                    mobile: require('./images/road-trip-tumbler/capacity/40oz.jpg'),
                    alt: 'RTIC Road Trip Tumbler dimensions',
                  },
                  description: (
                    <>
                      <h4>40oz Road Trip Tumbler</h4>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>3-in-1 Lid</b> can be used in 3 different ways: Open, with a straw (2 straws included),
                    or sealed with the leak-resistant closure*.
                  </span>,
                  <span>
                    <b>Ceramic Lining</b> prevents altered or metallic taste and smell, so drinks taste better
                    and it’s easy to clean.
                    <br />
                    Note: Do not use metal or hard plastic straws or utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Keeps Cold for up to 24 Hours</b> thanks to vacuum-sealed insulation and an airtight
                    seal.
                  </span>,
                  <span>
                    <b>Cup Holder-Friendly Design</b> fits in more cup holders, so you can take hydration on
                    the go.
                  </span>,
                  <span>
                    <b>Big, Comfortable Handle</b> makes it easy to carry and hold, whether it’s in the car,
                    walking, or working out.
                  </span>,
                ],
                [
                  <span>
                    <b>No Sweat Exterior</b> remains dry and free of condensation, regardless of what’s
                    inside.
                  </span>,
                  <span>
                    <b>18/8 Recycled Stainless Steel Construction</b> offers durability and quality RTIC
                    Toughness.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> makes it easy to clean, remember to place the lid and straw on top
                    rack only.
                  </span>,
                  <span>
                    <b>Dual Threading Lid</b> makes the tumbler adaptable for left or right-handed use.
                    <br />
                    <p className='tw-mt-3 tw-text-[13px] tw-italic tw-leading-[18px]'>
                      * The Lid and Straw are not recommended for use with hot beverages.
                    </p>
                  </span>,
                ],
              ],
              bottomNote: (
                <i>
                  Some packaging may state this Road Trip Tumbler has a silicone base, however some colors of
                  this product has our classic, stainless steel base.
                </i>
              ),
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/road-trip-tumbler/Leak-Resistant Closure_m.svg'),
            title: 'Leak-Resistant Closure',
            description:
              'RTIC’s unique, double-tab locking system makes the Tumbler leak and spill resistant.*',
          },
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-ice-retention.svg'),
            title: 'Keeps Cold for up to 24 hours',
            description:
              'Double-wall vacuum-sealed insulation, UV heat protection, and an airtight lid seal help keep the cold in.',
          },
          {
            icon: require('./images/road-trip-tumbler/Ceramic Lined_m.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages.',
          },
          {
            icon: require('./images/road-trip-tumbler/3-in-1 Lid_m.svg'),
            title: '3-in-1 Lid',
            description:
              'Multi-function lid features a straw (2 straws included), open-mouth setting, and leak-resistant closure.',
          },
        ],
        iconFeaturesSmallprint: '*The Lid and Straw are not recommended for use with hot beverages.',
      };

    case ProductTypeId.ROAD_TRIP_TRAVEL_MUG:
      return {
        lifestyleHeader: {
          title: (
            <>
              The Ultimate
              <br />
              To-Go Mug
            </>
          ),
          containerClassName: 'tw-justify-start',
          titleContainerClassName:
            'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3 tw-items-end',
          titleClassName: 'tw-text-left',
          image: {
            desktop: require('./images/road-trip-travel-mug/Hero-D-Fallback.jpg'),
            mobile: require('./images/road-trip-travel-mug/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/road-trip-travel-mug-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/road-trip-travel-mug-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Take your favorite hot drinks on the road with a leak-resistant lid and ceramic lining for great
              taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps hot for up to 6 hours / cold for up to 24 hours</li>
              <li>Leak-resistant lid closure</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Leak-Resistant Lid',
            description:
              'Built for hot drinks on the go with a double-threaded, leak-resistant lid that flips up for sips and snaps down to lock.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double-wall, vacuum-sealed insulation and an airtight lid seal keeps hot for up to 6 hours or cold for up to 24 hours*',
          },
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic lining prevents altered or metallic taste and odor, for better tasting coffee and tea. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/icons/icon-silicon-base.svg'),
            title: 'Slim Fit, Soft Base',
            description: 'Cup holder-friendly design and a built-in silicone coaster to avoid clanking.',
          },
        ],
        iconFeaturesSmallprint: '*Use caution when consuming hot liquids.',
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-travel-mug/meet-the-collection/Better-Taste-Experience-D.jpg'),
                mobile: require('./images/road-trip-travel-mug/meet-the-collection/Better-Taste-Experience-M.jpg'),
                alt: 'RTIC Road Trip Travel Mug',
              },
              copy: {
                tagline: 'MEET THE ROAD TRIP TRAVEL MUG',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Road Trip Travel Mug is designed for great taste on the go with a ceramic-lined interior that protects coffee and tea from metallic taste and smell. The ceramic lining is also easy to clean, and doesn’t absorb any flavors.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-travel-mug/meet-the-collection/Hot-Drinks-D.jpg'),
                mobile: require('./images/road-trip-travel-mug/meet-the-collection/Hot-Drinks-M.jpg'),
                alt: 'RTIC Road Trip Travel Mug',
              },
              copy: {
                tagline: 'MEET THE ROAD TRIP TRAVEL MUG',
                title: `<span>Hot Drinks On the Go</span>`,
                description: `<span>This Mug is your new go-to for morning brews, refills at the office, and taking coffee on the road. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, it keeps hot for up to 6 hours. It also keeps cold for up to 24 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-travel-mug/meet-the-collection/Leak-Resistant-Lid-D.jpg'),
                mobile: require('./images/road-trip-travel-mug/meet-the-collection/Leak-Resistant-Lid-M.jpg'),
                alt: 'RTIC Road Trip Travel Mug',
              },
              copy: {
                tagline: 'MEET THE ROAD TRIP TRAVEL MUG',
                title: `<span>Leak-Resistant Lid</span>`,
                description: `<span>Enjoy your drinks without fear of spills or leaks thanks to the Travel Mug leak-resistant, fully-insulated lid. Featuring double threading so it stays on tight, this lid’s closure flips open for easy sipping and flips down for when you’re on the go.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '16oz',
              sections: [
                {
                  title: {
                    text: '16oz',
                  },
                  images: {
                    desktop: require('./images/road-trip-travel-mug/Dims_16oz.jpg'),
                    mobile: require('./images/road-trip-travel-mug/Dims_16oz.jpg'),
                    alt: 'RTIC Road Trip Travel Mug 16oz dimensions',
                  },
                  description: (
                    <>
                      <h4>16oz Road Trip Travel Mug</h4>
                    </>
                  ),
                },
              ],
            },
            {
              value: '20oz',
              sections: [
                {
                  title: {
                    text: '20oz',
                  },
                  images: {
                    desktop: require('./images/road-trip-travel-mug/Dims_20oz.jpg'),
                    mobile: require('./images/road-trip-travel-mug/Dims_20oz.jpg'),
                    alt: 'RTIC Road Trip Travel Mug 20oz dimensions',
                  },
                  description: (
                    <>
                      <h4>20oz Road Trip Travel Mug</h4>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leak-Resistant Lid</b> features double threading and a leak-resistant tab to keep your
                    drink from spilling and your lid on tight.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Do not use metal or hard plastic straws or utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Up to 6 Hours Hot / 24 Hours Cold</b> locks in the temperature with double-wall,
                    vacuum-sealed insulation, UV heat protection, and an airtight seal. Note: Please use
                    caution when opening the lid with hot liquids.
                  </span>,
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                ],
                [
                  <span>
                    <b>Built-In Silicone Base</b> avoids clanking for a soft landing on any surface.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Cup Holder-Friendly Design</b> fits in more cup holders, so you can take your favorite
                    hot drinks on the go.
                  </span>,
                  <span>
                    <b>Dual Threading</b> makes the tumbler adaptable for left or right-handed use.
                  </span>,
                  <span>
                    <b>Comfortable Handle</b> makes it easy to carry and hold, whether it’s in the car,
                    walking, or working out.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> Design so it’s easy to clean, remember to place the lid on the top
                    rack only.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.ROAD_TRIP_TUMBLER_64OZ:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName:
            'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3 tw-items-end',
          titleClassName: 'tw-text-left',
          title: (
            <>
              All-Day
              <br />
              Great Taste
            </>
          ),
          image: {
            desktop: require('./images/road-trip-tumbler-64oz/Hero-D-Fallback.jpg'),
            mobile: require('./images/road-trip-tumbler-64oz/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/64oz-road-trip-tumbler-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/64oz-road-trip-tumbler-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Insulated for all-day cold. Big enough for all-day hydration.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>3-in-1 lid (two straws included)</li>
              <li>Keeps cold for 24 hours</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Leak-Resistant Lid',
            description:
              'RTIC’s unique, double-tab locking system makes the Tumbler leak and spill resistant.*',
          },
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: 'Keeps Cold for Up to 24 Hours',
            description:
              'Double-wall vacuum-sealed insulation and an airtight lid seal help keep the cold in.',
          },
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic lining prevents altered or metallic taste and smell for better tasting beverages. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/road-trip-tumbler-64oz/icon-road-trip-lid.svg'),
            title: '3-in-1 Lid',
            description:
              'Multi-function lid features a straw, open-mouth opening, and leak-resistant closure.',
          },
        ],
        iconFeaturesSmallprint: '*The Lid and Straw are not recommended for use with hot beverages.',
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-tumbler-64oz/meet-the-collection/Better-Taste-Experience-D.jpg'),
                mobile: require('./images/road-trip-tumbler-64oz/meet-the-collection/Better-Taste-Experience-M.jpg'),
                alt: 'RTIC Road Trip Tumbler 64oz',
              },
              copy: {
                tagline: 'MEET THE 64OZ ROAD TRIP TUMBLER',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The 64oz Road Trip Tumbler is designed for great taste on the go with a ceramic-lined interior that protects beverages from metallic taste and smell–even when you carry your water all day long. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so fill it with your favorite beverage or add in your electrolytes and enjoy great taste with every sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-tumbler-64oz/meet-the-collection/Ice-Cold-D.jpg'),
                mobile: require('./images/road-trip-tumbler-64oz/meet-the-collection/Ice-Cold-M.jpg'),
                alt: 'RTIC Road Trip Tumbler 64oz',
              },
              copy: {
                tagline: 'MEET THE 64OZ ROAD TRIP TUMBLER',
                title: `<span>Ice Cold, All Day Long</span>`,
                description: `<span>This Tumbler is designed for all-day hydration from morning commutes, to afternoon workouts, to long days on the road. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, this Tumbler locks in the cold for up to 24 hours and keeps hot for up to 6 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-tumbler-64oz/meet-the-collection/Drink-it-Your-Way-fallback-D.jpg'),
                mobile: require('./images/road-trip-tumbler-64oz/meet-the-collection/Drink-it-Your-Way-fallback-M.jpg'),
                alt: 'RTIC Road Trip Tumbler 64oz',
              },
              videoClassName: 'md:tw-h-[450px] md:tw-object-cover md:tw-object-center',
              video: `${VIDEOS_DOMAIN}/64oz-road-trip-tumbler-drink-it-your-way_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/64oz-road-trip-tumbler-drink-it-your-way_m.mp4`,
              copy: {
                tagline: 'MEET THE 64OZ ROAD TRIP TUMBLER',
                title: `<span>Drink it Your Way</span>`,
                description: `<span>Designed to go everywhere you go, the 64oz Road Trip Tumbler features our uniquely designed, multi-function lid that allows you to drink with two settings, open mouth or with a straw (2 straws included). Once you’re finished, just close the locking tab or remove the straw for leak-resistant transportation.
                <br /><br />
                Note: The lid and straw are not recommended for use with hot beverages.</span>`,
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>64oz Road Trip Tumbler</p>
                </>
              ),
              images: {
                desktop: require('./images/road-trip-tumbler-64oz/Dims.jpg'),
                mobile: require('./images/road-trip-tumbler-64oz/Dims.jpg'),
                alt: 'RTIC Road Trip Tumbler 64oz Dimensions',
              },
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>3-in-1 Lid</b> can be used in 3 different ways: Open, with a straw (2 straws included),
                    or sealed with the leak-resistant closure*.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean. Note: Designed for use with the included straws only.
                    Avoid using metal or hard plastic straws, or utensils, as they may scrape the ceramic
                    lining.
                  </span>,
                  <span>
                    <b>Keeps Cold for Up to 24 Hours*</b> with double-wall, vacuum-sealed insulation, UV heat
                    protection, and an airtight seal.
                  </span>,
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                ],
                [
                  <span>
                    <b>Built-In Silicone Base</b> avoids clanking for a soft landing on any surface.
                  </span>,
                  <span>
                    <b>Big, Comfortable Handle</b> makes it easy to carry and hold, whether you’re walking or
                    working out.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dual Threading</b> makes the tumbler adaptable for left or right-handed use.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> design so it’s easy to clean, remember to place the lid on the top
                    rack only.
                    <small className='tw-mt-3 tw-block tw-text-xs'>
                      *The Lid and Straw are not recommended for use with hot beverages.
                    </small>
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.ESSENTIAL_COFFEE_MUG:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Take coffee & tea on the go with a splash-resistant lid and ceramic lining for great taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps hot for up to 6 hours / cold for up to 24 hours</li>
              <li>Splash-resistant lid</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-coffee-mug/meet-the-collection/Better-Taste-Experience-D.jpg'),
                mobile: require('./images/essential-coffee-mug/meet-the-collection/Better-Taste-Experience-M.jpg'),
                alt: 'RTIC Essential Coffee Mug',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL COFFEE MUG',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Essential Coffee Mug is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so go ahead and fill it with coffee or tea and enjoy every last sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-coffee-mug/meet-the-collection/Hot-Drinks-D.jpg'),
                mobile: require('./images/essential-coffee-mug/meet-the-collection/Hot-Drinks-M.jpg'),
                alt: 'RTIC Essential Coffee Mug',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL COFFEE MUG',
                title: `<span>Hot Drinks On the Go</span>`,
                description: `<span>This Mug is your new go-to for morning brews, refills at the office, and sipping campside. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, it keeps hot for up to 6 hours and cold for up to 24 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-coffee-mug/meet-the-collection/Splash-Resistant-Lid-D.jpg'),
                mobile: require('./images/essential-coffee-mug/meet-the-collection/Splash-Resistant-Lid-M.jpg'),
                alt: 'RTIC Essential Coffee Mug',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL COFFEE MUG',
                title: `<span>Splash-Resistant Lid</span>`,
                description: `<span>Take your favorite drinks with you thanks to the Mug’s splash-resistant, fully-insulated lid. This push-top lid flips open for easy sipping and snaps down for when you’re on the go.</span>`,
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Size & Capacity',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-0 tw-text-base tw-font-bold'>12oz Essential Coffee Mug</p>
                </>
              ),
              images: {
                desktop: require('./images/essential-coffee-mug/Dims_d.jpg'),
                alt: 'Essential Coffee Mug Dimensions',
              },
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Splash-Resistant Lid</b> keeps your drink from splashing out when you're on the go.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                  </span>,
                  <span>
                    <b>Up to 6 Hours Hot / 24 Hours Cold</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal.{' '}
                    <i>Note: Please use caution when opening the lid with hot liquids.</i>
                  </span>,
                ],
                [
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>Comfortable Handle</b> makes it easy to carry and hold your mug.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> design makes it easy to clean, remember to place the lid on the top
                    rack only.
                  </span>,
                ],
              ],
              bottomNote: (
                <i>
                  Some packaging may state this Mug has a silicone base, however this product has our classic,
                  stainless steel base.
                </i>
              ),
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic lining prevents altered or metallic taste and smell, for better tasting coffee and tea. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double-wall vacuum sealed insulation and an airtight lid seal keep hot for up to 6 hours or cold for up to 24 hours.',
          },
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Avoid Spills',
            description:
              'Built for hot drinks on the go with a splash-resistant, push top lid that flips up for sips and snaps down to lock.',
          },
        ],
      };

    case ProductTypeId.ESSENTIAL_LOWBALL_TUMBLER:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Built for your best cocktails, mocktails, & spirits—with ceramic lining that protects taste to
              the last sip.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours / hot for up to 6 hours</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-lowball-tumbler/meet-the-collection/Better-Taste-Experience-D.jpg'),
                mobile: require('./images/essential-lowball-tumbler/meet-the-collection/Better-Taste-Experience-M.jpg'),
                alt: 'RTIC Essential Lowball Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL LOWBALL TUMBLER',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Lowball Tumbler is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so go ahead and fill it with your favorite drink and enjoy every last sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-lowball-tumbler/meet-the-collection/Cold-Hot-D.jpg'),
                mobile: require('./images/essential-lowball-tumbler/meet-the-collection/Cold-Hot-M.jpg'),
                alt: 'RTIC Essential Lowball Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL LOWBALL TUMBLER',
                title: `<span>Cold (or Hot) ‘Til The Last Sip</span>`,
                description: `<span>This Lowball Tumbler is your new after-hours go to for cocktails, mocktails and all of your favorite drinks. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, it locks in the cold for up to 24 hours. It also keeps warm beverages hot for up to 6 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-lowball-tumbler/meet-the-collection/Splash-Resistant-Lid-D.jpg'),
                mobile: require('./images/essential-lowball-tumbler/meet-the-collection/Splash-Resistant-Lid-M.jpg'),
                alt: 'RTIC Essential Lowball Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL LOWBALL TUMBLER',
                title: `<span>Splash-Resistant Lid</span>`,
                description: `<span>The splash-resistant, fully-insulated lid keeps your drink inside, even when you’re on the go. Just flip the closure open for easy sipping and snap it down for spill-free transportation.</span>`,
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Size & Capacity',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-0 tw-text-base tw-font-bold'>12oz Essential Lowball Tumbler</p>
                </>
              ),
              images: {
                desktop: require('./images/essential-lowball-tumbler/Dims_d.jpg'),
                alt: 'Essential Lowball Tumbler Dimensions',
              },
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Splash-Resistant, Push-Top Lid</b> keeps your drink from splashing out when you’re on
                    the go.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold / 6 Hours Hot</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal.{' '}
                    <i>Note: Please use caution when opening the lid with hot liquids.</i>
                  </span>,
                ],
                [
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> design makes it easy to clean, remember to place the lid on the top
                    rack only.
                  </span>,
                ],
              ],
              bottomNote: (
                <i>
                  Some packaging may state this Lowball Tumbler has a silicone base, however this product has
                  our classic, stainless steel base.
                </i>
              ),
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double wall, vacuum-sealed insulation and an airtight lid seal lock in the cold for up to 24 hours or keep hot for up to 6 hours.',
          },
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Avoid Spills',
            description:
              'Splash-resistant, push-top lid is fully insulated and flips up for sips and snaps down to lock when you’re on the go.',
          },
        ],
      };

    case ProductTypeId.ESSENTIAL_PINT_TUMBLER:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Built to sip your favorite brews– with premium insulation and ceramic lining for great taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours / Keeps hot for up to 6 hours</li>
              <li>Splash-resistant lid</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-pint-tumbler/meet-the-collection/Better-Taste-Experience-Fallback-D.jpg'),
                mobile: require('./images/essential-pint-tumbler/meet-the-collection/Better-Taste-Experience-Fallback-M.jpg'),
                alt: 'RTIC Essential Pint Tumbler',
              },
              videoClassName: 'md:tw-h-[700px] md:tw-object-cover md:tw-object-center',
              video: `${VIDEOS_DOMAIN}/essential-pint-better-taste-experience_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/essential-pint-better-taste-experience_m.mp4`,
              copy: {
                tagline: 'MEET THE ESSENTIAL PINT TUMBLER',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Pint Tumbler is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so go ahead and fill it with your favorite brew and enjoy every last sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-pint-tumbler/meet-the-collection/Cold-Hot-D.jpg'),
                mobile: require('./images/essential-pint-tumbler/meet-the-collection/Cold-Hot-M.jpg'),
                alt: 'RTIC Essential Pint Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL PINT TUMBLER',
                title: `<span>Cold (or Hot) ‘Til The Last Sip</span>`,
                description: `<span>This Pint Tumbler is your new after-hours go to for your favorite brews on a hot day. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, it locks in the cold for up to 24 hours. It also keeps warm beverages hot for up to 6 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-pint-tumbler/meet-the-collection/Splash-Resistant-Lid-D.jpg'),
                mobile: require('./images/essential-pint-tumbler/meet-the-collection/Splash-Resistant-Lid-M.jpg'),
                alt: 'RTIC Essential Pint Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL PINT TUMBLER',
                title: `<span>Splash-Resistant Lid</span>`,
                description: `<span>The splash-resistant, fully-insulated lid keeps your drink inside, even when you’re on the go. Just flip the closure open for easy sipping and snap it down for spill-free transportation.</span>`,
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Size & Capacity',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-0 tw-text-base tw-font-bold'>16oz Essential Pint Tumbler</p>
                </>
              ),
              images: {
                desktop: require('./images/essential-pint-tumbler/Dims_d.jpg'),
                alt: 'Essential Pint Tumbler Dimensions',
              },
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Splash-Resistant Lid</b> keeps your drink from splashing out when you’re on the go.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Do not use metal or hard plastic straws or utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold / 6 Hours Hot</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal.{' '}
                    <i>Note: Please use caution when opening the lid with hot liquids.</i>
                  </span>,
                ],
                [
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> design makes it easy to clean, remember to place the lid on the top
                    rack only.
                  </span>,
                ],
              ],
              bottomNote: (
                <i>
                  Some packaging may state this Pint Tumbler has a silicone base, however this product has our
                  classic, stainless steel base.
                </i>
              ),
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double-wall, vacuum-sealed insulation and an airtight lid seal lock in the cold for up to 24 hours or keep hot for up to 6 hours.',
          },
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Avoid Spills',
            description:
              'Splash-resistant, push-top lid is fully insulated and flips up for sips and snaps down to lock when you’re on the go.',
          },
        ],
      };

    case ProductTypeId.CHILLOUT_BACKPACK_COOLER:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Get the best of both worlds: Part Backpack. Part Cooler.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>24 can capacity</li>
              <li>Lightweight, portable design</li>
              <li>
                Locks in the cold for up to 24 hours when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.BOTTLE_CHILLER:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              An insulated bottle holder designed to fit most bottle sizes.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Splash proof closure</li>
              <li>Fully insulated coverage</li>
              <li>Adaptable to fit 12oz or 16oz Bottles</li>
              <li>Built-in Bottle Opener</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.CAN_CHILLER:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              An insulated can holder designed to fit most can sizes.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Splash proof closure</li>
              <li>Fully insulated coverage</li>
              <li>Adaptable to fit 12oz, 16oz, and Skinny Cans</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.ICE_BUCKET:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>3L of ice capacity</li>
              <li>Locks in the cold for up to 24 hours</li>
              <li>Fully-insulated, stainless steel</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.COFFEE_MUG:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              An insulated Mug with a splash-resistant lid built for hot drinks.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>24 Hours Cold / 6 Hours Hot</li>
              <li>Dishwasher Safe</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.LOWBALL_TUMBLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              A splash-resistant Tumbler for your favorite beverages.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Dishwasher Safe</li>
              <li>Up to 24 hours ice retention</li>
              <li>Personalize with MyRTIC</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.PINT_TUMBLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              A splash-resistant Tumbler for your favorite pints and beverages.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Up to 24-hour Ice Retention</li>
              <li>Dishwasher Safe</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.TUMBLER_TYPE:
      return {
        showDescription: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              A go-to insulated Tumbler for hot and cold drinks.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Up to 24-hour Ice Retention</li>
              <li>Dishwasher Safe</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.JUG:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Insulated Jug built to carry, drink, and share more water.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>24 Hours Cold</li>
              <li>Dishwasher Safe</li>
              <li>Strong 18/8 Stainless Steel construction</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.COCKTAIL_TUMBLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Dishwasher Safe</li>
              <li>Up to 24 hours ice retention</li>
              <li>Personalize with MyRTIC</li>
              <li>Strong 18/8 Stainless Steel construction</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.WINE_TUMBLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Dishwasher Safe</li>
              <li>Up to 24 hours ice retention</li>
              <li>Personalize with MyRTIC</li>
              <li>Strong 18/8 Stainless Steel construction</li>
            </ul>
          </>
        ),
      };
    case ProductTypeId.COCKTAIL_TUMBLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Dishwasher Safe</li>
              <li>Up to 24 hours ice retention</li>
              <li>Personalize with MyRTIC</li>
              <li>Strong 18/8 Stainless Steel construction</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.TRAVEL_MUG:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Insulated Mug built for hot and cold drinks on the go.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>24 Hours Cold / 6 Hours Hot</li>
              <li>Dishwasher Safe</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.BOTTLE:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>Insulated Bottle built to sip as you go.</p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Up to 24-hour Ice Retention</li>
              <li>Dishwasher Safe</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.WATER_BOTTLE:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              An insulated Bottle with a slim-fit design and leak-proof lid.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Up to 24-hour Ice Retention</li>
              <li>Dishwasher Safe</li>
            </ul>
          </>
        ),
      };

    case ProductTypeId.JOURNEY_BOTTLE:
      return {
        /* tagline: 'Includes Divider And Basket', */
        lifestyleHeader: {
          title: 'TASTE THE JOURNEY',
          image: {
            desktop: require('./images/journey-bottle/fallback_hero-d.jpg'),
            mobile: require('./images/journey-bottle/fallback_hero-m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/journey-bottle-hero_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              Built for easy travel and great taste, with a sip-and-go straw and ceramic lining.
            </p>
            <ul className='tw-mb-0 tw-list-none tw-space-y-1 tw-pl-0 tw-text-base'>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/journey-bottle/icon-leak-proof-straw-lid.svg')}
                />
                <div>Leak-proof, flip-straw lid</div>
              </li>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/journey-bottle/icon-ceramic-lined.svg')}
                />
                <div>Ceramic lined for great taste</div>
              </li>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/journey-bottle/icon-ice.svg')}
                />
                <div>Keeps cold for up to 24 hours</div>
              </li>
              <li className='tw-flex tw-items-center tw-space-x-2'>
                <img
                  className='tw-h-[22px] tw-w-[22px]'
                  src={require('./images/journey-bottle/icon-dishwasher-safe.svg')}
                />
                <div>Dishwasher Safe</div>
              </li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/journey-bottle/why_we_love_it_1-d.jpg'),
                mobile: require('./images/journey-bottle/why_we_love_it_1-m.jpg'),
                alt: 'Man carrying the RTIC Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE JOURNEY BOTTLE',
                title: `<span>Clip It Anywhere, Carry It Everywhere</span>`,
                description: `<span>Designed for easy grab-and-go hydration, the Journey Bottle features a built-in, recessed handle that stays out of your way. Plus, you can use a personal carabiner to securely attach the bottle to any bag, backpack, or Soft Pack Cooler.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/journey-bottle/why_we_love_it_2-d.jpg'),
                mobile: require('./images/journey-bottle/why_we_love_it_2-m.jpg'),
                alt: 'RTIC Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE JOURNEY BOTTLE',
                title: `<span>Taste Your Drink, Not Your Drinkware</span>`,
                description: `<span>This Bottle is designed for great taste, with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean and doesn’t absorb any flavors–meaning your drink will taste fresh, sip after sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/journey-bottle/why_we_love_it_3-d.jpg'),
                mobile: require('./images/journey-bottle/why_we_love_it_3-m.jpg'),
                alt: 'RTIC Journey Bottle',
              },
              video: `${VIDEOS_DOMAIN}/journey-bottle_story-benefit-03_d.mp4`,
              copy: {
                tagline: 'MEET THE JOURNEY BOTTLE',
                title: `<span>Sip, Flip, Go</span>`,
                description: `<span>Built to be as active as you are, the Journey Bottle features a leak-proof, easy-sip lid design. Just flip the straw up for sips and flip it down when you’re on the go–you won’t have to worry about spills, leaks, or splashes.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/journey-bottle/why_we_love_it_4-d.jpg'),
                mobile: require('./images/journey-bottle/why_we_love_it_4-m.jpg'),
                alt: 'RTIC Journey Bottle',
              },
              video: `${VIDEOS_DOMAIN}/journey-bottle_story-benefit-04_d.mp4`,
              copy: {
                tagline: 'MEET THE JOURNEY BOTTLE',
                title: `<span>The Perfect Fit</span>`,
                description: `<span>This Bottle is compatible with almost any cup holder. So whether you’re commuting, adventuring, hitting the gym—it’s built to go everywhere you do. Plus, the uniquely designed built-in silicone coaster keeps it from clanking when you set it down.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '16oz',
              sections: [
                {
                  title: {
                    text: '16oz',
                  },
                  images: {
                    desktop: require('./images/journey-bottle/dims_16oz_d.png'),
                    mobile: require('./images/journey-bottle/dims_16oz_d.png'),
                    alt: 'RTIC Journey Bottle 16oz dimensions',
                  },
                  description: (
                    <>
                      <h4>16oz Journey Bottle</h4>
                    </>
                  ),
                },
              ],
            },
            {
              value: '20oz',
              sections: [
                {
                  title: {
                    text: '20oz',
                  },
                  images: {
                    desktop: require('./images/journey-bottle/dims_20oz_d.png'),
                    mobile: require('./images/journey-bottle/dims_20oz_d.png'),
                    alt: 'RTIC Journey Bottle 20oz dimensions',
                  },
                  description: (
                    <>
                      <h4>20oz Journey Bottle</h4>
                    </>
                  ),
                },
              ],
            },
            {
              value: '26oz',
              sections: [
                {
                  title: {
                    text: '26oz',
                  },
                  images: {
                    desktop: require('./images/journey-bottle/dims_26oz_d.png'),
                    mobile: require('./images/journey-bottle/dims_26oz_d.png'),
                    alt: 'RTIC Journey Bottle 26oz dimensions',
                  },
                  description: (
                    <>
                      <h4>26oz Journey Bottle</h4>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Flip-Straw, Leak-Proof Lid</b> flips up for sips and flips down for leak-proof
                    transportation. The lid is compatible with all Journey Bottle sizes.
                  </span>,
                  <span>
                    <b>Ceramic Lining</b> prevents altered or metallic taste and smell, so drinks taste better
                    and it’s easy to clean.
                  </span>,
                  <span>
                    <b>Keeps Cold for up to 24 Hours</b> with double-wall vacuum-sealed insulation, UV heat
                    protection, and an airtight seal
                  </span>,
                  <span>
                    <b>Cup Holder-Friendly Design</b> fits in almost any cup holder, from the car to your
                    beach chair.
                  </span>,
                  <span>
                    <b>Built-In, Recessed Handle</b> makes it easy to carry and hold, whether it's in the car,
                    walking, or working out.
                  </span>,
                ],
                [
                  <span>
                    <b>18/8 Stainless Steel Construction</b> and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>Silicone Base</b> avoids clanking and keeps your bottle in place.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains dry and free of condensation, regardless of what’s
                    inside.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> makes it easy to clean, remember to place the lid and straw on top
                    rack only.
                  </span>,
                ],
              ],
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/journey-bottle/icon-leak-proof.svg'),
            title: 'Leak-Proof, Flip-Straw Lid',
            description:
              'Easy-sip straw lid is designed to flip up for sips, flip down for leak-proof transportation.',
          },
          {
            icon: require('./images/journey-bottle/icon-ice.svg'),
            title: 'Keeps Cold for Up to 24 hours',
            description:
              'Double-wall vacuum insulation, UV heat protection, and an airtight seal lock in the cold.*',
          },
          {
            icon: require('./images/journey-bottle/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/journey-bottle/icon-silicon-base.svg'),
            title: 'Slim Fit + Silicone Base',
            description:
              'Cup holder-friendly design fits anywhere plus a built-in silicone coaster helps to avoid clanking.',
          },
        ],
        iconFeaturesSmallprint: '*The lid and straw are not recommended for use with hot beverages.',
      };

    case ProductTypeId.EVERYDAY_TUMBLER:
      return {
        lifestyleHeader: {
          title: 'Great Taste On The Go',
          image: {
            desktop: require('./images/everyday-tumbler/hero-fallback_d.jpg'),
            mobile: require('./images/everyday-tumbler/hero-fallback_m.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/everyday-tumbler_hero_d.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Premium drinkware with a leak-resistant lid and ceramic lining for better tasting drinks.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Spill-resistant lid (two straws included)</li>
              <li>Keeps cold for up to 24 hours</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/everyday-tumbler/why-we-love-it/perfect-fit_d.jpg'),
                mobile: require('./images/everyday-tumbler/why-we-love-it/perfect-fit_m.jpg'),
                alt: 'Woman drinking from a Blue RTIC Everyday Tumbler',
              },
              videoClassName: 'md:tw-h-[700px] md:tw-object-cover md:tw-object-center',
              video: `${VIDEOS_DOMAIN}/everyday-tumbler_perfect-fit_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/everyday-tumbler_perfect-fit_m.mp4`,
              copy: {
                tagline: 'Why We Love It',
                title: `<span>The Perfect Fit</span>`,
                description: `<span>This Tumbler is compatible with even more cup holders. So whether you’re commuting, hitting a yoga class, or sitting in your Ultra-Tough Chair—it’s built to go everywhere you do. 
                <br /><br />
                Sustainability tip: Bring the Everyday Tumbler to your favorite juice or coffee shop to reduce the waste of single-use plastic cups and straws.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/everyday-tumbler/why-we-love-it/better-taste-experience_d.jpg'),
                mobile: require('./images/everyday-tumbler/why-we-love-it/better-taste-experience_m.jpg'),
                alt: 'RTIC Everyday Tumbler Colors',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Everyday Tumbler is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. So go ahead and fill it with your favorite green juice or iced coffee and enjoy great flavor with every sip. 
                <br /><br />
                Bonus: the ceramic lining makes it easy to clean and doesn’t absorb any flavors.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/everyday-tumbler/why-we-love-it/spill-resistant-lid_d.jpg'),
                mobile: require('./images/everyday-tumbler/why-we-love-it/spill-resistant-lid_m.jpg'),
                alt: 'RTIC Everyday Tumbler Spill-Resistant Lid',
              },
              copy: {
                tagline: 'Why We Love It',
                title: `<span>Spill-Resistant Lid</span>`,
                description: `<span>Enjoy your drinks without fear of spilling thanks to the Tumbler’s splash-proof, fully-insulated Lid.
                <br /><br />
                RTIC’s uniquely designed lid gasket mechanism helps prevent any major spills—so even when your full Tumbler is knocked on its side, you have plenty of time to avoid a mess.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '20oz',
              sections: [
                {
                  title: {
                    text: '20oz',
                  },
                  images: {
                    desktop: require('./images/everyday-tumbler/20oz-dims_d.png'),
                    mobile: require('./images/everyday-tumbler/20oz-dims_m.png'),
                    alt: '20oz RTIC Everyday Tumbler dimensions',
                  },
                  description: (
                    <>
                      <h4>20oz Everyday Tumbler</h4>
                    </>
                  ),
                },
              ],
            },
            {
              value: '28oz',
              sections: [
                {
                  title: {
                    text: '28oz',
                  },
                  images: {
                    desktop: require('./images/everyday-tumbler/28oz-dims_d.png'),
                    mobile: require('./images/everyday-tumbler/28oz-dims_m.png'),
                    alt: '28oz RTIC Everyday Tumbler dimensions',
                  },
                  description: (
                    <>
                      <h4>28oz Everyday Tumbler</h4>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Spill-Resistant Lid</b> helps to prevent major spills and leaks thanks to a uniquely
                    design lid gasket.*
                  </span>,
                  <span>
                    <b>Ceramic Lining</b> prevents altered or metallic taste and smell, so drinks taste better
                    and it’s easy to clean.
                    <br />
                    Note: Do not use metal or hard plastic straws or utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Keeps Cold for up to 24 Hours</b> thanks to vacuum-sealed insulation and an airtight
                    seal that locks in the cold.
                  </span>,
                  <span>
                    <b>Cup Holder-Friendly Design</b> fits in almost every cup holder, so you can take
                    hydration everywhere you go.
                  </span>,
                ],
                [
                  <span>
                    <b>No Sweat Exterior</b> remains dry and free of condensation, regardless of what’s
                    inside.
                  </span>,
                  <span>
                    <b>18/8 Recycled Stainless Steel Construction</b> and BPA free materials offer RTIC
                    quality and durability.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> makes it easy to clean, remember to place the lid and straw on top
                    rack only.
                  </span>,
                  <span>
                    <b>Includes 2 straws</b> with purchase so you can always have a straw on hand when you
                    need it.
                    <br />
                    <p className='tw-mt-3 tw-text-[13px] tw-italic tw-leading-[18px]'>
                      * The Lid and Straw are not recommended for use with hot beverages.
                    </p>
                  </span>,
                ],
              ],
              bottomNote: (
                <i>
                  Some packaging may state this Everyday Tumbler has a silicone base, however this product has
                  our classic, stainless steel base.
                </i>
              ),
            },
          ],
        },
        iconFeatures: [
          {
            icon: require('./images/everyday-tumbler/icon-ceramic-lining-blue.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages.',
          },
          {
            icon: require('./images/everyday-tumbler/icon-snowflake-blue.svg'),
            title: 'Keeps Cold for up to 24 hours',
            description:
              'Double-wall vacuum-sealed insulation, UV heat protection, and an airtight lid seal help keep the cold in.',
          },
          {
            icon: require('./images/everyday-tumbler/icon-spill-resistant-blue.svg'),
            title: 'Spill-Resistant Lid*',
            description:
              "RTIC's uniquely designed lid gasket protects from major spills when the Tumbler is knocked over.",
          },
        ],
        iconFeaturesSmallprint: '*The Lid and Straw are not recommended for use with hot beverages.',
      };

    case ProductTypeId.ULTRA_LIGHT_72_QT_WHEELED:
      return {
        lifestyleHeader: {
          title: (
            <>
              More Space.
              <br />
              Greater Adventures.
            </>
          ),
          image: {
            desktop: require('./images/72qt-ultralight-wheeled/hero/Fallback image desktop.jpg'),
            mobile: require('./images/72qt-ultralight-wheeled/hero/Fallback image mobile.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/wheeled_72_hero_d.mp4`,
        },
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/why-we-love-it/fallback image_organize it your way_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/why-we-love-it/fallback image_organize it your way_m.jpg'),
                alt: '72QT Ultra-light Wheeled Cooler',
              },
              video: `${VIDEOS_DOMAIN}/wheeled_72_host-with-most_d.mp4`,
              copy: {
                tagline: 'Expertly Designed',
                title: `<span>Organize It Your Way</span>`,
                description: `<span>The 72 QT Ultra-Light Wheeled Cooler is expertly designed to fit more, so you can refill less. The interior is compatible with two RTIC's Cooler Dividers and Baskets to help you keep your provisions organized–whether you’re at a Tailgate, BBQ, or exploring off the beaten path.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/why-we-love-it/keeps ice for multiple days_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/why-we-love-it/keeps ice for multiple days_m.jpg'),
                alt: '72QT Ultra-light Wheeled Cooler',
              },
              copy: {
                tagline: 'Premium Ice Retention',
                title: `<span>Locks in the Cold for Up to 6 Days*</span>`,
                description: `<span>Up to 2.5’’ of closed cell foam insulation and a freezer-style gasket lock in the cold for up to 6 days.<br/><br/>To maximize your cold retention, pre chill your cooler with ice or ice packs for 24 hours. Then fill it with more ice, layers of pre chilled food and drinks, and more ice to top it off.<br/><br/>*When using <a class="!tw-underline" href="/cooler-care">RTIC’s Cooling & Care Tips</a></span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/why-we-love-it/durable enough for tough terrain_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/why-we-love-it/durable enough for tough terrain_m.jpg'),
                alt: '72QT Ultra-light Wheeled Cooler',
              },
              copy: {
                tagline: 'Ultra-Light Meets Ultra-Strong',
                title: `<span>Durable enough for tough terrain</span>`,
                description: `<span>With ultra-strong tires and a patented reinforced suspension, our all-terrain, puncture-proof wheels will help you take this cooler anywhere with ease. They can handle mud, dry sand, stairs, rugged trails, and more without ever getting a flat tire.</span>`,
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: 'Dimensions',
                icon: require('./images/icon-ruler.svg'),
              },
              images: {
                desktop: require('./images/72qt-ultralight-wheeled/capacity/72-QT-Wheeled-UL-Dims_d.png'),
                mobile: require('./images/72qt-ultralight-wheeled/capacity/72-QT-Wheeled-UL-Dims_m.png'),
                alt: 'RTIC 72QT Ultra-Light Cooler Wheeled front view and side view showing dimensions of the cooler',
              },
              description: (
                <>
                  <p className='tw-mb-2'>
                    Dimensions: 36 &frac34; ” &times; 18 &frac14;” &times; 18 &frac34;”
                  </p>
                  <p className='tw-mb-2'>Interior Dimensions: 27” &times; 13” &times; 11”</p>
                  <p className='tw-mb-0'>Empty Weight: 35 lbs.</p>
                </>
              ),
            },
            {
              title: {
                text: 'Cans Only',
                icon: require('./images/icon-can.svg'),
              },
              images: {
                desktop: require('./images/72qt-ultralight-wheeled/capacity/72-QT-Wheeled-UL-Cans_d.png'),
                mobile: require('./images/72qt-ultralight-wheeled/capacity/72-QT-Wheeled-UL-Cans_m.png'),
                alt: 'RTIC Wheeled 72QT Ultra-Light Cooler filled with 96 cans',
              },
              description: <p>Holds Up To 96 Cans (No Ice)</p>,
            },
            {
              title: {
                text: 'Ice Only',
                icon: require('./images/icon-ice.svg'),
              },
              images: {
                desktop: require('./images/72qt-ultralight-wheeled/capacity/72-QT-Wheeled-UL-Ice_d.png'),
                mobile: require('./images/72qt-ultralight-wheeled/capacity/72-QT-Wheeled-UL-Ice_m.png'),
                alt: 'RTIC Wheeled 52QT Ultra-Light Cooler filled with 53lbs. of ice',
              },
              description: <p>Holds Up To 71 lbs of Ice</p>,
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/ultra-strong-wheels_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/ultra-strong-wheels_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Wheels',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Ultra-Strong Wheels</span>`,
                description: `<span>Custom-designed, puncture- and impact-resistant tires easily navigate through most any terrain.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/multiple-dividers_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/multiple-dividers_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Baskets & Dividers',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Fits Multiple Baskets & Dividers</span>`,
                description: `<span>The cooler is designed to fit two RTIC Dividers and two Baskets perfectly, to help keep your food and drinks organized.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/insulation_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/insulation_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled 2.5’’ of Insulation',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>2.5’’ of Insulation</span>`,
                description: `<span>With up to 2.5’’ of closed cell foam insulation, this cooler is designed to lock in the cold for up to 7 days when using <a class="!tw-underline" href="/cooler-care">RTIC’s Cooling & Care Tips.</a></span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/aluminum-handle_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/aluminum-handle_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Aluminum Handle',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Powder-Coated Aluminum Handle</span>`,
                description: `<span>The aluminum handle features a pair of non-slip silicone covers for easy towing. When not in use, it falls gently down into position without slamming.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/wheel-axle_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/wheel-axle_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Fully Protected Wheel Axle',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Fully-Protected Wheel Axle</span>`,
                description: `<span>A unique patented chassis design, premium stainless steel wheel bearings and rugged stainless steel axle deliver an ultra-smooth ride and long-lasting durability.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/t-latches_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/t-latches_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Secure T-Latches',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Secure T-Latches</span>`,
                description: `<span>Silicone t-latches clamp down to secure your goods and the freezer-style gasket keeps your cooler sealed tight.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/bottle-opener_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/bottle-opener_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Bottle Opener',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Built-In Bottle Opener</span>`,
                description: `<span>A built-in bottle opener made of premium stainless steel makes it easy to open drinks anytime, anywhere.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/72qt-ultralight-wheeled/tech-and-features/cargo-net_d.jpg'),
                mobile: require('./images/72qt-ultralight-wheeled/tech-and-features/cargo-net_m.jpg'),
                alt: 'RTIC 52QT Ultra-Light Cooler Wheeled Silicone Cargo Net',
              },
              copy: {
                tagline: 'Tech & Features',
                title: `<span>Silicone Cargo Net</span>`,
                description: `<span>A large, durable silicone cargo net is built in to the lid of your cooler for easy storing of smaller items.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Fits Multiple Baskets & Dividers</b> to help keep your food and drinks organized.
                  </span>,
                  <span>
                    <b>Ultra-Strong Wheels</b> are custom-designed, puncture- and impact-resistant to easily
                    navigate through most any terrain.
                  </span>,
                  <span>
                    <b>2.5’’ of Closed Cell Foam Insulation</b> locks in the cold for up to 7 days*
                  </span>,
                  <span>
                    <b>30% Lighter</b> than competing premium wheeled coolers of similar capacities.
                  </span>,
                  <span>
                    <b>Powder-Coated Aluminum Handle</b> features a pair of non-slip silicone covers for easy
                    towing and gently falls into position without slamming down.
                  </span>,
                  <span>
                    <b>Fully-Protected Wheel Axle</b> uses a unique, patented chassis design, premium
                    stainless steel wheel bearings, and rugged stainless steel axle deliver an ultra-smooth
                    ride and long-lasting durability.
                  </span>,
                ],
                [
                  <span>
                    <b>Secure T-Latches</b> clamp down the lid and a freezer-style gasket keeps your cooler
                    sealed tight.
                  </span>,
                  <span>
                    <b>Silicone Cargo Net</b> is built in to the lid of your cooler for easy storing of
                    smaller items.
                  </span>,
                  <span>
                    <b>Built-In Bottle Opener</b> made of premium stainless steel makes it easy to open drinks
                    anytime, anywhere.
                  </span>,
                  <span>
                    <b>Non-Skid Rubber Feet</b> help keep the cooler in place, even on the smoothest and most
                    slippery surfaces. Avoid setting coolers on light-colored surfaces or fabrics, especially
                    when using in high heat or in direct sun.
                  </span>,
                  <span>
                    <b>Tie Down Points</b> are available to easily attach your cooler to a boat, trailer, or
                    truck bed.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains dry, so you can store it in the back of your car without
                    worrying about any mess.
                  </span>,
                ],
              ],
            },
          ],
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Big enough to fit all you need, light enough to go everywhere—from a Tailgate to an epic
              adventure.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>96 can capacity</li>
              <li>All-terrain, puncture-proof wheels</li>
              <li>
                Locks in the cold for up to 6 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-ice-retention.svg'),
            title: 'Premium Ice Retention',
            description:
              'Up to 2.5” of insulation and a freezer-style gasket lock in the cold for up to 6 days.',
          },
          {
            icon: require('./images/ultra-light-construction.svg'),
            title: 'Ultra-Light Construction',
            description:
              'Engineered to be up to 30% lighter than wheeled rotomolded coolers of similar capacities.',
          },
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-ultra-strong-wheels.svg'),
            title: 'Ultra-Strong Wheels',
            description: 'Puncture-proof, all-terrain wheels help you tow through rugged terrains with ease.',
          },
          {
            icon: require('./images/52qt-ultralight-wheeled/icon-easy-to-tow.svg'),
            title: 'Ergonomic Handle',
            description:
              'A silicone-gripped, no-slam, aluminum handle makes it easy to tow, even when it’s full.',
          },
        ],
        iconFeaturesSmallprint: (
          <>
            *When using RTIC's{' '}
            <a className='!tw-underline' href='/cooler-care'>
              Cooling & Care Tips
            </a>
            <br />
            **Compared to comparable leading rotomolded coolers
          </>
        ),
        collectionComponent: <MeetTheUltraLightCollection />,
      };

    case ProductTypeId.BACKPACK_COOLER:
      return {
        showDescription: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-[22px]'>
              Go hands-free with this durable, fully-waterproof Backpack Cooler
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Leakproof, hands-free design</li>
              <li>Waterproof zipper</li>
              <li>
                Locks in the cold for up to 2 days when following{' '}
                <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        variants: {
          size: {
            '24 Can': {
              noTooltip: true,
              tagline: (
                <>
                  Some packaging may say 20 Can Backpack Cooler.
                  <br />
                  Don't worry, it still fits 24 cans.
                </>
              ),
            },
            '36 Can': {
              noTooltip: true,
              tagline: (
                <>
                  Some packaging may say 30 Can Backpack Cooler.
                  <br />
                  Don't worry, it still fits 36 cans.
                </>
              ),
            },
          },
        },
      };

    case ProductTypeId.ULTRA_TOUGH_20QT_COOLER:
      return {
        showDescription: true,
        showDimensions: true,
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Pack for adventure with this compact, durable cooler.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>30 can capacity</li>
              <li>Rotomolded Construction for extreme durability</li>
              <li>Multi-day ice retention</li>
            </ul>
          </>
        ),
        featureSlides: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/20qt-ultra-tough-cooler/easy-to-take-d.jpg'),
                mobile: require('./images/20qt-ultra-tough-cooler/easy-to-take-m.jpg'),
                alt: 'RTIC 20QT Ultra-Tough Cooler Easy to Take, Tough to Break',
              },
              copy: {
                tagline: 'MEET THE 20 QT ULTRA-TOUGH COOLER',
                title: <span>Easy to Take, Tough to Break</span>,
                description: (
                  <p>
                    Small enough to carry solo, big enough to fit 30 cans, and tough enough to take the heat
                    for years to come—whether you’re bringing it up to the worksite, out on the boat, or
                    anywhere in between.
                  </p>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/20qt-ultra-tough-cooler/long-live-the-ice-d.jpg'),
                mobile: require('./images/20qt-ultra-tough-cooler/long-live-the-ice-m.jpg'),
                alt: 'RTIC 20QT Ultra-Tough Cooler Holding Ice for too long',
              },
              copy: {
                tagline: 'MEET THE 20 QT ULTRA-TOUGH COOLER',
                title: <span>Long Live The Ice</span>,
                description: (
                  <p>
                    Locks in the cold for up to 4 days with 1.7 inches of closed-cell foam insulation backed
                    up by a 360º freezer-style gasket and exterior T-latches.
                  </p>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/20qt-ultra-tough-cooler/ultra-tough-construction-d.jpg'),
                mobile: require('./images/20qt-ultra-tough-cooler/ultra-tough-construction-m.jpg'),
                alt: 'RTIC 20QT Ultra-Tough Cooler Ultra-Tough Construction',
              },
              copy: {
                tagline: 'MEET THE 20 QT ULTRA-TOUGH COOLER',
                title: <span>Ultra-Tough Construction</span>,
                description: (
                  <p>
                    Fold away the 20’s Stainless Steel Handle to use its sturdy, rotomolded lid as a casting
                    platform, camp seat, or ‘coffee’ table.
                  </p>
                ),
              },
            },
          ],
        },
      };

    case ProductTypeId.ROAD_TRIP_PERSONAL_COOLER:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName:
            'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3 tw-items-end',
          titleClassName: 'tw-text-left',
          title: (
            <>
              YOUR <br />
              PERSONAL <br />
              SIDEKICK
            </>
          ),
          image: {
            desktop: require('./images/road-trip-personal-cooler/Hero-D-Fallback.jpg'),
            mobile: require('./images/road-trip-personal-cooler/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/roadtrip-personal-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/roadtrip-personal-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              Your grab 'n go cooler-light, strong and ready to go.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>12 Can | 8 QT Capacity</li>
              <li className='tw-text-base tw-font-normal'>Durable & Lightweight</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 40 hours when following{' '}
                <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
                  RTIC's Cooling Tips
                </a>
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2’’ of closed-cell foam insulation locks in the cold for up to 40 hours when following{' '}
                <a className='tw-underline' target='_blank' href='/ice-retention-tips'>
                  RTIC's Cooling Tips
                </a>
                .
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-construction-leaf.svg'),
            title: <>Ultra-Light Construction</>,
            description: (
              <>Engineered with RTIC’s Ultra-Light technology for a lightweight, durable design.</>
            ),
          },
          {
            icon: require('./images/icons/icon-lid-lock-handle.svg'),
            title: <>Lid Lock Handle</>,
            description: (
              <>Durable handle lifts up to lock the lid in place and folds down to open the lid.</>
            ),
          },
          {
            icon: require('./images/icons/icon-personal-size.svg'),
            title: <>Personal Size</>,
            description: <>Compact design is easy to pack for work or activities and fits in small spaces.</>,
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/meet-the-collection/Safe-and-Secure-Fallback-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/meet-the-collection/Safe-and-Secure-Fallback-M.jpg'),
                alt: 'RTIC Road Trip Personal Cooler Safe and Secure Image',
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/roadtrip-personal-safe-secure_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/roadtrip-personal-safe-secure_m.mp4`,
              copy: {
                tagline: 'MEET THE ROAD TRIP PERSONAL COOLER',
                title: <span>Safe and Secure</span>,
                description: (
                  <span>
                    The Road Trip Personal Cooler features a lid-lock handle to help keep ice securely inside.
                    When the handle is up, it locks your cooler. When the handle is down, the lid stays on
                    even when it’s knocked over.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/meet-the-collection/Compact-Design-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/meet-the-collection/Compact-Design-M.jpg'),
                alt: 'RTIC Road Trip Personal Cooler Compact Design Image',
              },
              copy: {
                tagline: 'MEET THE ROAD TRIP PERSONAL COOLER',
                title: <span>Premium Performance, Compact Design</span>,
                description: (
                  <span>
                    Designed with the quality, durability, and insulation of our Ultra-Light Collection, the
                    Road Trip Personal Cooler offers premium performance in a compact design. This personal
                    cooler is light, strong, and locks in the cold for up to 40 hours when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC's Cooling Tips
                    </a>{' '}
                    –so it keeps your favorite things cold even on a hot day.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/meet-the-collection/Personal-Packing-Fallback-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/meet-the-collection/Personal-Packing-Fallback-M.jpg'),
                alt: 'RTIC Road Trip Personal Cooler Personal Packing Image',
              },
              videoClassName: 'tw-object-cover',
              video: 'https://static.velkybrands.com/v/roadtrip-personal-packing_d.mp4',
              videoMob: 'https://static.velkybrands.com/v/roadtrip-personal-packing_m.mp4',
              copy: {
                tagline: 'MEET THE ROAD TRIP PERSONAL COOLER',
                title: <span>Personal Packing</span>,
                description: (
                  <span>
                    With a 12 can capacity, the Road Trip Personal Cooler makes it easy to pack your lunch,
                    picnic, or road trip essentials. Compatible with loose ice to keep everything extra cold,
                    while a roomy silicone cargo net helps keep your dry goods dry and organized. Plus, make
                    it extra personal with MyRTIC personalization options.
                  </span>
                ),
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>Exterior Dimensions:</p>
                  <p className='tw-mb-1 tw-font-medium'>
                    Handle Up: 11 &frac34;” &times; 8” &times; 14 &frac14;”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>Handle Down: 11 &frac34;” &times; 8” &times; 11”</p>
                  <p className='tw-mb-1 tw-font-medium'>
                    Interior Dimensions: 10” &times; 5 &frac12;” &times; 7 &frac34;”
                  </p>
                  <p className='tw-mb-1 tw-font-medium'>Can Capacity: 12 Cans</p>
                  <p className='tw-mb-1 tw-font-medium'>Empty Weight: 4.1 lbs.</p>
                </>
              ),
              images: {
                desktop: require('./images/road-trip-personal-cooler/Dims-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/Dims-M.jpg'),
                alt: 'Road Trip Personal Cooler Dimensions',
              },
            },
          ],
        },

        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Ultra-Light-Construction-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Ultra-Light-Construction-M.jpg'),
                alt: 'Close up of the Road Trip Personal Cooler Ultra-Light Construction',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Ultra-Light Construction</span>,
                description: (
                  <span>
                    Ultra-Light Construction uses injection molding to make this cooler durable and
                    lightweight.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Premium-Ice-Retention-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Premium-Ice-Retention-M.jpg'),
                alt: 'Close up of the gasket on the lid of the RTIC 22QT Ultra-Light Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <span>
                    Up to 2 inches of closed-cell foam insulation and a freezer-style gasket lock in the cold
                    all day long when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC’s Cooling Tips
                    </a>
                    .
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Lid-Lock-Handle-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Lid-Lock-Handle-M.jpg'),
                alt: 'Close up of the Lid-Lock Handle on the RTIC Road Trip Personal Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Lid-Lock Handle</span>,
                description: (
                  <span>
                    Durable handle locks the lid in place when it’s up and unlocks the lid when it’s down.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Durable-Handle-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Durable-Handle-M.jpg'),
                alt: 'Close up of the Durable Handle on the RTIC Road Trip Personal Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Durable Handle</span>,
                description: (
                  <span>Durable, textured handle is metal reinforced and recesses into lid for storage.</span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Personal-Size-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Personal-Size-M.jpg'),
                alt: 'Close up of the Personal Size of the RTIC Road Trip Personal Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Personal Size</span>,
                description: (
                  <span>Compact design is easy to pack for work or activities and fits in small spaces.</span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Silicone-Cargo-Net-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Silicone-Cargo-Net-M.jpg'),
                alt: 'Close up of the Silicone Cargo Net on the RTIC Road Trip Personal Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Silicone Cargo Net</span>,
                description: (
                  <span>
                    Silicone cargo net is built into the lid of the cooler for easy storage of dry goods.
                  </span>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/road-trip-personal-cooler/features/Non-Skid-Feet-D.jpg'),
                mobile: require('./images/road-trip-personal-cooler/features/Non-Skid-Feet-M.jpg'),
                alt: 'Close up of the Non-Skid Feet on the RTIC Road Trip Personal Cooler',
              },
              copy: {
                tagline: 'Tech & Features',
                title: <span>Non-Skid Feet</span>,
                description: (
                  <span>Non-skid feet help keep the cooler in place even on the smoothest surfaces.</span>
                ),
              },
            },

            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Ultra-Light Construction</b> uses injection molding to make this cooler durable and
                    lightweight.
                  </span>,
                  <span>
                    <b>Up to 2 inches of Closed-Cell Foam Insulation</b> and a freezer-style gasket locks in
                    the cold all day long when following{' '}
                    <a href='/ice-retention-tips' target='_blank' className='tw-underline'>
                      RTIC's Cooling Tips
                    </a>
                    .
                  </span>,
                  <span>
                    <b>Lid-Lock Handle</b> locks the lid in place when it's up and unlocks the lid when it's
                    down.
                  </span>,
                  <span>
                    <b>Durable, Textured Handle</b> is metal reinforced and recesses into lid for storage.
                  </span>,
                ],
                [
                  <span>
                    <b>Compact, Personal Size</b> is easy to pack for work or activities and fits in small
                    spaces.
                  </span>,
                  <span>
                    <b>Silicone Cargo Net</b> is built in to the lid of your cooler for easy storing of
                    smaller items.
                  </span>,
                  <span>
                    <b>Non-Skid Feet</b> help keep the cooler in place even on the smoothest and most slippery
                    surfaces.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.LITTLE_JOURNEY_BOTTLE:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Built to go everywhere your kids do—with a sip-and-go straw and ceramic lining for great taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Leak-proof, flip-straw lid</li>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours</li>
              <li>Dishwasher safe + BPA free</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Leak-Proof, Flip-Straw Lid',
            description:
              'Easy-sip straw lid flips up for sips and flips down for leak-proof travel, from the backpack to the playground.',
          },
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: 'Keeps Cold for Up to 24 hours',
            description:
              'Double-wall vacuum insulation, UV heat protection, and an airtight seal lock in the cold to keep your little one hydrated.*',
          },
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for great tasting water & juice. Plus, it’s easy to clean.',
          },
          {
            icon: require('./images/icons/icon-silicon-base.svg'),
            title: 'Slim Fit + Silicone Base',
            description:
              'Easy to carry for little hands, fits in car seat cup holders, and doesn’t clank when it’s set down.',
          },
        ],
        iconFeaturesSmallprint: '*The lid and straw are not recommended for use with hot beverages.',
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/little-journey-bottle/meet-the-collection/Better-Taste-D.jpg'),
                mobile: require('./images/little-journey-bottle/meet-the-collection/Better-Taste-M.jpg'),
                alt: 'RTIC Little Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE LITTLE JOURNEY BOTTLE',
                title: `<span>Better Taste with Every Sip</span>`,
                description: `<span>This Bottle is designed for great taste, with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean and doesn’t absorb any flavors, meaning water and juice will taste just how it should–sip after sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/little-journey-bottle/meet-the-collection/Sip-Flip-Go-D.jpg'),
                mobile: require('./images/little-journey-bottle/meet-the-collection/Sip-Flip-Go-M.jpg'),
                alt: 'RTIC Little Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE LITTLE JOURNEY BOTTLE',
                title: `<span>Sip, Flip, Go</span>`,
                description: `<span>Built to be as active as kids are, the Little Journey Bottle features a leak-proof, easy-sip lid design. Just flip the straw up for sips and flip it down to close it and keep it clean–so you won’t have to worry about spills or splashes–from the back seat to the playground.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/little-journey-bottle/meet-the-collection/Clip-It-Anywhere-D.jpg'),
                mobile: require('./images/little-journey-bottle/meet-the-collection/Clip-It-Anywhere-M.jpg'),
                alt: 'RTIC Little Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE LITTLE JOURNEY BOTTLE',
                title: `<span>Clip It Anywhere, Carry It Everywhere</span>`,
                description: `<span>Designed for easy grab-and-go hydration, the Little Journey Bottle features a built-in, recessed handle that makes it easy to carry, even for little hands. It can also be used to clip the bottle to backpacks or lunch bags using a personal carabiner.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/little-journey-bottle/meet-the-collection/The-Perfect-Fit-D.jpg'),
                mobile: require('./images/little-journey-bottle/meet-the-collection/The-Perfect-Fit-M.jpg'),
                alt: 'RTIC Little Journey Bottle',
              },
              copy: {
                tagline: 'MEET THE LITTLE JOURNEY BOTTLE',
                title: `<span>The Perfect Fit</span>`,
                description: `<span>This Bottle is compatible with almost any cup and bottle holder. So it’s built to go everywhere—from the car seat to the classroom, to recess, to after-school activities. The uniquely designed built-in silicone coaster keeps it from clanking when it’s dropped. Plus, you can personalize it with MyRTIC so it won’t get lost at school.</span>`,
              },
            },
          ],
        },
        capacity: {
          textOnly: true,
          title: 'Dimensions',
          sections: [
            {
              description: (
                <>
                  <p className='tw-mb-1 tw-font-medium'>12oz Little Journey Bottle</p>
                </>
              ),
              images: {
                desktop: require('./images/little-journey-bottle/Dims.jpg'),
                mobile: require('./images/little-journey-bottle/Dims.jpg'),
                alt: 'RTIC Little Journey Bottle Dimensions',
              },
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leak-Proof Lid</b> has a Flip-Straw, Leak-Proof Lid that flips up for sips and flips
                    down for leak-proof transportation.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Designed for use with the included straw only. Avoid using metal or hard plastic
                    straws, or utensils, as they may scrape the ceramic lining.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold*</b> with double-wall, vacuum-sealed insulation, UV heat
                    protection, and an airtight seal.
                  </span>,
                  <span>
                    <b>Cup Holder-Friendly Design</b> is a Slim-Fit Design that fits in almost any cupholder,
                    including backpack bottle holders.
                  </span>,
                ],
                [
                  <span>
                    <b>Built-In, Recessed Handle</b> has a Recessed Carrying Ring that makes it easy to carry
                    and hold, whether that’s heading to school, recess, or after-school activities.
                  </span>,
                  <span>
                    <b>RTIC Tough</b> 18/8 Stainless Steel and BPA-free materials offer quality and durability
                    you can count on.
                  </span>,
                  <span>
                    <b>Silent Silicone Base</b> avoids clanking for a soft landing on any surface.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so their hands stay dry and it
                    won’t leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe design</b> so it’s easy to clean, remember to place the lid on the top
                    rack only.
                    <br />
                    <br />
                    *The Lid and Straw are not recommended for use with hot beverages.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.OUTBACK_BOTTLE:
      return {
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              The ultimate, insulated bottle, with a leak-proof, wide-mouth lid and ceramic lining for great
              taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours / hot for up to 6 hours</li>
              <li>Leak-proof, wide-mouth lid</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double-wall, vacuum-sealed insulation and an airtight lid seal lock in the cold for up to 24 hours or keep hot for up to 6 hours.',
          },
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Leak-Proof Lid',
            description:
              'An insulated lid with a leak-proof, flip-top closure and wide-mouth design for easy refills and adding ice.',
          },
          {
            icon: require('./images/icons/icon-silicon-base.svg'),
            title: 'Silent Silicone Base',
            description: 'A built-in silicone coaster avoids clanking for a soft landing on any surface.',
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/outback-bottle/meet-the-collection/Taste-Your-Water-D.jpg'),
                mobile: require('./images/outback-bottle/meet-the-collection/Taste-Your-Water-M.jpg'),
                alt: 'RTIC OUTBACK BOTTLE',
              },
              copy: {
                tagline: 'MEET THE OUTBACK BOTTLE',
                title: `<span>Taste Your Water, Not Your Bottle</span>`,
                description: `<span>The Outback Bottle is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell–even when you’re outdoors in the hot sun all day. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so your water tastes just how it should until the very last sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/outback-bottle/meet-the-collection/Stay-Refreshed-D.jpg'),
                mobile: require('./images/outback-bottle/meet-the-collection/Stay-Refreshed-M.jpg'),
                alt: 'RTIC OUTBACK BOTTLE',
              },
              copy: {
                tagline: 'MEET THE OUTBACK BOTTLE',
                title: `<span>Stay Refreshed</span>`,
                description: `<span>This Bottle is your new go to for staying hydrated on any adventure. Built to handle hot days outdoors with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, it locks in the cold for up to 24 hours. It also keeps hot for up to 6 hours. Please use caution when opening the lid with hot liquids.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/outback-bottle/meet-the-collection/Active-D.jpg'),
                mobile: require('./images/outback-bottle/meet-the-collection/Active-M.jpg'),
                alt: 'RTIC OUTBACK BOTTLE',
              },
              copy: {
                tagline: 'MEET THE OUTBACK BOTTLE',
                title: `<span>Built to Be as Active as You Are</span>`,
                description: `<span>Stay hydrated without fear of spills or leaks thanks to the Bottle’s leak-proof, flip-top lid. Featuring threading that stays on tight and a built-in carrying ring, the lid’s closure flips open for easy sipping and flips down for when you’re on the go. Plus, the wide mouth opening makes it easy to fill up and add ice when you’re ready for a refill.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '32oz',
              sections: [
                {
                  title: {
                    text: '32oz',
                  },
                  images: {
                    desktop: require('./images/outback-bottle/dims/32oz_d.jpg'),
                    mobile: require('./images/outback-bottle/dims/32oz_m.jpg'),
                    alt: 'RTIC 32oz Outback Bottle Dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>32oz Outback Bottle</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '40oz',
              sections: [
                {
                  title: {
                    text: '40oz',
                  },
                  images: {
                    desktop: require('./images/outback-bottle/dims/40oz_d.jpg'),
                    mobile: require('./images/outback-bottle/dims/40oz_m.jpg'),
                    alt: 'RTIC 40oz Outback Bottle Dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>40oz Outback Bottle</p>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leak-Proof Lid</b> with a leak-proof closure, built-in carrying ring, and wide mouth
                    opening for easy refills and adding ice.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Do not use metal or hard plastic straws and utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold / 6 Hours Hot</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal.{' '}
                    <i>Note: Please use caution when opening the lid with hot liquids.</i>
                  </span>,
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                ],
                [
                  <span>
                    <b>Silent Silicone Base</b> avoids clanking for a soft landing on any surface.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe Design</b> is easy to clean, remember to place the lid on the top rack
                    only.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.OUTBACK_JUG:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              BETTER TASTE <br />
              FEWER REFILLS
            </>
          ),
          image: {
            desktop: require('./images/outback-jug/Hero-D.jpg'),
            mobile: require('./images/outback-jug/Hero-M.jpg'),
          },
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              Insulated for all-day cold. Ceramic lined for all-day great taste. Big enough to be shared.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours / hot for up to 6 hours</li>
              <li>Leak-proof, wide-mouth lid</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Cold & Hot',
            description:
              'Double-wall vacuum sealed insulation and an airtight lid seal lock in the cold for up to 24 hours or keep hot for up to 6 hours.',
          },
          {
            icon: require('./images/icons/icon-wide-mouth-lid-spout.svg'),
            title: 'Wide-Mouth Lid & Spout',
            description:
              'Leak-proof lid has a wide-mouth design for easy refills and adding ice, and a spout designed for smooth pours and easy drinking.',
          },
          {
            icon: require('./images/icons/icon-silicon-base.svg'),
            title: 'Silent Silicone Base',
            description: 'A built-in silicone coaster avoids clanking for a soft landing on any surface.',
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/outback-jug/meet-the-collection/Taste-Your-Drink-D.jpg'),
                mobile: require('./images/outback-jug/meet-the-collection/Taste-Your-Drink-M.jpg'),
                alt: 'RTIC Outback Jug',
              },
              copy: {
                tagline: 'MEET THE OUTBACK JUG',
                title: `<span>Taste Your Drink, Not Your Drinkware</span>`,
                description: `<span>The Outback Jug is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell–even when you’re outdoors in the hot sun all day. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so your water tastes just how it should until the very last sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/outback-jug/meet-the-collection/All-Day-Hydration-Fallback-D.jpg'),
                mobile: require('./images/outback-jug/meet-the-collection/All-Day-Hydration-Fallback-M.jpg'),
                alt: 'RTIC Outback Jug',
              },
              copy: {
                tagline: 'MEET THE OUTBACK JUG',
                title: `<span>All-Day Hydration</span>`,
                description: `<span>This Jug is your new go-to for when you need a lot of water–whether that’s hiking, heading to practice, or camping with the family. Built to handle hot days outdoors with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, it locks in the cold for up to 24 hours. It also keeps hot for up to 6 hours. Please use caution when opening the lid.</span>`,
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/outback-jug-all-day-hydration_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/outback-jug-all-day-hydration_m.mp4`,
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/outback-jug/meet-the-collection/Built-for-Adventure-D.jpg'),
                mobile: require('./images/outback-jug/meet-the-collection/Built-for-Adventure-M.jpg'),
                alt: 'RTIC Outback Jug',
              },
              copy: {
                tagline: 'MEET THE OUTBACK JUG',
                title: `<span>Built for Adventure</span>`,
                description: `<span>Keep your whole crew hydrated, from the road to the campground, without fear of spills or leaks thanks to the Jug’s leak-proof lid. Featuring threading that stays on tight and a durable, built-in carrying handle, the lid’s cap unscrews open for easy sipping and screws on tight for when you’re on the go. Plus, the wide mouth opening makes it easy to fill up and add ice when you’re ready for a refill.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: 'Half Gallon',
              sections: [
                {
                  title: {
                    text: '1/2G',
                  },
                  images: {
                    desktop: require('./images/outback-jug/Dims-HalfG.jpg'),
                    mobile: require('./images/outback-jug/Dims-HalfG.jpg'),
                    alt: 'RTIC Outback Jug 1/2G dimensions',
                  },
                },
              ],
            },
            {
              value: 'One Gallon',
              sections: [
                {
                  title: {
                    text: '1G',
                  },
                  images: {
                    desktop: require('./images/outback-jug/Dims-1G.jpg'),
                    mobile: require('./images/outback-jug/Dims-1G.jpg'),
                    alt: 'RTIC Outback Jug 1G dimensions',
                  },
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leak-Proof Lid</b> has a wide-mouth design for easy refills and adding ice, and a spout
                    designed for smooth pours and easy drinking.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior*</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Do not use metal or hard plastic straws and utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold / 6 Hours Hot</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal. Use caution when opening the lid with hot liquids.
                  </span>,
                ],
                [
                  <span>
                    <b>Built-in Handle</b> is soft gripped for comfortable carrying, even with a full jug.
                  </span>,
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>Silent Silicone Base</b> avoids clanking for a soft landing on any surface.
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe Design</b> is easy to clean, remember to place the lid on the top rack
                    only.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.ESSENTIAL_TUMBLER:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              YOUR GO-TO <br />
              TUMBLER
            </>
          ),
          image: {
            desktop: require('./images/essential-tumbler/Hero-D-Fallback.jpg'),
            mobile: require('./images/essential-tumbler/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/essential-tumbler-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/essential-tumbler-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              A premium, insulated Tumbler with ceramic lining—for all-day cold and all-day great taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours / hot for up to 6 hours</li>
              <li>Leak-resistant closure</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double-wall, vacuum-sealed insulation and an airtight lid seal lock in the cold for up to 24 hours or keep hot for up to 6 hours.',
          },
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Leak-Resistant Lid',
            description:
              'Built for drinks on the go with a double-threaded, leak-resistant lid that flips up for sips and snaps down to lock.',
          },
          {
            icon: require('./images/icons/icon-silicon-base.svg'),
            title: 'Silent Silicone Base',
            description: 'A built-in silicone coaster avoids clanking for a soft landing on any surface.',
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-tumbler/meet-the-collection/Better-Taste-Experience-D.jpg'),
                mobile: require('./images/essential-tumbler/meet-the-collection/Better-Taste-Experience-M.jpg'),
                alt: 'RTIC Essential Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL TUMBLER',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Essential Tumbler is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so go ahead and fill it with your favorite beverage to enjoy great taste with every sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-tumbler/meet-the-collection/Hot-Cold-D.jpg'),
                mobile: require('./images/essential-tumbler/meet-the-collection/Hot-Cold-M.jpg'),
                alt: 'RTIC Essential Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL TUMBLER',
                title: `<span>Hot or Cold Drinks On the Go</span>`,
                description: `<span>This Tumbler is your new go to for all-day drinks from morning brews, to on-the-go hydration, and evening commutes. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, this Tumbler locks in the cold for up to 24 hours and keeps hot for up to 6 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-tumbler/meet-the-collection/Leak-Resistant-Lid-D.jpg'),
                mobile: require('./images/essential-tumbler/meet-the-collection/Leak-Resistant-Lid-M.jpg'),
                alt: 'RTIC Essential Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL TUMBLER',
                title: `<span>Leak-Resistant Lid</span>`,
                description: `<span>Enjoy your drinks without fear of spills or leaks thanks to the Tumbler’s leak-resistant, fully-insulated lid. Featuring double threading so it stays on tight, this lid’s closure flips open for easy sipping and snaps down for when you’re on the go.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '20oz',
              sections: [
                {
                  title: {
                    text: '20oz',
                  },
                  images: {
                    desktop: require('./images/essential-tumbler/Dims_20oz.jpg'),
                    mobile: require('./images/essential-tumbler/Dims_20oz.jpg'),
                    alt: 'RTIC Essential Tumbler 20oz dimensions',
                  },
                },
              ],
            },
            {
              value: '30oz',
              sections: [
                {
                  title: {
                    text: '30oz',
                  },
                  images: {
                    desktop: require('./images/essential-tumbler/Dims_30oz.jpg'),
                    mobile: require('./images/essential-tumbler/Dims_30oz.jpg'),
                    alt: 'RTIC Essential Tumbler 30oz dimensions',
                  },
                },
              ],
            },
            {
              value: '40oz',
              sections: [
                {
                  title: {
                    text: '40oz',
                  },
                  images: {
                    desktop: require('./images/essential-tumbler/Dims_40oz.jpg'),
                    mobile: require('./images/essential-tumbler/Dims_40oz.jpg'),
                    alt: 'RTIC Essential Tumbler 40oz dimensions',
                  },
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leak-Resistant Lid</b> features double threading and a leak-resistant tab to keep your
                    drink from spilling and your lid on tight.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Do not use metal or hard plastic straws or utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold / 6 Hours Hot</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal.{' '}
                    <i>Note: Please use caution when opening the lid with hot liquids.</i>
                  </span>,
                ],
                [
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>Built-in Silicone Base</b> avoids clanking for a soft landing on any surface.{' '}
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> makes it easy to clean, remember to place the lid on the top rack
                    only.
                  </span>,
                ],
              ],
            },
          ],
        },
      };
    case ProductTypeId.ESSENTIAL_TUMBLER:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              YOUR GO-TO <br />
              TUMBLER
            </>
          ),
          image: {
            desktop: require('./images/essential-tumbler/Hero-D-Fallback.jpg'),
            mobile: require('./images/essential-tumbler/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/essential-tumbler-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/essential-tumbler-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal'>
              A premium, insulated Tumbler with ceramic lining—for all-day cold and all-day great taste.
            </p>

            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4 tw-text-base tw-font-normal'>
              <li>Ceramic lined for great taste</li>
              <li>Keeps cold for up to 24 hours / hot for up to 6 hours</li>
              <li>Leak-resistant closure</li>
              <li>Dishwasher safe</li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ceramic-lined.svg'),
            title: 'Ceramic Lined',
            description:
              'Ceramic-lined interior protects against metallic taste and smell, for better tasting beverages.',
          },
          {
            icon: require('./images/icons/icon-hot-cold.svg'),
            title: 'Hot & Cold',
            description:
              'Double-wall, vacuum-sealed insulation and an airtight lid seal lock in the cold for up to 24 hours or keep hot for up to 6 hours.',
          },
          {
            icon: require('./images/icons/icon-avoid-spills.svg'),
            title: 'Leak-Resistant Lid',
            description:
              'Built for drinks on the go with a double-threaded, leak-resistant lid that flips up for sips and snaps down to lock.',
          },
          {
            icon: require('./images/icons/icon-silicon-base.svg'),
            title: 'Silent Silicone Base',
            description: 'A built-in silicone coaster avoids clanking for a soft landing on any surface.',
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-tumbler/meet-the-collection/Better-Taste-Experience-D.jpg'),
                mobile: require('./images/essential-tumbler/meet-the-collection/Better-Taste-Experience-M.jpg'),
                alt: 'RTIC Essential Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL TUMBLER',
                title: `<span>A Better Taste Experience</span>`,
                description: `<span>The Essential Tumbler is designed for great taste with a ceramic-lined interior that protects beverages from metallic taste and smell. The ceramic lining is also easy to clean, and doesn’t absorb any flavors, so go ahead and fill it with your favorite beverage to enjoy great taste with every sip.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-tumbler/meet-the-collection/Hot-Cold-D.jpg'),
                mobile: require('./images/essential-tumbler/meet-the-collection/Hot-Cold-M.jpg'),
                alt: 'RTIC Essential Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL TUMBLER',
                title: `<span>Hot or Cold Drinks On the Go</span>`,
                description: `<span>This Tumbler is your new go to for all-day drinks from morning brews, to on-the-go hydration, and evening commutes. Built with double-wall, vacuum-sealed insulation, UV heat protection, and an airtight seal, this Tumbler locks in the cold for up to 24 hours and keeps hot for up to 6 hours.</span>`,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/essential-tumbler/meet-the-collection/Leak-Resistant-Lid-D.jpg'),
                mobile: require('./images/essential-tumbler/meet-the-collection/Leak-Resistant-Lid-M.jpg'),
                alt: 'RTIC Essential Tumbler',
              },
              copy: {
                tagline: 'MEET THE ESSENTIAL TUMBLER',
                title: `<span>Leak-Resistant Lid</span>`,
                description: `<span>Enjoy your drinks without fear of spills or leaks thanks to the Tumbler’s leak-resistant, fully-insulated lid. Featuring double threading so it stays on tight, this lid’s closure flips open for easy sipping and snaps down for when you’re on the go.</span>`,
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '20oz',
              sections: [
                {
                  title: {
                    text: '20oz',
                  },
                  images: {
                    desktop: require('./images/essential-tumbler/Dims_20oz.jpg'),
                    mobile: require('./images/essential-tumbler/Dims_20oz.jpg'),
                    alt: 'RTIC Essential Tumbler 20oz dimensions',
                  },
                },
              ],
            },
            {
              value: '30oz',
              sections: [
                {
                  title: {
                    text: '30oz',
                  },
                  images: {
                    desktop: require('./images/essential-tumbler/Dims_30oz.jpg'),
                    mobile: require('./images/essential-tumbler/Dims_30oz.jpg'),
                    alt: 'RTIC Essential Tumbler 30oz dimensions',
                  },
                },
              ],
            },
            {
              value: '40oz',
              sections: [
                {
                  title: {
                    text: '40oz',
                  },
                  images: {
                    desktop: require('./images/essential-tumbler/Dims_40oz.jpg'),
                    mobile: require('./images/essential-tumbler/Dims_40oz.jpg'),
                    alt: 'RTIC Essential Tumbler 40oz dimensions',
                  },
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leak-Resistant Lid</b> features double threading and a leak-resistant tab to keep your
                    drink from spilling and your lid on tight.
                  </span>,
                  <span>
                    <b>Ceramic-Lined Interior</b> prevents altered or metallic taste and smell, so drinks
                    taste better and it’s easy to clean.
                    <br />
                    <br />
                    Note: Do not use metal or hard plastic straws or utensils with ceramic lining as scraping
                    can occur.
                  </span>,
                  <span>
                    <b>Up to 24 Hours Cold / 6 Hours Hot</b> with double-wall, vacuum-sealed insulation, UV
                    heat protection, and an airtight seal.{' '}
                    <i>Note: Please use caution when opening the lid with hot liquids.</i>
                  </span>,
                ],
                [
                  <span>
                    <b>RTIC Tough</b> with 18/8 Stainless Steel and BPA-free materials offer quality and
                    durability you can count on.
                  </span>,
                  <span>
                    <b>Built-in Silicone Base</b> avoids clanking for a soft landing on any surface.{' '}
                  </span>,
                  <span>
                    <b>No Sweat Exterior</b> remains free of condensation, so your hands stay dry and it won’t
                    leave water rings.
                  </span>,
                  <span>
                    <b>Dishwasher Safe</b> makes it easy to clean, remember to place the lid on the top rack
                    only.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.SOFT_PACK_PRO:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              THE ULTIMATE
              <br />
              WATERPROOF
              <br />
              COOLER
            </>
          ),
          image: {
            desktop: require('./images/soft-pack-pro/Hero-D-Fallback.jpg'),
            mobile: require('./images/soft-pack-pro/Hero-M-Fallback.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/ultra-tough-soft-cooler-pro-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/ultra-tough-soft-cooler-pro-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              A premium waterproof carryall that floats—keeping food cold and essentials dry.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>All-new RTIC EZ Waterproof Zipper</li>
              <li className='tw-text-base tw-font-normal'>RTIC Cold Lock Press Lid</li>
              <li className='tw-text-base tw-font-normal'>Leakproof design that floats</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 2 days when following <RticCoolingTipsLink />
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/soft-pack-pro/icons/icon-floating.svg'),
            title: <>Built to Float</>,
            description: (
              <>
                Leakproof design is air tight thanks to the All New RTIC EZ Waterproof Zipper—it glides open
                and closed easily, while keeping water out.
              </>
            ),
          },
          {
            icon: require('./images/soft-pack-pro/icons/icon-lock.svg'),
            title: <>Cold Lock Press Lid</>,
            description: (
              <>
                RTIC’s unique Cold Lock Press Lid presses down to lock in the cold, so you don’t have to zip
                it every time you grab a drink.
              </>
            ),
          },
          {
            icon: require('./images/soft-pack-pro/icons/icon-handy-bag.svg'),
            title: <>Essentials on Hand</>,
            description: (
              <>
                Waterproof exterior box pocket uses the RTIC EZ Waterproof Zipper to keep all of your
                essentials dry and secure.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2’’ of closed-cell foam insulation locks in the cold for up to 2 days when using{' '}
                <RticCoolingTipsLink />.
              </>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/meet-the-collection/The-Floating-Carryall-D.jpg'),
                mobile: require('./images/soft-pack-pro/meet-the-collection/The-Floating-Carryall-M.jpg'),
                alt: 'RTIC Ultra-Tough Soft Cooler Pro floating',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER PRO',
                title: <span>The Floating Carryall</span>,
                description: (
                  <>
                    This cooler combines welded construction, lightweight design, and an innovative,
                    waterproof zipper design to lock the air in and keep the water out. So your drinks and
                    snacks can go wherever you go—from the road to the water.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/meet-the-collection/Press-it-Zip-it-Go-D.jpg'),
                mobile: require('./images/soft-pack-pro/meet-the-collection/Press-it-Zip-it-Go-M.jpg'),
                alt: 'RTIC Ultra-Tough Soft Cooler Pro Opened',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER PRO',
                title: <span>Press it. Zip it. Go.</span>,
                description: (
                  <>
                    The exclusively designed RTIC Cold Lock Press Lid simply presses down to lock in the cold,
                    so you don’t have to unzip the lid every time you want to grab a drink. Just be sure to
                    zip it shut for leakproof transportation when you’re ready to get moving.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/meet-the-collection/Essentials-On-Hand-D.jpg'),
                mobile: require('./images/soft-pack-pro/meet-the-collection/Essentials-On-Hand-M.jpg'),
                alt: 'RTIC Ultra-Tough Soft Cooler Pro essentials on hand',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER PRO',
                title: <span>Essentials On Hand</span>,
                description: (
                  <>
                    This cooler features a premium, waterproof box pocket that lets you keep your keys, phone,
                    and wallet on hand and dry. Featuring an RTIC EZ Waterproof Zipper this roomy pocket is
                    completely waterproof.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/meet-the-collection/Premium-Ice-Retention-D.jpg'),
                mobile: require('./images/soft-pack-pro/meet-the-collection/Premium-Ice-Retention-M.jpg'),
                alt: 'RTIC Ultra-Tough Soft Cooler Pro ice retention',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER PRO',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <>
                    Up to 2 inches of closed-cell foam insulation, and welded seams lock in the cold for up to
                    2 days when following <RticCoolingTipsLink />. The leakproof interior can be used with
                    loose ice for optimal ice retention.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/meet-the-collection/Durable-Portable-D.jpg'),
                mobile: require('./images/soft-pack-pro/meet-the-collection/Durable-Portable-M.jpg'),
                alt: 'RTIC Ultra-Tough Soft Cooler Pro durability and portability',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH SOFT COOLER PRO',
                title: <span>Durable & Portable</span>,
                description: (
                  <>
                    Made with heavy-duty, coated fabric and a puncture-resistant, waterproof interior–these
                    coolers are lightweight, yet extremely durable. With built-in carrying handles and a
                    removable, adjustable strap, you can sling it on your shoulder and take it wherever you
                    go.
                  </>
                ),
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '20 Can',
              sections: [
                {
                  title: {
                    text: '20 can',
                  },
                  images: {
                    desktop: require('./images/soft-pack-pro/dims/20-can-D.jpg'),
                    mobile: require('./images/soft-pack-pro/dims/20-can-M.jpg'),
                    alt: 'RTIC Soft Pack Cooler Pro 20 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Exterior Dims: 13.39 × 10.63 × 14.57</p>
                      <p className='tw-bold tw-mb-1'>Interior Dims: 11.22 × 6.69 × 10.83</p>
                      <p className='tw-bold tw-mb-1'>Liters: 13L</p>
                      <p className='tw-bold tw-mb-1'>Ice Capacity: 12 lbs</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 4 lbs</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '30 Can',
              sections: [
                {
                  title: {
                    text: '30 can',
                  },
                  images: {
                    desktop: require('./images/soft-pack-pro/dims/30-can-D.jpg'),
                    mobile: require('./images/soft-pack-pro/dims/30-can-M.jpg'),
                    alt: 'RTIC Soft Pack Cooler Pro 30 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Exterior Dims: 15.8 × 11.5 × 14 ”</p>
                      <p className='tw-bold tw-mb-1'>Interior Dims: 12.99 × 8.27 × 10.63 ”</p>
                      <p className='tw-bold tw-mb-1'>Liters: 19L</p>
                      <p className='tw-bold tw-mb-1'>Ice Capacity: 18 lbs</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 5 lbs</p>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/Leakproof-Design-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/Leakproof-Design-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro leakproof design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Leakproof Design</span>,
                description: (
                  <>Welded seams and the RTIC EZ Waterproof Zipper make this cooler air and water tight.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/RTIC-EZ-Waterproof-Zipper-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/RTIC-EZ-Waterproof-Zipper-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro RTIC EZ Waterproof Zipper',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>RTIC EZ Waterproof Zipper</span>,
                description: (
                  <>
                    Our exclusive Waterproof Zipper is easy to zip open or closed, and keeps water from
                    getting in or out.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/RTIC-Cold-Lock-Press-Lid-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/RTIC-Cold-Lock-Press-Lid-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro RTIC Cold Lock Press Lid',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>RTIC Cold Lock Press Lid</span>,
                description: (
                  <>
                    The uniquely designed Cold Lock Press Lid presses down to lock in the cold, so you don’t
                    have to unzip it every time you grab a drink.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/Exterior-Box-Pocket-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/Exterior-Box-Pocket-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro exterior box pocket',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Exterior Box Pocket</span>,
                description: (
                  <>
                    Exterior, Waterproof Box Pocket uses the RTIC EZ Waterproof Zipper to keep your essentials
                    dry and on hand.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/Wide-Mouth-Opening-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/Wide-Mouth-Opening-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro wide mouth opening',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Wide Mouth Opening</span>,
                description: (
                  <>The wide mouth opening makes it easy to pack and access your food and drinks.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/Heavy-Duty-Exterior-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/Heavy-Duty-Exterior-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro heavy-duty exterior',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Heavy-Duty Exterior</span>,
                description: (
                  <>The heavy-duty, coated fabric is tear and puncture resistant for extreme durability.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/Welded-Waterproof-Interior-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/Welded-Waterproof-Interior-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro welded waterproof interior',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Welded Waterproof Interior</span>,
                description: <>Can be used with loose ice cubes, is puncture resistant, and easy to clean.</>,
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/soft-pack-pro/features/Lightweight-Easy-To-Carry-D.jpg'),
                mobile: require('./images/soft-pack-pro/features/Lightweight-Easy-To-Carry-M.jpg'),
                alt: 'RTIC Soft Pack Cooler Pro lightweight and easy to carry',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Lightweight & Easy To Carry</span>,
                description: (
                  <>
                    Lightweight design is easy to carry with built-in handles and an adjustable shoulder
                    strap.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leakproof Design</b> with welded seams and the RTIC EZ Waterproof Zipper make this
                    cooler air and water tight.
                  </span>,
                  <span>
                    <b>RTIC EZ Waterproof Zipper</b> is easy to zip open or closed, and keeps water from
                    getting in or out.
                  </span>,
                  <span>
                    <b>RTIC Cold Lock Press Lid</b> presses down to lock in the cold, so you don’t have to
                    unzip it every time you grab a drink.
                  </span>,
                  <span>
                    <b>Exterior Box Pocket</b> uses the RTIC EZ Waterproof Zipper to keep your essentials dry
                    and on hand.
                  </span>,
                  <span>
                    <b>Heavy-Duty Exterior</b> is tear and puncture resistant for extreme durability.
                  </span>,
                ],
                [
                  <span>
                    <b>Welded Waterproof Interior</b> can be used with loose ice cubes, is puncture resistant,
                    and easy to clean.
                  </span>,
                  <span>
                    <b>Lightweight & Easy To Carry</b> with built-in handles and adjustable shoulder strap.
                  </span>,
                  <span>
                    <b>Wide Mouth Opening</b> makes it easy to pack and access your food and drinks.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.ULTRA_TOUGH_BACKPACK_PRO:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              HANDS-FREE
              <br />
              ADVENTURE
            </>
          ),
          image: {
            desktop: require('./images/ultra-tough-backpack-pro/Hero-D-Fallback.jpg'),
            mobile: require('./images/ultra-tough-backpack-pro/Hero-M-Fallback.jpg'),
          },
          // video: `${VIDEOS_DOMAIN}/essential-tumbler-hero_d.mp4`,
          // videoMob: `${VIDEOS_DOMAIN}/essential-tumbler-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              An easy-zip, waterproof backpack that floats—keeping food cold and essentials dry.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>All-new RTIC EZ Waterproof Zipper</li>
              <li className='tw-text-base tw-font-normal'>RTIC Cold Lock Press Lid</li>
              <li className='tw-text-base tw-font-normal'>Trail-ready suspension</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 2 days when following <RticCoolingTipsLink />
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/ultra-tough-backpack-pro/icons/icon-floating.svg'),
            title: <>Built to Float</>,
            description: (
              <>
                Leakproof design is air tight thanks to the All New RTIC EZ Waterproof Zipper—it glides open
                and closed easily, while keeping water out.
              </>
            ),
          },
          {
            icon: require('./images/ultra-tough-backpack-pro/icons/icon-lock.svg'),
            title: <>Cold Lock Press Lid</>,
            description: (
              <>
                RTIC’s unique Cold Lock Press Lid presses down to lock in the cold, so you don’t have to unzip
                it every time you grab a drink.
              </>
            ),
          },
          {
            icon: require('./images/ultra-tough-backpack-pro/icons/icon-trail-ready.svg'),
            title: <>Trail-Ready Suspension</>,
            description: (
              <>
                Ergonomic, trail-ready suspension makes it easy to carry with articulated shoulder straps and
                back support.
              </>
            ),
          },
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>Premium Ice Retention</>,
            description: (
              <>
                Up to 2’’ of closed-cell foam insulation locks in the cold for up to 2 days when using{' '}
                <RticCoolingTipsLink />.
              </>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/meet-the-collection/Hands-Free-Carryall-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/meet-the-collection/Hands-Free-Carryall-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro hands-free carryall',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH BACKPACK COOLER',
                title: <span>The Waterproof, Hands-Free Carryall</span>,
                description: (
                  <>
                    This backpack cooler combines welded construction, lightweight design, and the innovative
                    waterproof zipper design to lock the cold in and keep the water out.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/meet-the-collection/Press-It-Zip-It-Go-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/meet-the-collection/Press-It-Zip-It-Go-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro Opened',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH BACKPACK COOLER',
                title: <span>Press it. Zip it. Go.</span>,
                description: (
                  <>
                    The exclusively designed RTIC Cold Lock Press Lid simply presses down to lock in the cold,
                    so you don’t have to unzip the lid every time you want to grab a drink. Just be sure to
                    zip it shut for leakproof transportation when you’re ready to get moving.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/meet-the-collection/Premium-Ice-Retention-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/meet-the-collection/Premium-Ice-Retention-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro premium ice retention',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH BACKPACK COOLER',
                title: <span>Premium Ice Retention</span>,
                description: (
                  <>
                    Up to 2 inches of closed-cell foam insulation, and welded seams lock in the cold for up to
                    2 days when following <RticCoolingTipsLink />. The leakproof interior can be used with
                    loose ice for optimal ice retention.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/meet-the-collection/Durable-Portable-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/meet-the-collection/Durable-Portable-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro durability and portability',
              },
              copy: {
                tagline: 'MEET THE ULTRA-TOUGH BACKPACK COOLER',
                title: <span>Durable & Portable</span>,
                description: (
                  <>
                    Heavy-duty, coated fabric and a puncture-resistant, waterproof interior make it
                    lightweight, yet extremely durable. Featuring trail-ready suspension with adjustable,
                    padded shoulder straps and additional back support–these backpack coolers are comfortable
                    to carry, even with a full load.
                  </>
                ),
              },
            },
          ],
        },
        capacity: {
          sizes: [
            {
              value: '24 Can',
              sections: [
                {
                  title: {
                    text: '24 can',
                  },
                  images: {
                    desktop: require('./images/ultra-tough-backpack-pro/dims/24-can-d.jpg'),
                    mobile: require('./images/ultra-tough-backpack-pro/dims/24-can-m.jpg'),
                    alt: 'RTIC Soft Pack Cooler Pro 24 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Exterior Dimensions: 13.78 × 12.6 × 16.14”</p>
                      <p className='tw-bold tw-mb-1'>Interior Dimensions: 9.65 × 7.48 × 12.99</p>
                      <p className='tw-bold tw-mb-1'>Liters: 14L</p>
                      <p className='tw-bold tw-mb-1'>Ice Capacity: 14 lbs</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 5 lbs</p>
                    </>
                  ),
                },
              ],
            },
            {
              value: '48 Can',
              sections: [
                {
                  title: {
                    text: '48 can',
                  },
                  images: {
                    desktop: require('./images/ultra-tough-backpack-pro/dims/48-can-d.jpg'),
                    mobile: require('./images/ultra-tough-backpack-pro/dims/48-can-m.jpg'),
                    alt: 'RTIC Soft Pack Cooler Pro 48 Can dimensions',
                  },
                  description: (
                    <>
                      <p className='tw-bold tw-mb-1'>Exterior Dimensions: 14.57 × 12.2 × 21.26”</p>
                      <p className='tw-bold tw-mb-1'>Interior Dimensions: 12.2 × 7.28 × 17.72”</p>
                      <p className='tw-bold tw-mb-1'>Liters: 26L</p>
                      <p className='tw-bold tw-mb-1'>Ice Capacity: 23 lbs</p>
                      <p className='tw-bold tw-mb-1'>Empty Weight: 6 lbs</p>
                    </>
                  ),
                },
              ],
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Leakproof-Design-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Leakproof-Design-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro leakproof design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Leakproof Design</span>,
                description: (
                  <>Welded seams and the RTIC EZ Waterproof Zipper make this cooler air and water tight.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Waterproof-Zipper-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Waterproof-Zipper-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro RTIC EZ Waterproof Zipper',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>RTIC EZ Waterproof Zipper</span>,
                description: (
                  <>
                    Our exclusive EZ Waterproof Zipper is easy to zip open or closed, and keeps water from
                    getting in or out.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Cold-Lock-Press-Lid-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Cold-Lock-Press-Lid-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro RTIC Cold Lock Press Lid',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>RTIC Cold Lock Press Lid</span>,
                description: (
                  <>
                    The uniquely designed Cold Lock Press Lid presses down to lock in the cold, so you don’t
                    have to unzip it every time you grab a drink.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Trail-Ready-Suspension-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Trail-Ready-Suspension-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro trail ready suspension',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Trail Ready Suspension</span>,
                description: (
                  <>
                    Trail-ready suspension with adjustable, padded shoulder straps and back support make the
                    backpack comfortable to carry–even with a full load.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Exterior-Box-Pocket-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Exterior-Box-Pocket-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro exterior box pocket',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Exterior Box Pocket</span>,
                description: (
                  <>
                    Waterproof box pocket uses the RTIC EZ Waterproof Zipper to keep your essentials dry and
                    on hand.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Heavy-Duty-Exterior-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Heavy-Duty-Exterior-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro heavy-duty exterior',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Heavy-Duty Exterior</span>,
                description: (
                  <>The heavy-duty, coated fabric is tear and puncture resistant for extreme durability.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Welded-Waterproof-Interior-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Welded-Waterproof-Interior-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro welded waterproof interior',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Welded Waterproof Interior</span>,
                description: (
                  <>
                    The interior can be used with loose ice cubes, is puncture resistant, and easy to clean.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/ultra-tough-backpack-pro/features/Wide-Mouth-Opening-D.jpg'),
                mobile: require('./images/ultra-tough-backpack-pro/features/Wide-Mouth-Opening-M.jpg'),
                alt: 'RTIC Ultra-Tough Backpack Cooler Pro wide mouth opening',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Wide Mouth Opening</span>,
                description: (
                  <>The wide mouth opening makes it easy to pack and access your food and drinks.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Leakproof Design</b> with welded seams and the RTIC EZ Waterproof Zipper make this
                    cooler air and water tight.
                  </span>,
                  <span>
                    <b>RTIC EZ Waterproof Zipper</b> is easy to zip open or closed, and keeps water from
                    getting in or out.
                  </span>,
                  <span>
                    <b>RTIC Cold Lock Press Lid</b> presses down to lock in the cold, so you don’t have to
                    unzip it every time you grab a drink.
                  </span>,
                  <span>
                    <b>Trail Ready Suspension</b> with adjustable, padded shoulder straps and back support
                    make the backpack comfortable to carry–even with a full load.
                  </span>,
                  <span>
                    <b>Exterior Box Pocket</b> uses the RTIC EZ Waterproof Zipper to keep your essentials dry
                    and on hand.
                  </span>,
                ],
                [
                  <span>
                    <b>Heavy-Duty Exterior</b> is tear and puncture resistant for extreme durability.
                  </span>,
                  <span>
                    <b>Welded Waterproof Interior</b> can be used with loose ice cubes, is puncture resistant,
                    and easy to clean.
                  </span>,
                  <span>
                    <b>Wide Mouth Opening</b> makes it easy to pack and access your food and drinks.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    case ProductTypeId.WHEELED_EVERYDAY_COOLER:
      return {
        lifestyleHeader: {
          containerClassName: 'tw-justify-start',
          titleContainerClassName: 'tw-w-full tw-max-w-[1440px] tw-mx-auto tw-inline-flex tw-pl-5 tw-pb-3',
          titleClassName: 'tw-text-left',
          title: (
            <>
              ROLL IT. CARRY IT.
              <br />
              DO IT ALL.
            </>
          ),
          image: {
            desktop: require('./images/wheeled-everyday-cooler/Hero-Fallback-D.jpg'),
            mobile: require('./images/wheeled-everyday-cooler/Hero-Fallback-M.jpg'),
          },
          video: `${VIDEOS_DOMAIN}/everyday-wheeled-cooler-hero_d.mp4`,
          videoMob: `${VIDEOS_DOMAIN}/everyday-wheeled-cooler-hero_m.mp4`,
        },
        subtitle: (
          <>
            <p className='tw-mb-2 tw-text-base tw-font-normal tw-leading-tight'>
              Pack the good times and keep them rolling with this collapsible, fully-insulated wheeled Cooler.
            </p>
            <ul className='tw-mb-0 tw-list-outside tw-list-disc tw-pl-4'>
              <li className='tw-text-base tw-font-normal'>40 can capacity</li>
              <li className='tw-text-base tw-font-normal'>Detachable Cooler + Wheeled Trolley</li>
              <li className='tw-text-base tw-font-normal'>Collapsible Design</li>
              <li className='tw-text-base tw-font-normal'>
                Locks in the cold for up to 24 hours when following <RticCoolingTipsLink />
              </li>
            </ul>
          </>
        ),
        iconFeatures: [
          {
            icon: require('./images/icons/icon-ice-flake.svg'),
            title: <>All-Day Cold</>,
            description: (
              <>
                Fully insulated interior with welded seams locks in the cold for up to 24 hours with freezable
                gel blocks or packs.
              </>
            ),
          },
          {
            icon: require('./images/wheeled-everyday-cooler/icons/icon-packable.svg'),
            title: <>Packable Design</>,
            description: (
              <>
                Low-profile, collapsible design means both the soft cooler and trolley can be folded into a
                compact configuration when not in use.
              </>
            ),
          },
          {
            icon: require('./images/wheeled-everyday-cooler/icons/icon-everyday-cooler.svg'),
            title: <>Everyday Cooler</>,
            description: (
              <>
                40 Can Everyday Cooler can be rolled on the trolley or removed and carried with a shoulder
                strap.
              </>
            ),
          },
          {
            icon: require('./images/wheeled-everyday-cooler/icons/icon-rugged-trolley.svg'),
            title: <>Rugged Trolley</>,
            description: (
              <>
                Durable trolley features a convenient cooler connection, rubber-coated wheels and retractable
                handle for easy towing.
              </>
            ),
          },
        ],
        whyWeLoveIt: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/meet-the-collection/Packable-Portable-Fallback-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/meet-the-collection/Packable-Portable-Fallback-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler packable & portable',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY WHEELED COOLER',
                title: <span>Packable & Portable</span>,
                description: (
                  <>
                    Designed to be used all day, then packed away–the Everyday Wheeled Cooler features a
                    low-profile, packable design. Once you’re ready to use it, the Soft Cooler and Trolley can
                    be expanded in 4 simple steps:
                    <br />
                    <br />
                    1. Remove Everyday Cooler from Trolley
                    <br />
                    2. Unlatch hooks to untuck and expand the base of the cooler from the lid.
                    <br />
                    3. Open the trolley by lifting the handle up, sliding the base slider away from the
                    handle.
                    <br />
                    4. Slide the expanded Everyday Cooler over the insert on the back of the Trolley.
                  </>
                ),
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/everyday-wheeled-cooler-packable-portable_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/everyday-wheeled-cooler-packable-portable_m.mp4`,
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/meet-the-collection/Do-It-All-Cooler-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/meet-the-collection/Do-It-All-Cooler-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler do-it-all cooler',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY WHEELED COOLER',
                title: <span>The Do-It-All Cooler</span>,
                description: (
                  <>
                    The Everyday Wheeled Cooler combines a fully insulated, 40 can Everyday Cooler and a
                    detachable smooth-rolling trolley to create a cooler that’s built to go everywhere,
                    everyday. Whether you’re heading to a tailgate or the park, it can be rolled on the
                    trolley or carried with a shoulder strap. When you’re done, just collapse and store it.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/meet-the-collection/All-Day-Cold-Fallback-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/meet-the-collection/All-Day-Cold-Fallback-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler all-day cold',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY WHEELED COOLER',
                title: <span>All-Day Cold</span>,
                description: (
                  <>
                    The Everyday Wheeled Cooler features a fully insulated interior to keep your drinks and
                    food cold for up to 24 hours when following <RticCoolingTipsLink />. Featuring welded
                    seams and a fabric zipper, we recommend using freezable gel blocks or packs.
                  </>
                ),
              },
              videoClassName: 'tw-object-cover',
              video: `${VIDEOS_DOMAIN}/everyday-wheeled-cooler-all-day-cold_d.mp4`,
              videoMob: `${VIDEOS_DOMAIN}/everyday-wheeled-cooler-all-day-cold_m.mp4`,
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/meet-the-collection/Every-Day-Made-Easier-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/meet-the-collection/Every-Day-Made-Easier-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler every day made easier',
              },
              copy: {
                tagline: 'MEET THE EVERYDAY WHEELED COOLER',
                title: <span>Every Day Made Easier</span>,
                description: (
                  <>
                    From multiple exterior and interior pockets, to a removable shoulder strap and built-in
                    bottle opener–this cooler includes all of the great features of the best-selling Everyday
                    Cooler. But, we’ve also taken it to the next level with the built-in lid hatch that lets
                    you easily grab drinks or snacks without having to unzip and zip the lid every time.
                  </>
                ),
              },
            },
          ],
        },
        capacity: {
          sections: [
            {
              title: {
                text: 'Expanded',
              },
              images: {
                desktop: require('./images/wheeled-everyday-cooler/dims/Dims_expanded.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/dims/Dims_expanded.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler expanded dimensions',
              },
              description: (
                <>
                  <div className='tw-flex tw-justify-center tw-text-center'>
                    <ul className='tw-text-center [&>li]:tw-mb-2'>
                      <li>Exterior Dimensions (Expanded): 14” × 14” × 41”</li>
                      <li>Can Capacity: 40 Cans</li>
                    </ul>
                  </div>
                </>
              ),
            },
            {
              title: {
                text: 'Collapsed',
              },
              images: {
                desktop: require('./images/wheeled-everyday-cooler/dims/Dims_collapsed.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/dims/Dims_collapsed.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler collapsed dimensions',
              },
              description: (
                <>
                  <div className='tw-flex tw-justify-center tw-text-center'>
                    <ul className='tw-text-center [&>li]:tw-mb-2'>
                      <li>Exterior Dimensions (Collapsed): 14” × 8” × 20”</li>
                      <li>Can Capacity: 40 Cans</li>
                    </ul>
                  </div>
                </>
              ),
            },
          ],
        },
        features: {
          slides: [
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/All-Day-Cold-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/All-Day-Cold-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler leakproof design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>All-Day Cold</span>,
                description: (
                  <>
                    Fully insulated interior with welded seams locks in the cold for up to 24 hours when
                    following <RticCoolingTipsLink />. For best performance, use{' '}
                    <a href='/Ice-Packs' className='tw-underline' target='_blank'>
                      RTIC Ice Packs
                    </a>{' '}
                    in Small, Medium, or Large.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Innovative-Design-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Innovative-Design-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler innovative design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Innovative Design</span>,
                description: (
                  <>
                    Innovative design features a 40 can Cooler and detachable, smooth-rolling wheeled trolley
                    that both collapse for easy storing.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Everyday-Cooler-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Everyday-Cooler-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler everyday cooler',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Everyday Cooler</span>,
                description: (
                  <>
                    40 Can Everyday Cooler can be rolled on the trolley or removed and carried with the
                    shoulder strap.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Rugged-Trolley-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Rugged-Trolley-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler rugged trolley',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Rugged Trolley</span>,
                description: (
                  <>
                    Durable trolley features a convenient cooler connection, rubber-coated wheels and
                    retractable handle for easy towing.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Packable-Design-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Packable-Design-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler packable design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Packable Design</span>,
                description: (
                  <>
                    Low-profile packable design means both the soft cooler and trolley can be folded into a
                    compact, easy-to-stow configuration.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Easy-Grab-Lid-Hatch-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Easy-Grab-Lid-Hatch-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler easy grab lid hatch',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Easy-Grab Lid Hatch</span>,
                description: (
                  <>
                    A convenient, insulated lid hatch is built into the cooler so you can easily grab drinks
                    without having to zip up every time.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Convenient-Bottle-Opener-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Convenient-Bottle-Opener-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler convenient bottle opener',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Convenient Bottle Opener</span>,
                description: (
                  <>Removable bottle opener is latched onto the front for opening drinks anytime, anywhere.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Retractable-Handle-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Retractable-Handle-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler retractable handle',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Retractable Handle</span>,
                description: (
                  <>The ergonomic, retractable handle makes it easy to tow and stores away when not in use.</>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Waterproof-Base-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Waterproof-Base-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler waterproof base',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Waterproof Base</span>,
                description: (
                  <>
                    Seam-sealed, waterproof cooler base offers extra protection from dirty and damp surfaces.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Multiple-Pockets-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Multiple-Pockets-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler multiple pockets',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Multiple Pockets</span>,
                description: (
                  <>
                    This cooler features multiple exterior and interior pockets for easy organization and
                    storing of essentials.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Innovative-Design-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Innovative-Design-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler innovative design',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Innovative Design</span>,
                description: (
                  <>
                    Innovative design features a 40 can Cooler and detachable, smooth-rolling wheeled trolley
                    that both collapse for easy storing.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.IMAGE_CONTENT,
              image: {
                desktop: require('./images/wheeled-everyday-cooler/features/Easy-to-Carry-D.jpg'),
                mobile: require('./images/wheeled-everyday-cooler/features/Easy-to-Carry-M.jpg'),
                alt: 'RTIC Wheeled Everyday Cooler easy to carry',
              },
              copy: {
                tagline: 'TECH & FEATURES',
                title: <span>Easy to Carry</span>,
                description: (
                  <>
                    Padded webbing handles and a removable shoulder strap make it easy to carry, even with a
                    full load.
                  </>
                ),
              },
            },
            {
              template: AnimatedSlideTemplate.ITEMS_LIST,
              tagline: 'Tech & Features',
              title: 'Full List',
              listItems: [
                [
                  <span>
                    <b>Fully Insulated Interior</b> with welded seams locks in the cold for up to 24 hours
                    when following <RticCoolingTipsLink />. For best performance, use{' '}
                    <a href='/Ice-Packs' className='tw-underline' target='_blank'>
                      RTIC Ice Packs
                    </a>{' '}
                    in Small, Medium, or Large.
                  </span>,
                  <span>
                    <b>Innovative Design</b> features a 40 can Cooler and detachable, smooth-rolling wheeled
                    trolley that both collapse for easy storing.
                  </span>,
                  <span>
                    <b>40 Can Everyday Cooler</b> can be rolled on the trolley or removed and carried with the
                    shoulder strap.
                  </span>,
                  <span>
                    <b>Rugged, Durable Trolley</b> features a convenient cooler connection, rubber-coated
                    wheels and retractable handle for easy towing.
                  </span>,
                  <span>
                    <b>Packable Design</b> means both the soft cooler and trolley can be folded into a
                    compact, easy-to-stow configuration.
                  </span>,
                  <span>
                    <b>Easy-Grab Lid Hatch</b> makes it easy to grab drinks without having to zip up every
                    time.
                  </span>,
                ],
                [
                  <span>
                    <b>Removable Bottle Opener</b> latched onto the front for opening drinks anytime,
                    anywhere.
                  </span>,
                  <span>
                    <b>Ergonomic, Retractable Handle</b> makes it easy to tow and stores away when not in use.
                  </span>,
                  <span>
                    <b>Structured, Waterproof Cooler Base</b> offers extra protection from dirty and damp
                    surfaces.
                  </span>,
                  <span>
                    <b>Multiple Pockets</b> for easy organization and storing of essentials.
                  </span>,
                  <span>
                    <b>Easy to Carry</b> with padded webbing handles and a removable shoulder strap.
                  </span>,
                ],
              ],
            },
          ],
        },
      };

    default:
      return null;
  }
};

export default getExtraData;

export const RticCoolingTipsLink = () => (
  <a href='/ice-retention-tips' className='tw-underline' target='_blank'>
    RTIC's Cooling Tips
  </a>
);
